import { bindMarket, marketGameHtml, marketLobbyHtml, marketTileHtml, retainMarketDraft, setMarketUiRoom } from "./market-wars";
import type { MarketView } from "../worker/src/market-core";
import { randomId } from "./random-id";
import QRCode from "qrcode";
import { leaderboardShellHtml, mountLeaderboard } from "./all-time-leaderboard";
import { flushStandaloneResults, saveStandaloneResult } from "./standalone-leaderboard";
import { hostParticipationHtml, spectatorRoundControl } from "./room-participation";
import { observerHoldingsHtml } from "./observer-holdings";
import type { ObserverHolding } from "../worker/src/observer-holdings";
import { pokerHandHistoryHtml, pokerObserverHtml } from "./poker-observer";
import type { PokerPrivateHand, PokerPublicHandHistory } from "../worker/src/game-core";
import { RoomChat, roomChatButtonHtml } from "./room-chat";
import "./styles.css";
import { whatsappShareUrl } from "./share";
import { bindOfflineGallery, bindRoomExperiences, libraryShellHtml, mountLibrary, roomExperienceHtml } from "./party-experiences";
import { isRoomCode, primaryHomeAction } from "./room-code";
import { diceBidDefaults } from "./liars-dice";
import { mountGhostMansion } from "./ghost-mansion";
import { mountStainedGlassRoom, mountStainedGlass, stainedGlassTileHtml } from "./stained-glass";
import { mountCoralChorusRoom, mountCoralChorus, coralChorusTileHtml } from "./coral-chorus";
import { loadAppSettings } from "./app-settings";
import { VOICEOVER_PACKS, VOICEOVER_URLS, voiceoverCueForTransition, voiceoverPackForGame, voicesEnabledFromPreference, type VoiceoverCue, type VoiceoverPack } from "./voiceovers";
import { KART_DRIVERS, KART_TRACKS, TurboRallyGame, type KartRaceView } from "./turbo-rally";
import { bindKingTokyo, kingTokyoCardPurchasePopupHtml, kingTokyoDisplayHtml, kingTokyoGameHtml, kingTokyoLobbyHtml, kingTokyoRecapHtml, kingTokyoRollResultPopupHtml, kingTokyoYieldRefusalPopupHtml, type KingTokyoCardPurchase, type KingTokyoView, type KingTokyoRollResult, type KingTokyoYieldDecisionEvent } from "./king-tokyo";
import { detectTokyoMovement, type TokyoDeparture, type TokyoMovementEvent, type TokyoMovementSnapshot } from "./king-tokyo-movements";
import { bindHearts, heartsDisplayHtml, heartsGameHtml, heartsLobbyHtml, type HeartsView } from "./hearts";
import { bindSpades, spadesDisplayHtml, spadesGameHtml, spadesLobbyHtml, type SpadesView } from "./spades";
import { bindUno, unoDisplayHtml, unoGameHtml, unoLobbyHtml, type UnoView } from "./uno";
import { bindPartyPetsDisplay, bindPartyPetsPhone, hostControlsHtml, canPatchPartyPetsMinigame, partyPetsDisplayHtml, partyPetsPhoneHtml, partyPetsTileHtml, patchPartyPetsMinigame, type PartyPetView } from "./party-pets";
import "./party-pets.css";
import { bindHexchainDisplay, bindHexchainGame, bindHexchainLobby, cleanupHexchain, hexchainDisplayHtml, hexchainDuelResultHtml, hexchainGameHtml, hexchainLobbyHtml, hexchainTileHtml, type HexchainView } from "./hexchain";
import { shouldDeferHexchainRender } from "./hexchain-interaction";
import { clientReleaseIsStale, moduleScriptUrl } from "./client-freshness";
import { bindConnectFour, connectFourDisplayHtml, connectFourFinalHtml, connectFourGameHtml, connectFourLobbyHtml, localConnectFourSettingsHtml, type ConnectFourHint, type ConnectFourView } from "./connect-four";
import { animatePuzzleFighterDrops, animatePuzzleFighterImpact, bindPuzzleFighter, puzzleFighterDisplayHtml, puzzleFighterFinalHtml, puzzleFighterGameHtml, puzzleFighterLobbyHtml, puzzleFighterTileHtml, unbindPuzzleFighter, type PuzzleFighterView } from "./puzzle-fighter";
import { bindTicketToRide, bindTicketToRideLobby, ticketToRideDisplayHtml, ticketToRideFinalHtml, ticketToRideGameHtml, ticketToRideLobbyHtml, type TicketRideView } from "./ticket-to-ride";
import { axisAlliesDisplayHtml, axisAlliesFinalHtml, axisAlliesGameHtml, axisAlliesLobbyHtml, axisAlliesTileHtml, bindAxisAllies, localAxisAlliesSettingsHtml, type AxisAlliesView } from "./axis-allies";
import { bindStratego, strategoDisplayHtml, strategoFinalHtml, strategoGameHtml, strategoLobbyHtml, strategoTileHtml, type StrategoView } from "./stratego";
import { catanTileHtml, localCatanSettingsHtml, catanLobbyHtml, catanGameHtml, catanDisplayHtml, catanFinishedHtml, bindCatanActions, bindCatanViewport } from "./catan";
import type { CatanView } from "../worker/src/catan-core";
import type { CoralRoomView } from "../worker/src/coral-chorus-room";
import type { GlassRoomView } from "../worker/src/stained-glass-room";
import { bindModernArt, modernArtDisplayHtml, modernArtFinalHtml, modernArtGameHtml, modernArtLobbyHtml, modernArtTileHtml, type ModernArtView } from "./modern-art";
import { KING_COURT_GAME_TYPE, courtifyText } from "../worker/src/king-court-theme";
import { canUseRoomControl } from "../worker/src/room-controls";

type Player = { id: string; name: string; score: number; connected: boolean; isAdmin: boolean };
type Question = { id: string; prompt: string; choices: string[]; difficulty: string; era: string; reveal?: string; answerUnit?: string };
type Result = {
  playerId: string;
  playerName: string;
  selectedIndex: number;
  correctIndex: number;
  correct: boolean;
  guess?: number;
  distance?: number;
  skipped?: boolean;
  winnerIds?: string[];
  targetValue?: number;
};
type Attempt = {
  playerId: string;
  playerName: string;
  selectedIndex: number;
  correct: boolean;
  answeredAt: number;
  guess?: number;
  distance?: number;
  skipped?: boolean;
};
type Category = "eighties_music" | "nineties_music" | "two_thousands_music" | "twenty_tens_music" | "food" | "history" | "entertainment" | "sports" | "business" | "rotten_tomatoes" | "lior" | "dogs" | "animated_movies" | "berkeley" | "warriors" | "star_trek" | "word_match" | "impostor" | "closest_without_over" | "liars_dice" | "taboo" | "codenames" | "sequence" | "holdem" | "blackjack" | "hearts" | "spades" | "uno" | "monopoly_deal" | "king_of_tokyo" | "king_of_the_court" | "turbo_rally" | "party_pets" | "family_feud" | "hexchain" | "connect_four" | "puzzle_fighter" | "ticket_to_ride" | "axis_allies" | "stratego" | "modern_art" | "market_wars" | "catan" | "coral_chorus" | "stained_glass";
type GameMode = "closest" | "without_over" | "multiple_choice" | "judge_choice" | "impostor_vote" | "liars_dice" | "taboo" | "codenames" | "sequence" | "holdem" | "blackjack" | "hearts" | "spades" | "uno" | "monopoly_deal" | "king_of_tokyo" | "king_of_the_court" | "turbo_rally" | "party_pets" | "family_feud" | "hexchain" | "connect_four" | "puzzle_fighter" | "ticket_to_ride" | "axis_allies" | "stratego" | "modern_art" | "market_wars" | "catan" | "coral_chorus" | "stained_glass";
type GameType = "trivia" | "word_match" | "impostor" | "closest" | "liars_dice" | "taboo" | "codenames" | "sequence" | "holdem" | "blackjack" | "hearts" | "spades" | "uno" | "monopoly_deal" | "king_of_tokyo" | "king_of_the_court" | "turbo_rally" | "party_pets" | "family_feud" | "hexchain" | "connect_four" | "puzzle_fighter" | "ticket_to_ride" | "axis_allies" | "stratego" | "modern_art" | "market_wars" | "catan" | "coral_chorus" | "stained_glass";
type FamilyFeudTeam = "a" | "b";
type FamilyFeudView = {
  teams: Record<string, FamilyFeudTeam>;
  teamScores: Record<FamilyFeudTeam, number>;
  activeTeam: FamilyFeudTeam;
  canJudge: boolean;
  roundNumber: number;
  prompt: string;
  answers: Array<{ label: string; points: number | null; revealed: boolean }>;
  strikes: number;
  roundBank: number;
  multiplier: number;
  roundAwardedTo: FamilyFeudTeam | null;
  skippedRounds: number;
  winnerTeam: FamilyFeudTeam | null;
  sourceKind: "crowdsourced" | "game-show-archive" | null;
  sourceTitle: string;
  sourceUrl: string;
};
type MonopolyColor = "brown" | "light_blue" | "pink" | "orange" | "red" | "yellow" | "green" | "dark_blue" | "railroad" | "utility";
type MonopolyCard = { id: string; key: string; name: string; kind: "money" | "property" | "wild" | "action" | "rent" | "improvement"; value: number; colors?: MonopolyColor[]; action?: string };
type MonopolyPendingAction = { kind: "rent" | "debt_collector" | "birthday" | "sly_deal" | "forced_deal" | "deal_breaker"; sourceId: string; targetIds: string[]; targetIndex: number; targetCardId?: string; offeredCardId?: string; color?: MonopolyColor; amount?: number; multiplier: number; noCount: number; responderId: string };
type MonopolyPlayerStats = { rentCollected: number; biggestRent: number; propertiesStolen: number; completeSetsStolen: number };
type MonopolyView = {
  hand: MonopolyCard[];
  boards: Record<string, { bank: MonopolyCard[]; properties: Array<{ cardId: string; color: MonopolyColor; card: MonopolyCard }>; improvements: Array<{ cardId: string; color: MonopolyColor; card: MonopolyCard }>; completeColors: MonopolyColor[] }>;
  playerOrder: string[]; currentPlayerId: string | null; currentPlayerName: string; playsThisTurn: number; deckRemaining: number; discardCount: number; canAct: boolean;
  pendingAction: MonopolyPendingAction | null; pendingPayment: { debtorId: string; creditorId: string; amount: number } | null; winnerId: string | null; lastAction: string; log: string[]; stats: Record<string, MonopolyPlayerStats>; colors: MonopolyColor[];
};
type PokerCard = string;
type BlackjackResult = { playerId: string; playerName: string; handIndex: number; outcome: "win" | "loss" | "push"; playerTotal: number; dealerTotal: number; bet: number; payout: number; net: number; blackjack: boolean };
type BlackjackView = {
  startingBankroll: number; defaultBet: number; roundNumber: number; deckRemaining: number; shuffleCount: number; reshuffledThisRound: boolean; showCardCount: boolean; cardCount: number | null; playerOrder: string[];
  stacks: Record<string, number>; bets: Record<string, number>; hasBet: Record<string, boolean>;
  hands: Record<string, PokerCard[][]>; handBets: Record<string, number[]>; totals: Record<string, number[]>; statuses: Record<string, Array<"playing" | "stood" | "busted" | "blackjack">>; fromSplit: Record<string, boolean[]>;
  insuranceBets: Record<string, number>; insuranceDecisions: Record<string, boolean>; insurancePayouts: Record<string, number>;
  dealerHand: Array<PokerCard | null>; dealerTotal: number; currentPlayerId: string | null; currentHandIndex: number; currentPlayerName: string; canAct: boolean; canBet: boolean; canInsure: boolean; insuranceOffer: number; canDouble: boolean; canSplit: boolean; bettingCount: number; eligibleCount: number; results: BlackjackResult[];
};
type PokerAction = import("../worker/src/game-core").PokerAction;
type PokerAward = { playerId: string; playerName: string; amount: number; handLabel: string };
type PokerHistory = PokerPublicHandHistory;
type PokerView = {
  smallBlind: number; bigBlind: number; startingStack: number; seatOrder: string[]; stacks: Record<string, number>; statuses: Record<string, string>;
  dealerIndex: number; handNumber: number; board: PokerCard[]; holeCards: PokerCard[]; street: string; currentPlayerId: string | null; currentPlayerName: string;
  observerHands?: PokerPrivateHand[];
  currentBet: number; streetBets: Record<string, number>; contributions: Record<string, number>; pot: number; callAmount: number; minimumRaiseTo: number;
  maximumRaiseTo: number; canAct: boolean; canCheck: boolean; canRaise: boolean; actions: PokerAction[]; result: { awards: PokerAward[]; shownHands: Array<{ playerId: string; playerName: string; cards: PokerCard[]; handLabel: string }>; totalPot: number } | null; history: PokerHistory[];
};
type WordSubmission = { id: string; card: string; playerId?: string; playerName?: string };
type WordMatchView = {
  targetScore: number;
  roundNumber: number;
  judgeId: string | null;
  judgeName: string;
  greenCard: string | null;
  hand: string[];
  hasSubmitted: boolean;
  submittedCount: number;
  eligibleCount: number;
  submissions: WordSubmission[];
  winner: WordSubmission | null;
};
type ImpostorResult = {
  impostorId: string;
  impostorName: string;
  topic: string;
  caught: boolean;
  correctVoterIds: string[];
  mostVotedIds: string[];
  tally: Record<string, number>;
};
type ImpostorView = {
  targetScore: number;
  roundNumber: number;
  role: "impostor" | "insider" | null;
  topic: string | null;
  topicCategory: string | null;
  hasSubmitted: boolean;
  submittedCount: number;
  eligibleCount: number;
  currentCluePlayerId: string | null;
  currentCluePlayerName: string;
  turnOrder: Array<{ playerId: string; playerName: string; submitted: boolean }>;
  clues: Array<{ playerId: string; playerName: string; clue: string; verbal?: boolean }>;
  hasVoted: boolean;
  votedForId: string | null;
  voteCount: number;
  result: ImpostorResult | null;
};
type DiceBid = { quantity: number; face: number; playerId: string; playerName: string };
type LiarsDiceResult = {
  bidderId: string;
  bidderName: string;
  challengerId: string;
  challengerName: string;
  loserId: string;
  loserName: string;
  bid: DiceBid;
  actualCount: number;
  bidWasTrue: boolean;
  revealedHands: Array<{ playerId: string; playerName: string; dice: number[] }>;
};
type LiarsDiceView = {
  roundNumber: number;
  hand: number[];
  diceCounts: Record<string, number>;
  eligiblePlayerIds: string[];
  isPlaying: boolean;
  currentPlayerId: string | null;
  currentPlayerName: string;
  currentBid: DiceBid | null;
  bidHistory: DiceBid[];
  totalDice: number;
  result: LiarsDiceResult | null;
};
type TabooTeam = "a" | "b";
type TabooView = {
  teamMode: "random" | "manual";
  teams: Record<string, TabooTeam>;
  teamScores: Record<TabooTeam, number>;
  targetScore: number;
  roundSeconds: number;
  roundNumber: number;
  activeTeam: TabooTeam | null;
  viewerTeam: TabooTeam | null;
  clueGiverId: string | null;
  clueGiverName: string;
  card: { word: string; forbidden: string[] } | null;
  correctThisTurn: number;
  skippedThisTurn: number;
  taboosThisTurn: number;
  lastAction: "correct" | "skip" | "taboo" | null;
  result: { clueGiverId: string; clueGiverName: string; team: TabooTeam; correct: number; skipped: number; taboos: number } | null;
};
type CodeNamesTeam = "red" | "blue";
type CodeNamesRole = CodeNamesTeam | "neutral" | "assassin";
type CodeNamesView = {
  teamMode: "random" | "manual";
  teams: Record<string, CodeNamesTeam>;
  spymasters: Record<CodeNamesTeam, string | null>;
  spymasterNames: Record<CodeNamesTeam, string>;
  board: Array<{ word: string; role: CodeNamesRole | null; revealed: boolean }>;
  activeTeam: CodeNamesTeam | null;
  startingTeam: CodeNamesTeam | null;
  viewerTeam: CodeNamesTeam | null;
  isSpymaster: boolean;
  clue: { word: string; count: number } | null;
  clueHistory: Array<{ team: CodeNamesTeam; word: string; count: number; turnNumber: number }>;
  guessesLeft: number;
  bonusGuessAvailable: boolean;
  turnNumber: number;
  remaining: Record<CodeNamesTeam, number>;
  winnerTeam: CodeNamesTeam | null;
  lastGuess: { index: number; role: CodeNamesRole; playerId: string; playerName: string } | null;
};
type SequenceTeam = "coral" | "aqua" | "gold";
type SequenceCard = string;
type SequenceView = {
  teamCount: 2 | 3;
  teams: Record<string, SequenceTeam>;
  teamScores: Record<SequenceTeam, number>;
  targetSequences: number;
  board: Array<SequenceCard | null>;
  chips: Array<SequenceTeam | null>;
  locked: boolean[];
  hand: SequenceCard[];
  deadCards: boolean[];
  legalSpaces: number[][];
  playerOrder: string[];
  turnOrder: Array<{ playerId: string; playerName: string; team: SequenceTeam }>;
  currentPlayerId: string | null;
  currentPlayerName: string;
  viewerTeam: SequenceTeam | null;
  canAct: boolean;
  turnNumber: number;
  deckRemaining: number;
  winnerTeam: SequenceTeam | null;
  lastMove: { playerId: string; playerName: string; team: SequenceTeam; card: SequenceCard; boardIndex: number | null; action: "place" | "remove" | "swap"; sequencesMade: number } | null;
};
type RoomState = {
  partyNight?: import("../worker/src/party-night").PartyNight | null;
  savedRoom?: import("../worker/src/saved-library").SavedRoom | null;
  type: "state";
  roomCode: string;
  isAdmin: boolean;
  isDisplay: boolean;
  isSpectator: boolean;
  observerHoldings?: ObserverHolding[];
  spectators: Player[];
  gameType: GameType;
  phase: "menu" | "lobby" | "question" | "result" | "submitting" | "judging" | "round_result" | "clueing" | "voting" | "impostor_result" | "dice_bidding" | "dice_result" | "taboo_ready" | "taboo_turn" | "taboo_result" | "codenames_clue" | "codenames_guess" | "sequence_turn" | "poker_betting" | "poker_result" | "blackjack_betting" | "blackjack_insurance" | "blackjack_playing" | "blackjack_result" | "hearts_passing" | "hearts_playing" | "hearts_round_result" | "spades_bidding" | "spades_playing" | "spades_round_result" | "uno_playing" | "uno_round_result" | "monopoly_turn" | "tokyo_roll" | "tokyo_yield" | "tokyo_costume" | "tokyo_buy" | "kart_countdown" | "kart_racing" | "party_live" | "party_minigame" | "party_finale" | "feud_board" | "feud_round_result" | "hexchain_battle" | "connect_four_playing" | "puzzle_fighter_playing" | "ticket_to_ride_playing" | "axis_allies_playing" | "stratego_playing" | "modern_art_playing" | "market_wars_playing" | "catan_playing" | "coral_chorus_playing" | "stained_glass_playing" | "finished";
  players: Player[];
  question: Question | null;
  questionNumber: number;
  totalQuestions: number;
  maxQuestions: number;
  deadline: number | null;
  result: Result | null;
  attempts: Attempt[];
  category: Category;
  categoryLabel: string;
  gameMode: GameMode;
  wordMatch?: WordMatchView;
  impostor?: ImpostorView;
  liarsDice?: LiarsDiceView;
  taboo?: TabooView;
  codeNames?: CodeNamesView;
  sequence?: SequenceView;
  poker?: PokerView;
  blackjack?: BlackjackView;
  hearts?: HeartsView;
  spades?: SpadesView;
  uno?: UnoView;
  monopolyDeal?: MonopolyView;
  kingTokyo?: KingTokyoView;
  kartRace?: KartRaceView;
  partyPets?: PartyPetView;
  familyFeud?: FamilyFeudView;
  hexchain?: HexchainView;
  connectFour?: ConnectFourView;
  puzzleFighter?: PuzzleFighterView;
  ticketToRide?: TicketRideView;
  axisAllies?: AxisAlliesView;
  stratego?: StrategoView;
  modernArt?: ModernArtView;
  marketWars?: MarketView;
  catan?: CatanView;
  coralChorus?: CoralRoomView;
  stainedGlass?: GlassRoomView;
};

const CONFIGURED_API_BASE = (import.meta.env.VITE_API_BASE || "http://localhost:8787").replace(/\/$/, "");
const API_BASE = (import.meta.env.VITE_LOCAL_GAME_TYPES || import.meta.env.VITE_LOCAL_GAME_TYPE) ? location.origin : CONFIGURED_API_BASE;
const CLIENT_RELEASE_CHECK_MS = 60_000;
const CLIENT_RELEASE_REFRESH_KEY = "party-stack-client-release";
const LOCAL_GAME_TYPES = String(import.meta.env.VITE_LOCAL_GAME_TYPES || import.meta.env.VITE_LOCAL_GAME_TYPE || "")
  .split(",")
  .map((value) => value.trim())
  .filter(Boolean) as Array<GameType | "stained_glass" | "coral_chorus">;
const LOCAL_GAME_TYPE = LOCAL_GAME_TYPES.length === 1 ? LOCAL_GAME_TYPES[0] : "";
const IS_LOCAL_RUNTIME = LOCAL_GAME_TYPES.length > 0;
const IS_LOCAL_CONNECT_FOUR = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("connect_four");
const IS_LOCAL_PUZZLE_FIGHTER = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("puzzle_fighter");
const IS_LOCAL_TICKET_TO_RIDE = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("ticket_to_ride");
const IS_LOCAL_AXIS_ALLIES = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("axis_allies");
const IS_LOCAL_STRATEGO = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("stratego");
const IS_LOCAL_MODERN_ART = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes("modern_art");
const IS_LOCAL_KING_TOKYO = (() => {
  if (IS_LOCAL_RUNTIME) return LOCAL_GAME_TYPES.includes("king_of_tokyo");
  try {
    const api = new URL(API_BASE);
    return ["127.0.0.1", "localhost"].includes(api.hostname) && api.port === "8799";
  } catch {
    return false;
  }
})();
const IS_LOCAL_KING_COURT = IS_LOCAL_RUNTIME && LOCAL_GAME_TYPES.includes(KING_COURT_GAME_TYPE);
const IS_LOCAL_KING_RULES = IS_LOCAL_KING_TOKYO || IS_LOCAL_KING_COURT;
const app = document.querySelector<HTMLDivElement>("#app")!;
const palette = ["amber", "coral", "violet", "aqua", "lime"];
const questionCountPresets = [5, 10, 15, 20];
const categories: Array<{ id: Category; icon: string; label: string; description: string }> = [
  { id: "eighties_music", icon: "♫", label: "80s Music", description: "See the song. Name the artist." },
  { id: "nineties_music", icon: "♫", label: "90s Music", description: "See the song. Name the artist." },
  { id: "two_thousands_music", icon: "♫", label: "2000s Music", description: "See the song. Name the artist." },
  { id: "twenty_tens_music", icon: "♫", label: "2010s Music", description: "See the song. Name the artist." },
  { id: "food", icon: "🍴", label: "Food", description: "Ingredients, world dishes, and kitchen know-how" },
  { id: "rotten_tomatoes", icon: "●", label: "Rotten Tomatoes", description: "Guess exact critic and audience scores" },
  { id: "animated_movies", icon: "✦", label: "Animated Movies", description: "Pixar, anime, classics, and more" },
  { id: "dogs", icon: "🐾", label: "Dogs", description: "Breeds, origins, senses, and history" },
  { id: "berkeley", icon: "🐻", label: "Berkeley", description: "Cal, city history, culture, and discoveries" },
  { id: "warriors", icon: "🏀", label: "Golden State Warriors", description: "Titles, stars, records, and Dub Nation" },
  { id: "star_trek", icon: "🖖", label: "Star Trek", description: "Crews, worlds, episodes, and films" },
  { id: "lior", icon: "L", label: "Lior", description: "Facebook, Scuba, and startup lore" },
  { id: "history", icon: "⌛", label: "History", description: "Ancient worlds to today" },
  { id: "entertainment", icon: "★", label: "Entertainment", description: "Movies, television, and stars" },
  { id: "sports", icon: "●", label: "Sports", description: "Teams, athletes, and records" },
  { id: "business", icon: "↗", label: "Business", description: "Companies, markets, and money" },
];

const musicTriviaCategories = new Set<Category>(["eighties_music", "nineties_music", "two_thousands_music", "twenty_tens_music"]);

let socket: WebSocket | null = null;
let room: RoomState | null = null;
let unmountLeaderboard: (() => void) | null = null;
const isKingCourtGame = (gameType: GameType | undefined = room?.gameType) => gameType === KING_COURT_GAME_TYPE;
const kingGameHtml = (html: string, gameType: GameType | undefined = room?.gameType) => isKingCourtGame(gameType) ? courtifyText(html) : html;
let myId = localStorage.getItem("quiz-spark-player-id") || makeId();
let myName = localStorage.getItem("quiz-spark-name") || "";
let planPartyNightOnConnect = false;
const roomChat = new RoomChat(() => myId, (message) => {
  if (socket?.readyState !== WebSocket.OPEN) return false;
  socket.send(JSON.stringify(message));
  return true;
});
const initialParams = new URLSearchParams(location.search);
let displayMode = initialParams.get("view") === "display";
let pendingRoom = initialParams.get("room")?.toUpperCase() || "";
let selectedAnswer: number | null = null;
let selectedGuess: number | null = null;
let selectedSkip = false;
let selectedWordCard: string | null = null;
let reconnectTimer: number | undefined;
let clientReleaseCheckPending = false;
let timerFrame: number | undefined;
let toastTimer: number | undefined;
let tokyoMovementPopupTimer: number | undefined;
let tokyoRollResultPopupTimer: number | undefined;
let tokyoMovementDepartures: TokyoDeparture[] = [];
let soundEffectsEnabled = localStorage.getItem("quiz-spark-sound-effects") !== "off";
let musicEnabled = localStorage.getItem("quiz-spark-music") === "on";
let voicesEnabled = voicesEnabledFromPreference(localStorage.getItem("quiz-spark-voices"));
let appFontScale = loadAppSettings(localStorage).fontScale;
document.documentElement.style.fontSize = `${appFontScale}%`;
const SOUND_VOLUME_CALIBRATION = "hexchain-loud-v2";
const hasCurrentSoundCalibration = localStorage.getItem("quiz-spark-volume-calibration") === SOUND_VOLUME_CALIBRATION;
const savedSoundVolumeValue = localStorage.getItem("quiz-spark-volume");
const savedSoundVolume = savedSoundVolumeValue === null ? Number.NaN : Number(savedSoundVolumeValue);
let soundVolume = hasCurrentSoundCalibration && Number.isFinite(savedSoundVolume) && savedSoundVolume >= 0 && savedSoundVolume <= 100 ? savedSoundVolume : 50;
if (!hasCurrentSoundCalibration) {
  localStorage.setItem("quiz-spark-volume", "50");
  localStorage.setItem("quiz-spark-volume-calibration", SOUND_VOLUME_CALIBRATION);
}
let audioContext: AudioContext | null = null;
let audioResumePromise: Promise<void> | null = null;
let audioMasterGain: GainNode | null = null;
let audioCompressor: DynamicsCompressorNode | null = null;
let voiceGain: GainNode | null = null;
let activeVoiceSource: AudioBufferSourceNode | null = null;
let voicePlaybackId = 0;
const voiceBufferCache = new Map<VoiceoverCue, Promise<AudioBuffer>>();
const GAME_MUSIC_URLS: Partial<Record<GameType, string>> = {
  hexchain: "/audio/hexchain-music.mp3",
  puzzle_fighter: "/audio/puzzle-fighter-music.mp3",
};
const GAME_MUSIC_VOLUME_MULTIPLIERS: Partial<Record<GameType, number>> = { puzzle_fighter: 1.5 };
let backgroundMusic: HTMLAudioElement | null = null;
let backgroundMusicUrl = "";
let backgroundMusicPlayPending: HTMLAudioElement | null = null;
type HexchainLeaderboardEntry = { playerName: string; score: number };
let hexchainLeaderboardEntries: HexchainLeaderboardEntry[] = [];
let hexchainLeaderboardKey = "";
let hexchainLeaderboardStatus: "idle" | "loading" | "loaded" | "error" = "idle";
let blackjackAutoBetEnabled = localStorage.getItem("quiz-spark-blackjack-auto-bet") === "on";
let blackjackAutoBetSentKey = "";
let selectedSequenceCardIndex: number | null = null;
let selectedMonopolyActionCardId: string | null = null;
let connectFourHint: ConnectFourHint | null = null;
let connectFourHintLoading = false;
let previewMonopolyCardId: string | null = null;
let monopolyHistoryOpen = false;
let monopolyRulesOpen = false;
let joinQrOpen = false;
let unmountGhostMansion: (() => void) | null = null;
let unmountStainedGlass: (() => void) | null = null;
let unmountCoralChorus: (() => void) | null = null;
let unmountCatanViewport: (() => void) | null = null;
let creativeRoomMount: {destroy():void;update(view: never):void} | null = null;
let creativeRoomKey = "";
let turboRallyGame: TurboRallyGame | null = null;
let lastKingTokyoRollSoundContext = "";

localStorage.setItem("quiz-spark-player-id", myId);

function makeId() {
  return randomId();
}

function escapeHtml(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;",
  })[character]!);
}

function initials(name: string) {
  return name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function pageShell(content: string, compact = false, shellClass = "") {
  unmountCatanViewport?.(); unmountCatanViewport = null;
  const catanShell = shellClass === "app-shell--catan";
  const displayLink = room && !displayMode ? `
    <a class="shared-display-link" href="/?room=${encodeURIComponent(room.roomCode)}&amp;view=display" target="_blank" rel="noopener">
      <span aria-hidden="true">▣</span> Shared display
    </a>` : "";
  const joinQrButton = room && !displayMode ? `
    <button class="join-qr-trigger" id="join-qr-trigger" type="button" aria-label="Show the join QR code for everyone" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3h7v7H3V3Zm2 2v3h3V5H5Zm9-2h7v7h-7V3Zm2 2v3h3V5h-3ZM3 14h7v7H3v-7Zm2 2v3h3v-3H5Zm9-2h3v3h-3v-3Zm4 0h3v3h-3v-3Zm-4 4h3v3h-3v-3Zm4 0h3v3h-3v-3Z"/></svg>
    </button>` : "";
  const headerRoomCode = room && !displayMode ? `<span class="header-room-code"><small>ROOM</small><strong>${escapeHtml(room.roomCode)}</strong></span>` : "";
  const localHomeControl = room && IS_LOCAL_RUNTIME && !displayMode
    ? `<button class="display-control" id="local-games-home" type="button">Local games</button>`
    : "";
  const joinQrDialog = room && !displayMode ? `
    <div class="join-qr-overlay" id="join-qr-overlay" ${joinQrOpen ? "" : "hidden"}>
      <section class="join-qr-dialog" role="dialog" aria-modal="true" aria-labelledby="join-qr-title">
        <button class="join-qr-close" id="join-qr-close" type="button" aria-label="Close the join QR code">×</button>
        <p>SCAN TO JOIN</p>
        <h1 id="join-qr-title">Join room <strong>${escapeHtml(room.roomCode)}</strong></h1>
        <canvas id="join-qr-canvas" aria-label="QR code to join room ${escapeHtml(room.roomCode)}"></canvas>
        <div class="join-qr-room-code"><span>ROOM CODE</span><strong>${escapeHtml(room.roomCode)}</strong></div>
        <small>Open your camera and point it at the code</small>
      </section>
    </div>` : "";
  const showVoiceControl = !room || IS_LOCAL_RUNTIME || displayMode || room.isDisplay || room.isAdmin;
  const voiceControl = showVoiceControl ? `<label class="voice-control"><input id="voice-toggle" type="checkbox" ${voicesEnabled ? "checked" : ""}><span>Voices</span></label>` : "";
  return `
    <main class="app-shell ${compact ? "app-shell--compact" : ""} ${displayMode ? "app-shell--display" : ""} ${shellClass}">
      <header class="brand-bar ${displayMode ? "brand-bar--display" : ""}">
        <div class="brand-identity">
          <a class="brand" href="${displayMode ? "/?view=display" : "/"}" aria-label="${displayMode ? "Shared display setup" : "Party Stack home"}">
            <img class="brand__icon" src="/favicon-64.png" alt="" width="36" height="36" aria-hidden="true">
            <span>Party Stack${displayMode ? " <small>SHARED DISPLAY</small>" : ""}</span>
          </a>
          ${joinQrButton}
          ${headerRoomCode}
          ${room && !displayMode && !room.isDisplay ? roomChatButtonHtml() : ""}
        </div>
        ${catanShell ? '<details class="catan-app-controls"><summary>Table controls</summary>' : ""}<div class="brand-actions">
          ${!displayMode ? `<a class="leaderboard-control" id="open-saved-library" href="/?view=library"${room ? ' target="_blank" rel="noopener"' : ""}>Your library</a>` : ""}
          ${room && !displayMode && !IS_LOCAL_RUNTIME && room.phase !== "menu" && canUseRoomControl(room, "party_menu", room.isAdmin) ? `<button id="choose-party-game" class="display-control" type="button">Choose game</button>` : ""}
          ${localHomeControl}
          ${displayLink}
          ${!displayMode && shellClass !== "app-shell--leaderboard" ? `<a class="leaderboard-control" id="open-all-time-leaderboard" href="/?view=leaderboard"${room ? ` target="_blank" rel="noopener" title="Open leaderboard in a new tab"` : ""}>Leaderboard</a>` : ""}
          <label class="font-size-control" title="Change the text size for the entire app"><span aria-hidden="true">A−</span><input id="app-font-size" type="range" min="80" max="150" step="5" value="${appFontScale}" aria-label="App font size, ${appFontScale} percent"><span aria-hidden="true">A+</span><output id="app-font-size-output">${appFontScale}%</output></label>
          ${displayMode ? `${voiceControl}<button class="display-control" id="display-fullscreen" type="button"><span aria-hidden="true">⛶</span> Full screen</button><button class="display-control" id="display-exit" type="button">Exit display</button>` : `<div class="sound-control"><button class="sound-toggle music-toggle" id="music-toggle" type="button" aria-pressed="${musicEnabled}" aria-label="Turn music ${musicEnabled ? "off" : "on"}">Music ${musicEnabled ? "on" : "off"}</button><button class="sound-toggle effects-toggle" id="sound-toggle" type="button" aria-pressed="${soundEffectsEnabled}" aria-label="Turn sound effects ${soundEffectsEnabled ? "off" : "on"}">Sound effects ${soundEffectsEnabled ? "on" : "off"}</button>${voiceControl}<label class="sound-volume"><input id="sound-volume" type="range" min="0" max="100" step="5" value="${soundVolume}" aria-label="Audio volume, ${soundVolume} percent"><output id="sound-volume-output">${soundVolume}%</output></label></div>`}
          ${catanShell && room ? roomExperienceHtml({ ...room, isDisplay: displayMode || room.isDisplay }, IS_LOCAL_RUNTIME) : ""}
        </div>${catanShell ? "</details>" : ""}
      </header>
      ${room && !displayMode ? hostParticipationHtml(room, escapeHtml) : ""}
      ${room && !catanShell ? roomExperienceHtml({ ...room, isDisplay: displayMode || room.isDisplay }, IS_LOCAL_RUNTIME) : ""}
      ${content}
      ${room && !displayMode && room.gameType !== "holdem" ? observerHoldingsHtml(room, escapeHtml) : ""}
      ${joinQrDialog}
      <div class="toast" id="toast" role="status" aria-live="polite"></div>
    </main>`;
}

function gameMenuHtml(action: "create" | "switch", disabled = false) {
  const attributes = (gameType: GameType) => {
    const unavailableLocally = action === "create" && IS_LOCAL_RUNTIME && !LOCAL_GAME_TYPES.includes(gameType);
    return `${action === "create" ? "data-create-game" : "data-switch-game"}="${gameType}"${disabled || unavailableLocally ? " disabled" : ""}${unavailableLocally ? " hidden" : ""}`;
  };
  return `<div class="game-menu" aria-label="Choose a game">
    ${action === "create" ? `<a class="game-tile game-tile--spectral" href="/games/verdant-crown/" data-verdant-crown><span class="game-tile__art" aria-hidden="true">♛</span><span class="game-tile__copy"><small>SOLO · COMPUTER ARMIES · OFFLINE</small><strong>Verdant Crown</strong><em>Build a kingdom, manage your deck, and battle computer armies with an AI adviser.</em></span><span class="game-tile__go" aria-hidden="true">→</span></a>` : ""}
    ${coralChorusTileHtml(attributes("coral_chorus"))}
    ${stainedGlassTileHtml(attributes("stained_glass"))}
    ${catanTileHtml(attributes("catan"), IS_LOCAL_RUNTIME)}
    ${action === "create" && IS_LOCAL_RUNTIME ? localCatanSettingsHtml() : ""}
    <button class="game-tile game-tile--trivia" ${attributes("trivia")}>
      <span class="game-tile__art" aria-hidden="true">?</span>
      <span class="game-tile__copy"><small>FAST MULTIPLAYER</small><strong>Trivia</strong><em>Race through 2,600+ questions and closest-wins rounds.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    ${action === "create" ? `<button class="game-tile game-tile--spectral" data-ghost-mansion type="button">
      <span class="game-tile__art spectral-tile-art" aria-hidden="true"><i></i><b>✦</b><em>✦</em></span>
      <span class="game-tile__copy"><small>NEW · 1–4 LOCAL PLAYERS</small><strong>Spectral Manor</strong><em>Hunt an invisible phantom through a cinematic haunted mansion—no room or second screen required.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>` : ""}
    ${axisAlliesTileHtml(attributes("axis_allies"), action === "create" && IS_LOCAL_AXIS_ALLIES)}
    ${action === "create" && IS_LOCAL_AXIS_ALLIES ? localAxisAlliesSettingsHtml() : ""}
    ${strategoTileHtml(attributes("stratego"), action === "create" && IS_LOCAL_STRATEGO)}
    ${marketTileHtml(attributes("market_wars"), action === "create" && IS_LOCAL_RUNTIME)}
    ${modernArtTileHtml(attributes("modern_art"), action === "create" && IS_LOCAL_MODERN_ART)}
    ${hexchainTileHtml(attributes("hexchain"))}
    ${partyPetsTileHtml(attributes("party_pets"))}
    <button class="game-tile game-tile--ticket" ${attributes("ticket_to_ride")}>
      <span class="game-tile__art ticket-tile-art" aria-hidden="true"><i>🚂</i></span>
      <span class="game-tile__copy"><small>${action === "create" && IS_LOCAL_TICKET_TO_RIDE ? "LOCAL SOLO · 2 AI CONDUCTORS" : "2–5 PLAYERS · ROUTE STRATEGY"}</small><strong>Ticket to Ride</strong><em>Collect train cards, claim routes across North America, and complete private destination tickets.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--connect-four" ${attributes("connect_four")}>
      <span class="game-tile__art connect-four-tile-art" aria-hidden="true">${Array.from({ length: 8 }, () => "<i></i>").join("")}</span>
      <span class="game-tile__copy"><small>${action === "create" && LOCAL_GAME_TYPES.includes("connect_four") ? "LOCAL SOLO · AI OPPONENT" : "2 PLAYERS · CLASSIC STRATEGY"}</small><strong>Connect Four</strong><em>Drop red and yellow discs, block your rival, and connect four in a row before they do.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    ${action === "create" && IS_LOCAL_CONNECT_FOUR ? localConnectFourSettingsHtml() : ""}
    ${puzzleFighterTileHtml(attributes("puzzle_fighter"), action === "create" && IS_LOCAL_PUZZLE_FIGHTER)}
    <button class="game-tile game-tile--turbo" ${attributes("turbo_rally")}>
      <span class="game-tile__art turbo-tile-art" aria-hidden="true"><i>⚡</i><b>88</b><em>RALLY</em></span>
      <span class="game-tile__copy"><small>NEW · LIVE MULTIPLAYER</small><strong>Turbo Car Rally</strong><em>Pick a driver, grab wild power-ups, and race your friends across five original circuits. Human racers only.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--tokyo" ${attributes("king_of_tokyo")}>
      <span class="game-tile__art tokyo-tile-art" aria-hidden="true"><i>✹</i><b>KING</b><em>TOKYO</em></span>
      <span class="game-tile__copy"><small>${action === "create" && IS_LOCAL_KING_TOKYO ? "LOCAL SOLO · AI OPPONENTS" : "ROLL, SMASH &amp; RULE"}</small><strong>King of Tokyo</strong><em>Become a giant Monster, conquer Tokyo, buy wild powers, and reach 20 Victory Points—or be the last one standing.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--court" ${attributes("king_of_the_court")}>
      <span class="game-tile__art court-tile-art" aria-hidden="true"><i>🏀</i><b>KING</b><em>COURT</em></span>
      <span class="game-tile__copy"><small>${action === "create" && IS_LOCAL_KING_COURT ? "LOCAL SOLO · AI OPPONENTS" : "NBA-STYLE · 2–6 PLAYERS"}</small><strong>King of the Court</strong><em>Lead one of six legendary teams, seize the Court, build a Playbook, and race to 20 Championship Points.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    ${action === "create" && IS_LOCAL_KING_RULES ? `<section class="local-tokyo-settings" aria-labelledby="local-tokyo-player-label">
      <div><small>LOCAL GAME SETUP</small><strong id="local-tokyo-player-label">Total players</strong><span>One human player plus AI opponents</span></div>
      <fieldset aria-label="Choose the total number of King game players">
        ${[2, 3, 4, 5, 6].map((total) => `<label><input type="radio" name="local-tokyo-player-count" value="${total}" ${total === 4 ? "checked" : ""}><span><b>${total}</b><small>${total - 1} AI</small></span></label>`).join("")}
      </fieldset>
    </section>` : ""}
    <button class="game-tile game-tile--feud" ${attributes("family_feud")}>
      <span class="game-tile__art feud-tile-art" aria-hidden="true"><i>SURVEY</i><b>100</b><em>SAYS</em></span>
      <span class="game-tile__copy"><small>NEW · TWO-TEAM SHOWDOWN</small><strong>Family Feud</strong><em>Play 100 sourced survey boards. Reveal answers, track strikes, and skip any familiar round.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--holdem" ${attributes("holdem")}>
      <span class="game-tile__art holdem-tile-art" aria-hidden="true"><i>♠</i><b>A</b><em>♥</em></span>
      <span class="game-tile__copy"><small>BET &amp; BLUFF</small><strong>Texas Hold’em</strong><em>Take a random seat, play no-limit poker, and build a history one hand at a time.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--blackjack" ${attributes("blackjack")}>
      <span class="game-tile__art blackjack-tile-art" aria-hidden="true"><i>♠</i><b>21</b><em>♥</em></span>
      <span class="game-tile__copy"><small>BET, HIT &amp; HOLD</small><strong>Blackjack</strong><em>Put down a wager, play the dealer, and collect 3:2 when you hit a natural 21.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--uno" ${attributes("uno")}>
      <span class="game-tile__art uno-tile-art" aria-hidden="true"><b>UNO</b></span>
      <span class="game-tile__copy"><small>2–10 PLAYERS</small><strong>Uno</strong><em>Match colors and symbols, unleash action cards, and race to empty your hand first.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--hearts" ${attributes("hearts")}>
      <span class="game-tile__art hearts-tile-art" aria-hidden="true"><i>Q♠</i><b>♥</b><em>LOW SCORE WINS</em></span>
      <span class="game-tile__copy"><small>PASS, PLAY &amp; EVADE</small><strong>Hearts</strong><em>Four players pass three, follow suit, and dodge every heart—especially the Queen of Spades.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--spades" ${attributes("spades")}>
      <span class="game-tile__art spades-tile-art" aria-hidden="true"><i>A</i><b>♠</b><em>PARTNERS · 500</em></span>
      <span class="game-tile__copy"><small>BID, TRUMP &amp; PARTNER UP</small><strong>Spades</strong><em>Four players pair up, call their tricks, chase Nil, and race to 500 without bagging out.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--monopoly" ${attributes("monopoly_deal")}>
      <span class="game-tile__art monopoly-tile-art" aria-hidden="true"><i>DEAL</i><b>M</b><em>3 SETS</em></span>
      <span class="game-tile__copy"><small>COLLECT, CHARGE &amp; STEAL</small><strong>Monopoly Deal</strong><em>Build three property sets, collect rent, and wreck a perfect portfolio at the last second.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--codenames" ${attributes("codenames")}>
      <span class="game-tile__art codenames-tile-art" aria-hidden="true"><i></i><i></i><i></i><i></i><b>CODE<br>NAMES</b><i></i><i></i><i></i><i></i></span>
      <span class="game-tile__copy"><small>CLUE &amp; CONNECT</small><strong>Code Names</strong><em>Two teams. One-word clues. Find your agents—and never touch the assassin.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--sequence" ${attributes("sequence")}>
      <span class="game-tile__art sequence-tile-art" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><b>5</b></span>
      <span class="game-tile__copy"><small>PLAY &amp; CONNECT</small><strong>Sequence</strong><em>Match cards to the board and lock five team chips in a row.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--taboo" ${attributes("taboo")}>
      <span class="game-tile__art taboo-tile-art" aria-hidden="true"><i>DON’T SAY</i><b>TABOO</b><em>coffee · mug · beans</em></span>
      <span class="game-tile__copy"><small>DESCRIBE &amp; DODGE</small><strong>Taboo</strong><em>Race your team through secret words—without saying the forbidden clues.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--closest" ${attributes("closest")}>
      <span class="game-tile__art closest-tile-art" aria-hidden="true"><i>≤</i><b>42,069</b></span>
      <span class="game-tile__copy"><small>GUESS &amp; ARGUE</small><strong>Closest Without Going Over</strong><em>Wild numerical questions. Bid high—but one too many knocks you out.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--impostor" ${attributes("impostor")}>
      <span class="game-tile__art impostor-tile-art" aria-hidden="true"><i>?</i><b>ONE<br>WORD</b></span>
      <span class="game-tile__copy"><small>BLUFF &amp; DETECT</small><strong>One-Word Impostor</strong><em>Know the secret—or fake it. Drop one clue, then find the outsider.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--dice" ${attributes("liars_dice")}>
      <span class="game-tile__art dice-tile-art" aria-hidden="true"><i>⚄</i><b>LIAR?</b></span>
      <span class="game-tile__copy"><small>ROLL &amp; BLUFF</small><strong>Liar’s Dice</strong><em>Raise the bid—or call the bluff. Last player with dice wins.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
    <button class="game-tile game-tile--match" ${attributes("word_match")}>
      <span class="game-tile__art" aria-hidden="true"><i>FUNNY</i><b>A raccoon<br>in a tiny hat</b></span>
      <span class="game-tile__copy"><small>JUDGE &amp; MATCH</small><strong>Apples to Apples</strong><em>Play a card. Win the judge. First to the target wins.</em></span>
      <span class="game-tile__go" aria-hidden="true">→</span>
    </button>
  </div>`;
}

function renderHome() {
  unmountLibrary?.(); unmountLibrary = null;
  cleanupCreativeRoom();
  unmountLeaderboard?.();
  unmountLeaderboard = null;
  cleanupCoralChorus();
  unmountStainedGlass?.();
  unmountStainedGlass = null;
  document.body.classList.remove("is-stained-glass");
  document.title = "Party Stack";
  roomChat.setRoom(null);
  document.body.classList.remove("is-king-court");
  stopVoiceover();
  cleanupHexchain();
  turboRallyGame?.destroy();
  turboRallyGame = null;
  unmountGhostMansion?.();
  unmountGhostMansion = null;
  cleanupTimer();
  room = null;
  syncBackgroundMusic();
  const hasRoomInvite = primaryHomeAction(pendingRoom) === "join";
  const homeActions = hasRoomInvite ? `
    <div class="room-intent"><span>You’re invited to room</span><strong>${escapeHtml(pendingRoom)}</strong></div>
    <input id="room-code" class="code-input code-input--invited" maxlength="${pendingRoom.length}" inputmode="text" autocomplete="off" autocapitalize="characters" aria-label="Room code" value="${escapeHtml(pendingRoom)}" />
    <button id="join-room" class="primary-button"><span>Join room ${escapeHtml(pendingRoom)}</span><b aria-hidden="true">→</b></button>
  ` : IS_LOCAL_RUNTIME ? gameMenuHtml("create") : `
    <div class="join-room-group">
      <span class="join-room-group__label">Join a room</span>
      <div class="code-row">
        <input id="room-code" class="code-input" maxlength="3" inputmode="text" autocomplete="off" autocapitalize="characters" aria-label="Room code" placeholder="ROOM CODE" value="" />
        <button id="join-room" class="secondary-button">Join</button>
      </div>
    </div>
    <div class="divider"><span>or start a new game</span></div>
    ${gameMenuHtml("create")}
  `;
  app.innerHTML = pageShell(`
    <section class="join-card glass-card">
      <label for="player-name">Your name</label>
      <input id="player-name" maxlength="24" autocomplete="nickname" placeholder="Party legend" value="${escapeHtml(myName)}" />
      <div class="home-actions">
        ${hasRoomInvite ? "" : '<button class="secondary-button" type="button" data-start-party-night>Plan a party night</button>'}
        ${homeActions}
      </div>
      <p class="microcopy">${IS_LOCAL_RUNTIME ? "Your game stays on this computer." : "Player names and game results appear on the public all-time leaderboard. IP addresses and connection details stay in private server logs."}</p>
    </section>
    <p class="creative-offline-options">Sharing one screen? <a href="/?game=coral-chorus&amp;mode=offline">Coral Chorus offline</a> · <a href="/?game=stained-glass&amp;mode=offline">Stained Glass offline</a></p>
    ${IS_LOCAL_RUNTIME ? "" : `<button class="shared-display-entry" id="open-shared-display" type="button"><span aria-hidden="true">▣</span><strong>Set up a shared display</strong><small>Put the room’s public game board on a TV or tablet. It never joins as a player.</small><b aria-hidden="true">→</b></button>`}
  `, false, "app-shell--home app-shell--game-picker");
  bindCommon();
  const nameInput = document.querySelector<HTMLInputElement>("#player-name")!;
  document.querySelector("[data-start-party-night]")?.addEventListener("click", () => {
    if (!saveName(nameInput)) return;
    const first = IS_LOCAL_RUNTIME ? LOCAL_GAME_TYPES.includes("connect_four") ? "connect_four" : LOCAL_GAME_TYPES[0] : "trivia";
    const tile = document.querySelector<HTMLButtonElement>(`[data-create-game="${first}"]`);
    if (tile) { planPartyNightOnConnect = true; tile.click(); }
  });
  const codeInput = document.querySelector<HTMLInputElement>("#room-code");
  codeInput?.addEventListener("input", () => codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z]/g, ""));
  document.querySelectorAll<HTMLButtonElement>("[data-create-game]").forEach((button) => button.addEventListener("click", async () => {
    if (!saveName(nameInput)) return;
    unlockAudio();
    button.disabled = true;
    button.classList.add("is-busy");
    try {
      const response = await fetch(`${API_BASE}/rooms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gameType: button.dataset.createGame as GameType,
          creatorId: myId,
          ...(button.dataset.createGame === "catan" && IS_LOCAL_RUNTIME ? {botCount:Number(document.querySelector<HTMLSelectElement>("#catan-bot-count")?.value || 3)} : {}),
          ...(["king_of_tokyo", KING_COURT_GAME_TYPE].includes(button.dataset.createGame || "") && IS_LOCAL_KING_RULES ? {
            botCount: Math.max(1, Math.min(5, Number(document.querySelector<HTMLInputElement>('input[name="local-tokyo-player-count"]:checked')?.value || 4) - 1)),
          } : {}),
          ...(button.dataset.createGame === "connect_four" && IS_LOCAL_CONNECT_FOUR ? {
            perfectAi: document.querySelector<HTMLInputElement>("#local-connect-four-perfect-ai")?.checked === true,
          } : {}),
          ...(button.dataset.createGame === "axis_allies" && IS_LOCAL_AXIS_ALLIES ? {
            humanPower: document.querySelector<HTMLSelectElement>("#local-axis-power")?.value || "united_states",
          } : {}),
        }),
      });
      if (!response.ok) throw new Error("Room creation failed");
      const { code, adminToken } = await response.json() as { code: string; adminToken: string };
      localStorage.setItem(`quiz-spark-admin-${code}`, adminToken);
      joinRoom(code);
    } catch {
      button.disabled = false;
      button.classList.remove("is-busy");
      showToast("Couldn’t create a room. Check your connection.");
      planPartyNightOnConnect = false;
    }
  }));
  document.querySelector<HTMLButtonElement>("[data-ghost-mansion]")?.addEventListener("click", () => launchGhostMansion());
  document.querySelector<HTMLButtonElement>("[data-stained-glass]:not([data-create-game])")?.addEventListener("click", () => launchStainedGlass());
  document.querySelector<HTMLButtonElement>("[data-coral-chorus]:not([data-create-game])")?.addEventListener("click", () => launchCoralChorus());
  const join = () => {
    if (!codeInput) return;
    if (!saveName(nameInput)) return;
    const code = codeInput.value.trim().toUpperCase();
    if (!isRoomCode(code)) return showToast("Enter the 3-letter room code.");
    unlockAudio();
    joinRoom(code);
  };
  document.querySelector("#join-room")?.addEventListener("click", join);
  codeInput?.addEventListener("keydown", (event) => { if (event.key === "Enter") join(); });
  document.querySelector("#open-shared-display")?.addEventListener("click", () => {
    displayMode = true;
    pendingRoom = "";
    history.pushState({ sharedDisplay: true }, "", "/?view=display");
    renderDisplayJoin();
  });
}

function launchGhostMansion(pushHistory = true) {
  cleanupCoralChorus();
  unmountStainedGlass?.();
  unmountStainedGlass = null;
  document.body.classList.remove("is-stained-glass");
  roomChat.setRoom(null);
  cleanupTimer();
  const currentSocket = socket;
  socket = null;
  currentSocket?.close();
  room = null;
  syncBackgroundMusic();
  unlockAudio();
  preloadVoiceoverPack("spectral-manor");
  displayMode = false;
  if (pushHistory) history.pushState({ standaloneGame: "ghost-mansion" }, "", "/?game=ghost-mansion");
  unmountGhostMansion?.();
  unmountGhostMansion = mountGhostMansion(app, () => {
    stopVoiceover();
    unmountGhostMansion?.();
    unmountGhostMansion = null;
    history.replaceState({}, "", "/");
    renderHome();
  }, {
    playerName: document.querySelector<HTMLInputElement>("#player-name")?.value.trim() || myName,
    onResult: (result) => saveStandaloneResult(API_BASE, result),
    voicesEnabled,
    onVoicesEnabledChange: (enabled) => {
      voicesEnabled = enabled;
      persistAudioPreferences();
      updateAudioOutputLevel();
      if (!enabled) stopVoiceover();
    },
    playVoiceover,
    stopVoiceover,
  });
}

function launchStainedGlass(pushHistory = true) {
  cleanupCoralChorus();
  roomChat.setRoom(null);
  cleanupTimer();
  cleanupHexchain();
  turboRallyGame?.destroy();
  turboRallyGame = null;
  unmountGhostMansion?.();
  unmountGhostMansion = null;
  unmountStainedGlass?.();
  const currentSocket = socket;
  socket = null;
  currentSocket?.close();
  clearTimeout(reconnectTimer);
  room = null;
  stopVoiceover();
  syncBackgroundMusic();
  displayMode = false;
  document.body.classList.remove("is-king-court");
  document.body.classList.add("is-stained-glass");
  document.title = "Stained Glass Together · Party Stack";
  if (pushHistory) history.pushState({ standaloneGame: "stained-glass" }, "", "/?game=stained-glass&mode=offline");
  unmountStainedGlass = mountStainedGlass(app, () => {
    history.replaceState({}, "", "/");
    renderHome();
  }, settings => { appFontScale = settings.fontScale; });
  bindOfflineGallery(app, "stained_glass", API_BASE);
}

function cleanupCoralChorus() {
  unmountCoralChorus?.();
  unmountCoralChorus = null;
  document.body.classList.remove("is-coral-chorus");
}

function launchCoralChorus(pushHistory = true) {
  cleanupCoralChorus();
  roomChat.setRoom(null);
  cleanupTimer();
  cleanupHexchain();
  turboRallyGame?.destroy();
  turboRallyGame = null;
  unmountGhostMansion?.();
  unmountGhostMansion = null;
  unmountStainedGlass?.();
  unmountStainedGlass = null;
  const currentSocket = socket;
  socket = null;
  currentSocket?.close();
  clearTimeout(reconnectTimer);
  room = null;
  stopVoiceover();
  syncBackgroundMusic();
  displayMode = false;
  document.body.classList.remove("is-king-court", "is-stained-glass");
  document.body.classList.add("is-coral-chorus");
  document.title = "Coral Chorus · Party Stack";
  if (pushHistory) history.pushState({ standaloneGame: "coral-chorus" }, "", "/?game=coral-chorus&mode=offline");
  unmountCoralChorus = mountCoralChorus(app, () => {
    history.replaceState({}, "", "/");
    renderHome();
  }, settings => { appFontScale = settings.fontScale; });
  bindOfflineGallery(app, "coral_chorus", API_BASE);
}

function renderDisplayJoin() {
  cleanupTimer();
  room = null;
  syncBackgroundMusic();
  app.innerHTML = pageShell(`
    <section class="display-join">
      <div class="display-join__icon" aria-hidden="true">▣</div>
      <p class="eyebrow">OPTIONAL SECOND SCREEN</p>
      <h1>Put the game where everyone can see it.</h1>
      <p>Enter the same room code the players are using. This screen is read-only and never takes a seat, a team, or a private hand.</p>
      <form id="display-join-form" class="glass-card">
        <label for="display-room-code">Room code</label>
        <div><input id="display-room-code" class="code-input" maxlength="3" inputmode="text" autocomplete="off" autocapitalize="characters" aria-label="Room code" placeholder="ABC" value="${escapeHtml(pendingRoom)}"><button class="primary-button" type="submit"><span>Show room</span><b aria-hidden="true">→</b></button></div>
      </form>
      <aside><strong>Players keep their full phone views.</strong><span>The shared screen is an optional extra—you can add or remove it at any time.</span></aside>
    </section>
  `, true);
  bindCommon();
  const input = document.querySelector<HTMLInputElement>("#display-room-code")!;
  input.addEventListener("input", () => input.value = input.value.toUpperCase().replace(/[^A-Z]/g, ""));
  document.querySelector<HTMLFormElement>("#display-join-form")!.addEventListener("submit", (event) => {
    event.preventDefault();
    const code = input.value.trim().toUpperCase();
    if (!isRoomCode(code)) return showToast("Enter the 3-letter room code.");
    unlockAudio();
    pendingRoom = code;
    history.pushState({ sharedDisplayRoom: code }, "", `/?room=${code}&view=display`);
    connect();
  });
  input.focus();
}

function saveName(input: HTMLInputElement) {
  myName = input.value.trim().replace(/\s+/g, " ");
  if (myName.length < 1) {
    input.focus();
    showToast("Add your name first.");
    return false;
  }
  localStorage.setItem("quiz-spark-name", myName);
  return true;
}

function joinRoom(code: string) {
  joinQrOpen = false;
  tokyoMovementDepartures = [];
  pendingRoom = code;
  const currentParams = new URLSearchParams(location.search);
  const testParams = new URLSearchParams();
  if (currentParams.has("bots")) testParams.set("bots", currentParams.get("bots") || "10");
  if (currentParams.has("dev")) testParams.set("dev", currentParams.get("dev") || "1");
  const testQuery = testParams.size ? `&${testParams}` : "";
  history.replaceState({ partyHome: true }, "", "/");
  history.pushState({ partyRoom: code }, "", `/?room=${code}${testQuery}`);
  connect();
}

function connect() {
  clearTimeout(reconnectTimer);
  socket?.close();
  const wsBase = API_BASE.replace(/^http/, "ws");
  const params = new URLSearchParams();
  if (displayMode) params.set("role", "display");
  else {
    params.set("playerId", myId);
    let marketKey = localStorage.getItem("party-stack-market-key");
    if (!marketKey) { marketKey = randomId(); localStorage.setItem("party-stack-market-key", marketKey); }
    params.set("marketToken", marketKey);
    params.set("name", myName);
    params.set("adminToken", localStorage.getItem(`quiz-spark-admin-${pendingRoom}`) || "");
  }
  const nextSocket = new WebSocket(`${wsBase}/rooms/${pendingRoom}/ws?${params}`);
  socket = nextSocket;
  roomChat.setRoom(displayMode ? null : pendingRoom);
  roomChat.setConnected(false);
  renderConnecting();
  nextSocket.addEventListener("message", (event) => {
    if (socket !== nextSocket) return;
    const message = JSON.parse(event.data);
    if (message.type === "library_saved") {
      showToast(message.entry.kind === "art" ? "Creation saved. Open Your library to view it." : "Campaign saved. Return through Your library.");
      return;
    }
    if (message.type === "chat_state") {
      roomChat.receive(message.roomCode, message.chat);
      return;
    }
    if (message.type === "chat_error") {
      roomChat.error(message.clientId, message.message);
      return;
    }
    if (message.type === "state") {
      const hadPreviousRoomState = Boolean(room);
      const previousConnectFourMoveCount = room?.gameType === "connect_four" ? room.connectFour?.moveCount : undefined;
      const previousTokyoRollResultId = room?.kingTokyo?.lastRollResult?.id || null;
      const previousTokyoCardPurchaseId = room?.kingTokyo?.lastCardPurchase?.id || null;
      const previousTokyoYieldDecisionId = room?.kingTokyo?.lastYieldDecision?.id || null;
      const movement = message.isDisplay
        ? { events: [] as TokyoMovementEvent[], departures: [] as TokyoDeparture[] }
        : detectTokyoMovement(tokyoMovementSnapshot(room), tokyoMovementSnapshot(message as RoomState), myId, tokyoMovementDepartures);
      tokyoMovementDepartures = movement.departures;
      const previousPhase = room?.phase;
      const previousBlackjackCardCount = visibleBlackjackCardCount(room);
      const previousQuestion = room?.question?.id;
      const previousGreenCard = room?.wordMatch?.greenCard;
      const previousImpostorRound = room?.impostor?.roundNumber;
      const previousDiceRound = room?.liarsDice?.roundNumber;
      const previousSequenceHand = room?.sequence?.hand.join("|");
      const previousSequenceCanAct = room?.gameType === "sequence" && room.sequence?.canAct;
      const previousAttemptCount = room?.attempts?.length || 0;
      const previousFeudPrompt = room?.familyFeud?.prompt;
      const previousFeudReveals = room?.familyFeud?.answers.filter((answer) => answer.revealed).length || 0;
      const previousFeudStrikes = room?.familyFeud?.strikes || 0;
      const nextRoom = message as RoomState;
      if (room && room.gameType !== nextRoom.gameType) stopVoiceover();
      const voiceoverCue = voiceoverCueForTransition(room, nextRoom);
      const deferHexchainRender = shouldDeferHexchainRender(room, nextRoom, myId);
      const patchPartyPets = room?.gameType === "party_pets"
        && nextRoom.gameType === "party_pets"
        && canPatchPartyPetsMinigame(room.partyPets, nextRoom.partyPets);
      const partyPetMinigameEnded = Boolean(room?.gameType === "party_pets" && room.partyPets?.currentMinigame && !nextRoom.partyPets?.currentMinigame);
      room = nextRoom;
      const voiceoverPack = voiceoverPackForGame(room.gameType);
      if (voiceoverPack) preloadVoiceoverPack(voiceoverPack);
      if (voiceoverCue) playVoiceover(voiceoverCue);
      if (room.gameType !== "connect_four" || room.phase !== "connect_four_playing" || previousConnectFourMoveCount !== room.connectFour?.moveCount) {
        connectFourHint = null;
        connectFourHintLoading = false;
      }
      displayMode = Boolean(message.isDisplay) || displayMode;
      const dealtBlackjackCards = visibleBlackjackCardCount(room) - previousBlackjackCardCount;
      if (previousPhase && room?.gameType === "blackjack" && room.phase !== "blackjack_result" && dealtBlackjackCards > 0) playCardDealSound(dealtBlackjackCards);
      if (previousQuestion !== room?.question?.id) {
        selectedAnswer = null;
        selectedGuess = null;
        selectedSkip = false;
      }
      if (room?.wordMatch?.greenCard !== previousGreenCard || previousPhase !== room?.phase) selectedWordCard = null;
      if (room?.phase === "result" && previousPhase === "question") {
        const won = room.gameMode === "closest"
          ? room.result?.winnerIds?.includes(myId) ?? false
          : room.result?.correct ?? false;
        celebrate(won);
      } else if (room?.phase === "round_result" && previousPhase === "judging") {
        celebrate(room.wordMatch?.winner?.playerId === myId);
      } else if (room?.phase === "impostor_result" && previousPhase === "voting") {
        const result = room.impostor?.result;
        const won = room.impostor?.role === "impostor" ? !result?.caught : result?.correctVoterIds.includes(myId) || false;
        celebrate(won);
      } else if (room?.phase === "dice_result" && previousPhase === "dice_bidding") {
        celebrate(room.liarsDice?.result?.loserId !== myId);
      } else if (room?.phase === "blackjack_result" && previousPhase && previousPhase !== "blackjack_result") {
        celebrate(room.blackjack?.results.some((result) => result.playerId === myId && result.outcome === "win") || false);
      } else if (room?.gameMode === "multiple_choice" && room?.phase === "question" && room.attempts.length > previousAttemptCount) playMiss();
      if (previousImpostorRound !== room?.impostor?.roundNumber) selectedWordCard = null;
      if (previousDiceRound !== room?.liarsDice?.roundNumber) selectedWordCard = null;
      if (previousSequenceHand !== room?.sequence?.hand.join("|")) selectedSequenceCardIndex = null;
      if (room?.gameType === "sequence" && room.phase === "sequence_turn" && room.sequence?.canAct && !previousSequenceCanAct) playTurnAlert();
      const currentFeud = room?.gameType === "family_feud" ? room.familyFeud : undefined;
      if (currentFeud && currentFeud.prompt === previousFeudPrompt) {
        const reveals = currentFeud.answers.filter((answer) => answer.revealed).length;
        if (reveals > previousFeudReveals) [523, 659, 784].forEach((frequency, index) => playTone(frequency, .13, "triangle", index * .07));
        if (currentFeud.strikes > previousFeudStrikes) playMiss();
      }
      if (room?.gameType === "turbo_rally" && room.phase === "kart_racing" && turboRallyGame && room.kartRace) {
        turboRallyGame.update({ ...room.kartRace, players: room.players });
      } else if (patchPartyPets && room.partyPets) {
        if (!patchPartyPetsMinigame(partyPetsContext())) renderRoom();
      } else if (!deferHexchainRender) renderRoom();
      if (planPartyNightOnConnect && room?.phase === "lobby") {
        planPartyNightOnConnect = false;
        document.querySelector<HTMLButtonElement>('[data-experience="playlist"]')?.click();
      }
      if (partyPetMinigameEnded) void refreshStaleClientRelease();
      movement.events.forEach(showTokyoMovementPopup);
      const finalRoll = room?.kingTokyo?.lastRollResult;
      if (!displayMode && hadPreviousRoomState && finalRoll && finalRoll.playerId !== myId && finalRoll.id !== previousTokyoRollResultId) {
        showTokyoRollResultPopup(finalRoll, room!.players);
      }
      const cardPurchase = room?.kingTokyo?.lastCardPurchase;
      if (hadPreviousRoomState && cardPurchase && cardPurchase.id !== previousTokyoCardPurchaseId) {
        showTokyoCardPurchasePopup(cardPurchase, room!.players);
      }
      const yieldDecision = room?.kingTokyo?.lastYieldDecision;
      if (!displayMode && hadPreviousRoomState && yieldDecision && !yieldDecision.yielded && yieldDecision.attackerId === myId && yieldDecision.id !== previousTokyoYieldDecisionId) {
        showTokyoYieldRefusalPopup(yieldDecision, room!.players);
      }
    } else if (message.type === "connect_four_hint") {
      connectFourHintLoading = false;
      const columns = Array.isArray(message.columns)
        ? message.columns.filter((column: unknown): column is number => Number.isInteger(column) && Number(column) >= 0 && Number(column) <= 6)
        : [];
      const outcomes = ["win", "draw", "loss", "unknown"];
      if (room?.gameType === "connect_four" && room.phase === "connect_four_playing"
          && message.moveCount === room.connectFour?.moveCount && columns.length && outcomes.includes(message.outcome)) {
        connectFourHint = { moveCount: message.moveCount, columns, bestScore: Number(message.bestScore) || 0,
          outcome: message.outcome, exact: message.exact === true };
        renderConnectFour();
      }
    } else if (message.type === "error") {
      connectFourHintLoading = false;
      if (room?.phase === "menu") renderPartyMenu();
      else if (room?.gameType === "connect_four" && room.phase === "connect_four_playing") renderConnectFour();
      showToast(message.message);
    }
  });
  nextSocket.addEventListener("close", () => {
    if (socket !== nextSocket) return;
    roomChat.setConnected(false);
    if (!room) {
      pendingRoom = "";
      if (displayMode) {
        history.replaceState({ sharedDisplay: true }, "", "/?view=display");
        renderDisplayJoin();
        showToast("Couldn’t find that room. Check the code and try again.");
      } else {
        history.replaceState({}, "", "/");
        renderHome();
        showToast("Couldn’t join that room. It may be full or expired.");
      }
      return;
    }
    if (room) document.body.classList.add("is-offline");
    reconnectTimer = window.setTimeout(connect, 1400);
  });
  nextSocket.addEventListener("open", () => {
    if (socket !== nextSocket) return;
    document.body.classList.remove("is-offline");
    roomChat.setConnected(true);
  });
}

function renderConnecting() {
  app.innerHTML = pageShell(`
    <section class="center-state">
      <div class="loader"><i></i><i></i><i></i></div>
      <h1>${displayMode ? "Opening display for" : "Joining"} room ${escapeHtml(pendingRoom)}</h1>
      <p>${displayMode ? "Connecting to the live public game board…" : "Warming up the buzzers…"}</p>
    </section>
  `, true);
  bindCommon();
}

function renderRoom() {
  if (!room) return;
  if (!["coral_chorus", "stained_glass"].includes(room.gameType) || room.phase === "menu") cleanupCreativeRoom();
  if (room.phase !== "menu" && ((room.gameType === "coral_chorus" && room.coralChorus) || (room.gameType === "stained_glass" && room.stainedGlass))) return renderCreativeRoom();
  unbindPuzzleFighter(app);
  document.body.classList.toggle("is-king-court", isKingCourtGame(room.gameType));
  syncBackgroundMusic();
  if (displayMode || room.isDisplay) return renderSharedDisplay();
  if (room.gameType === "market_wars" && room.marketWars && room.isSpectator && room.phase !== "lobby" && room.phase !== "menu") return renderMarket();
  if (room.isSpectator && !["lobby", "menu", "finished"].includes(room.phase) && !["family_feud", "party_pets"].includes(room.gameType)) return renderSpectatorGame();
  if (room.gameType === "party_pets" && room.partyPets) return room.isSpectator ? renderPartyPetsSpectator() : renderPartyPetsPhone();
  if (room.phase === "menu") renderPartyMenu();
  else if (room.phase === "lobby") renderLobby();
  else if (room.gameType === "word_match" && ["submitting", "judging", "round_result"].includes(room.phase)) renderWordMatch();
  else if (room.gameType === "impostor" && ["clueing", "voting", "impostor_result"].includes(room.phase)) renderImpostor();
  else if (room.gameType === "liars_dice" && ["dice_bidding", "dice_result"].includes(room.phase)) renderLiarsDice();
  else if (room.gameType === "taboo" && ["taboo_ready", "taboo_turn", "taboo_result"].includes(room.phase)) renderTaboo();
  else if (room.gameType === "codenames" && ["codenames_clue", "codenames_guess"].includes(room.phase)) renderCodeNames();
  else if (room.gameType === "sequence" && room.phase === "sequence_turn") renderSequence();
  else if (room.gameType === "holdem" && ["poker_betting", "poker_result"].includes(room.phase)) renderPoker();
  else if (room.gameType === "blackjack" && ["blackjack_betting", "blackjack_insurance", "blackjack_playing", "blackjack_result"].includes(room.phase)) renderBlackjack();
  else if (room.gameType === "hearts" && ["hearts_passing", "hearts_playing", "hearts_round_result"].includes(room.phase)) renderHearts();
  else if (room.gameType === "spades" && ["spades_bidding", "spades_playing", "spades_round_result"].includes(room.phase)) renderSpades();
  else if (room.gameType === "uno" && ["uno_playing", "uno_round_result"].includes(room.phase)) renderUno();
  else if (room.gameType === "monopoly_deal" && room.phase === "monopoly_turn") renderMonopolyDeal();
  else if (["king_of_tokyo", KING_COURT_GAME_TYPE].includes(room.gameType) && ["tokyo_roll", "tokyo_yield", "tokyo_costume", "tokyo_buy"].includes(room.phase)) renderKingTokyo();
  else if (room.gameType === "turbo_rally" && room.phase === "kart_racing") renderTurboRally();
  else if (room.gameType === "family_feud" && ["feud_board", "feud_round_result"].includes(room.phase)) renderFamilyFeud();
  else if (room.gameType === "hexchain" && room.phase === "hexchain_battle" && room.hexchain) renderHexchainGame();
  else if (room.gameType === "connect_four" && room.phase === "connect_four_playing" && room.connectFour) renderConnectFour();
  else if (room.gameType === "puzzle_fighter" && room.phase === "puzzle_fighter_playing" && room.puzzleFighter) renderPuzzleFighter();
  else if (room.gameType === "ticket_to_ride" && room.phase === "ticket_to_ride_playing" && room.ticketToRide) renderTicketToRide();
  else if (room.gameType === "axis_allies" && room.phase === "axis_allies_playing" && room.axisAllies) renderAxisAllies();
  else if (room.gameType === "stratego" && room.phase === "stratego_playing" && room.stratego) renderStratego();
  else if (room.gameType === "market_wars" && room.marketWars) renderMarket();
  else if (room.gameType === "modern_art" && room.phase === "modern_art_playing" && room.modernArt) renderModernArt();
  else if (room.gameType === "catan" && room.phase === "catan_playing" && room.catan) renderCatan();
  else if (room.phase === "question" || room.phase === "result") renderQuestion();
  else if (room.phase === "finished") renderFinished();
  else renderRoomSyncing();
}

function renderSpectatorGame() {
  turboRallyGame?.destroy();
  turboRallyGame = null;
  cleanupHexchain();
  if (room!.gameType === "holdem" && room!.isAdmin && room!.poker?.observerHands) {
    const historyExpansion = new Map(Array.from(app.querySelectorAll<HTMLDetailsElement>("[data-poker-history-hand]"), (detail) => [detail.dataset.pokerHistoryHand, detail.open] as const));
    app.innerHTML = pageShell(pokerObserverHtml(room!, { escapeHtml, money, cardHtml: pokerCardHtml, actionText: pokerActionText }, pokerNextHandStatusHtml(room!)), true);
    app.querySelectorAll<HTMLDetailsElement>("[data-poker-history-hand]").forEach((detail) => {
      if (historyExpansion.has(detail.dataset.pokerHistoryHand)) detail.open = historyExpansion.get(detail.dataset.pokerHistoryHand)!;
    });
    bindCommon();
    if (room!.phase === "poker_result" && room!.deadline) startTimer(7_000);
  } else renderSharedDisplay();
  const control = spectatorRoundControl(room!.phase);
  if (control && room!.isAdmin) {
    const section = document.createElement("div");
    section.className = "sticky-action";
    section.innerHTML = `<button id="spectator-next-round" class="primary-button"><span>${control.label}</span><b>→</b></button>`;
    document.querySelector(".app-shell")?.append(section);
    section.querySelector("button")?.addEventListener("click", () => send({ type: control.type }));
  }
}

function renderPartyPetsSpectator() {
  cleanupTimer();
  const context = partyPetsContext();
  app.innerHTML = pageShell(`${partyPetsDisplayHtml(context)}${room!.phase === "lobby" ? `<div class="sticky-action"><button id="spectator-pets-start" class="primary-button" ${Object.keys(context.state.pets).length ? "" : "disabled"}>Start Party Pets</button><p>Waiting for the other players to create their pets.</p></div>` : ""}${hostControlsHtml(context)}`);
  bindCommon();
  bindPartyPetsDisplay(context);
  bindPartyPetsPhone(context);
  document.querySelector("#spectator-pets-start")?.addEventListener("click", () => send({ type: "start" }));
}

function renderRoomSyncing() {
  cleanupTimer();
  app.innerHTML = pageShell(`
    <section class="center-state">
      <div class="loader"><i></i><i></i><i></i></div>
      <h1>Getting the game ready…</h1>
      <p>The room is syncing to the latest game state.</p>
    </section>
  `, true);
  bindCommon();
}

function partyPetsContext() {
  return {
    roomCode: room!.roomCode,
    isAdmin: room!.isAdmin,
    playerId: myId,
    players: room!.players,
    state: room!.partyPets!,
    send,
    escapeHtml,
    tap,
    showToast,
  };
}

function renderPartyPetsPhone() {
  cleanupTimer();
  const context = partyPetsContext();
  app.innerHTML = pageShell(partyPetsPhoneHtml(context));
  bindCommon();
  bindPartyPetsPhone(context);
}

function renderPartyPetsDisplay() {
  cleanupTimer();
  const context = partyPetsContext();
  displayShell(partyPetsDisplayHtml(context), "shared-display--party-pets");
  const canvas = document.querySelector<HTMLCanvasElement>("#pp-tv-qr");
  if (canvas) void QRCode.toCanvas(canvas, `${location.origin}/?room=${encodeURIComponent(room!.roomCode)}`, {
    width: 112, margin: 1, color: { dark: "#202039", light: "#fffaf0" }, errorCorrectionLevel: "M",
  });
  bindPartyPetsDisplay(context);
}

function hexchainContext() {
  return {
    roomCode: room!.roomCode,
    isAdmin: room!.isAdmin,
    playerId: myId,
    players: room!.players,
    state: room!.hexchain!,
    send,
    escapeHtml,
    initials,
    tap,
    tone: playHexchainTone,
    sweep: playHexchainSweep,
    noise: playHexchainNoise,
    showToast,
    refreshAfterGesture: renderRoom,
  };
}

function renderHexchainGame() {
  const context = hexchainContext();
  app.innerHTML = pageShell(hexchainGameHtml(context));
  bindCommon();
  bindHexchainGame(context);
}

function renderHexchainDisplay() {
  const context = hexchainContext();
  displayShell(hexchainDisplayHtml(context), "shared-display--hexchain");
  bindHexchainDisplay(context);
}

function renderConnectFourLobby() {
  const current = room!;
  app.innerHTML = pageShell(connectFourLobbyHtml(current.connectFour!, current.players, myId, current.isAdmin));
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click", () => {
    unlockAudio();
    playTone(294, .08, "triangle", .04);
    send({ type: "start", startingPlayerId: document.querySelector<HTMLSelectElement>("#connect-four-starter")?.value || null });
  });
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderConnectFour() {
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(connectFourGameHtml(current.connectFour!, current.players, myId,
    IS_LOCAL_CONNECT_FOUR ? { enabled: true, hint: connectFourHint, hintLoading: connectFourHintLoading } : undefined), true);
  bindCommon();
  bindConnectFour(app, sendConnectFourAction);
}

function sendConnectFourAction(message: Record<string, unknown>) {
  if (message.type === "connect_four_hint") {
    connectFourHint = null;
    connectFourHintLoading = true;
    send(message);
    if (room?.gameType === "connect_four" && room.phase === "connect_four_playing") renderConnectFour();
    return;
  }
  if (message.type === "connect_four_back") {
    connectFourHint = null;
    connectFourHintLoading = false;
  }
  send(message);
}

function renderConnectFourDisplay() {
  const current = room!;
  displayShell(connectFourDisplayHtml(current.connectFour!, current.players), "shared-display--connect-four");
}

function renderPuzzleFighterLobby() {
  const current = room!;
  app.innerHTML = pageShell(puzzleFighterLobbyHtml(current.puzzleFighter!, current.players, myId, current.isAdmin), false, "app-shell--puzzle-fighter");
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click", () => send({ type: "start" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderPuzzleFighter() {
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(puzzleFighterGameHtml(current.puzzleFighter!, current.players, myId), true, "app-shell--puzzle-fighter");
  bindCommon();
  bindPuzzleFighter(app, current.puzzleFighter!, myId, send);
}

function renderPuzzleFighterDisplay() {
  const current = room!;
  displayShell(puzzleFighterDisplayHtml(current.puzzleFighter!, current.players), "shared-display--puzzle-fighter", "app-shell--puzzle-fighter");
  animatePuzzleFighterDrops(app);
  animatePuzzleFighterImpact(app, current.puzzleFighter!);
}

function renderTicketToRideLobby() {
  const current = room!;
  app.innerHTML = pageShell(ticketToRideLobbyHtml({ ...current, ticketToRide: current.ticketToRide! }, IS_LOCAL_TICKET_TO_RIDE));
  bindCommon();
  bindTicketToRideLobby(app, send);
  document.querySelector("#start-game")?.addEventListener("click", () => send({ type: "start" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderTicketToRide() {
  const current = room!;
  app.innerHTML = pageShell(ticketToRideGameHtml(current.ticketToRide!, current.players, myId), false, "app-shell--ticket-game");
  bindCommon();
  bindTicketToRide(app, current.ticketToRide!, send, showToast);
}

function renderTicketToRideDisplay() {
  const current = room!;
  displayShell(ticketToRideDisplayHtml(current.ticketToRide!, current.players), "shared-display--ticket-to-ride");
}

function renderAxisAlliesLobby() {
  const current = room!;
  app.innerHTML = pageShell(axisAlliesLobbyHtml(current.axisAllies!, current.players, current.isAdmin, IS_LOCAL_AXIS_ALLIES));
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click", () => send({ type: "start", ...(IS_LOCAL_AXIS_ALLIES ? { humanPower: document.querySelector<HTMLInputElement>('input[name="axis-lobby-human-power"]:checked')?.value || "united_states" } : {}) }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderAxisAllies() {
  const current = room!;
  app.innerHTML = pageShell(axisAlliesGameHtml(current.axisAllies!, current.players), false, "app-shell--axis-game");
  bindCommon(); bindAxisAllies(app, current.axisAllies!, send, showToast);
}

function renderAxisAlliesDisplay() {
  const current = room!;
  displayShell(axisAlliesDisplayHtml(current.axisAllies!, current.players), "shared-display--axis-allies");
}

function renderStrategoLobby() {
  const current = room!;
  app.innerHTML = pageShell(strategoLobbyHtml(current.players, current.isAdmin, IS_LOCAL_STRATEGO), false, "app-shell--stratego-game");
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click", () => send({ type: "start" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderStratego() {
  const current = room!;
  app.innerHTML = pageShell(strategoGameHtml(current.stratego!, current.players), false, "app-shell--stratego-game");
  bindCommon(); bindStratego(app, current.stratego!, send, showToast);
}

function renderStrategoDisplay() {
  const current = room!;
  displayShell(strategoDisplayHtml(current.stratego!, current.players), "shared-display--stratego", "app-shell--stratego-game");
}

function cleanupCreativeRoom() {
  creativeRoomMount?.destroy(); creativeRoomMount = null; creativeRoomKey = "";
}

function syncCreativeHeader() {
  for (const [id,label,enabled] of [["music-toggle","Music",musicEnabled],["sound-toggle","Sound effects",soundEffectsEnabled]] as const) {
    const button = document.getElementById(id);
    if (!button) continue;
    button.textContent = `${label} ${enabled ? "on" : "off"}`;
    button.setAttribute("aria-pressed",String(enabled));
    button.setAttribute("aria-label",`Turn ${label.toLowerCase()} ${enabled ? "off" : "on"}`);
  }
  const voices = document.querySelector<HTMLInputElement>("#voice-toggle");
  if (voices) voices.checked = voicesEnabled;
  for (const [id,value,label] of [["app-font-size",appFontScale,"App font size"],["sound-volume",soundVolume,"Audio volume"]] as const) {
    const input = document.querySelector<HTMLInputElement>(`#${id}`);
    if (input) {input.value = String(value); input.setAttribute("aria-label",`${label}, ${value} percent`);}
    const output = document.querySelector<HTMLOutputElement>(`#${id}-output`);
    if (output) output.value = `${value}%`;
  }
}

function renderCreativeRoom() {
  const current = room!;
  const key = `${current.roomCode}:${current.gameType}:${displayMode || current.isDisplay ? "display" : current.isSpectator ? "spectator" : "player"}:${current.isAdmin}`;
  const view = current.gameType === "coral_chorus" ? current.coralChorus! : current.stainedGlass!;
  if (creativeRoomMount && key === creativeRoomKey && document.querySelector("#creative-room-mount")) {
    creativeRoomMount.update(view as never); syncCreativeHeader();
    const panel = document.querySelector("#room-experiences");
    if (panel) panel.outerHTML = roomExperienceHtml({ ...current, isDisplay: displayMode || current.isDisplay }, IS_LOCAL_RUNTIME);
    bindRoomExperiences(document.querySelector("#room-experiences") || document.createElement("div"), () => room, send, IS_LOCAL_RUNTIME ? LOCAL_GAME_TYPES : []);
    return;
  }
  cleanupCreativeRoom();
  app.innerHTML = pageShell('<div id="creative-room-mount"></div>', false, "app-shell--creative-room");
  bindCommon();
  const root = document.querySelector<HTMLElement>("#creative-room-mount")!;
  const options = {
    isAdmin: current.isAdmin && !current.isDisplay && !displayMode,
    sendAction: (action: unknown) => send({type: current.gameType === "coral_chorus" ? "coral_action" : "stained_glass_action", action}),
    onHome: () => current.isAdmin && !IS_LOCAL_RUNTIME ? send({type:"party_menu"}) : leaveRoom(),
    onSettingsSave: (settings: {fontScale:number}) => {appFontScale = settings.fontScale; syncCreativeHeader();},
  };
  creativeRoomMount = (current.gameType === "coral_chorus"
    ? mountCoralChorusRoom(root, current.coralChorus!, options)
    : mountStainedGlassRoom(root, current.stainedGlass!, {
      ...options,
      mountChat: current.isDisplay || displayMode ? undefined : host => roomChat.attachToCreation(host),
    })) as typeof creativeRoomMount;
  creativeRoomKey = key;
}

function renderCatanLobby() {
  const current = room!;
  app.innerHTML = pageShell(catanLobbyHtml(current.catan!,current.players,current.isAdmin,IS_LOCAL_RUNTIME),false,"app-shell--catan");
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click",()=>send({type:"start"}));
  document.querySelector("#leave")?.addEventListener("click",leaveRoom);
}
function renderCatan() {
  const current = room!;
  app.innerHTML = pageShell(catanGameHtml(current.catan!,current.players,myId),false,"app-shell--catan");
  bindCommon(); bindCatanActions(app,send);
  unmountCatanViewport = bindCatanViewport(app);
}
function renderCatanDisplay() {
  const current = room!;
  displayShell(catanDisplayHtml(current.catan!,current.players),"shared-display--catan","app-shell--catan");
  unmountCatanViewport = bindCatanViewport(app);
}
function renderCatanFinished() {
  const current = room!;
  app.innerHTML = pageShell(`${catanFinishedHtml(current.catan!,current.players)}<div class="sticky-action"><button id="rematch" class="primary-button">Rematch</button><button id="change-game" class="secondary-button">Choose another game</button><button id="leave" class="text-button">Leave room</button></div>`,false,"app-shell--catan");
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click",()=>send({type:"reset"}));
  document.querySelector("#change-game")?.addEventListener("click",()=>IS_LOCAL_RUNTIME ? leaveRoom() : send({type:"party_menu"}));
  document.querySelector("#leave")?.addEventListener("click",leaveRoom);
}

function renderMarket() {
  const current = room!;
  localStorage.setItem("party-stack-market-room", current.roomCode);
  retainMarketDraft(app);
  setMarketUiRoom(current.roomCode);
  const focused = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  const draftKey = focused?.dataset?.mwDraft;
  const selectionStart = focused?.selectionStart, selectionEnd = focused?.selectionEnd;
  const scroll = document.querySelector(".mw-watchlist")?.scrollTop || 0;
  app.innerHTML = pageShell(current.phase === "lobby" ? marketLobbyHtml(current.marketWars!, current.players, current.isAdmin, IS_LOCAL_RUNTIME) : marketGameHtml(current.marketWars!, current.players, myId, current.isAdmin, IS_LOCAL_RUNTIME, current.isSpectator && !current.isAdmin), false, "app-shell--market");
  bindCommon(); bindMarket(app, current.marketWars!, send, renderMarket);
  const list = app.querySelector(".mw-watchlist"); if (list) list.scrollTop = scroll;
  if (draftKey) {
    const target = app.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-mw-draft="${draftKey}"]`);
    target?.focus();
    if (target && typeof selectionStart === "number" && typeof selectionEnd === "number" && typeof target.setSelectionRange === "function") target.setSelectionRange(selectionStart, selectionEnd);
  }
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderModernArtLobby() {
  const current = room!;
  app.innerHTML = pageShell(modernArtLobbyHtml(current.modernArt!, current.players, current.isAdmin, IS_LOCAL_MODERN_ART));
  bindCommon();
  document.querySelector("#start-game")?.addEventListener("click", () => send({ type: "start" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
}

function renderModernArt() {
  const current = room!;
  app.innerHTML = pageShell(modernArtGameHtml(current.modernArt!, current.players, myId), false, "app-shell--modern-art");
  bindCommon(); bindModernArt(app, current.modernArt!, send);
}

function renderModernArtDisplay() {
  const current = room!;
  displayShell(modernArtDisplayHtml(current.modernArt!, current.players), "shared-display--modern-art");
}

function displayGameTitle(gameType: GameType) {
  return ({
    trivia: "Trivia", word_match: "Apples to Apples", impostor: "One-Word Impostor", closest: "Closest Without Going Over",
    liars_dice: "Liar’s Dice", taboo: "Taboo", codenames: "Code Names", sequence: "Sequence", holdem: "Texas Hold’em", blackjack: "Blackjack", hearts: "Hearts", spades: "Spades", uno: "Uno", monopoly_deal: "Monopoly Deal", king_of_tokyo: "King of Tokyo", king_of_the_court: "King of the Court", turbo_rally: "Turbo Car Rally", party_pets: "Party Pets", family_feud: "Family Feud", hexchain: "Hexchain Siege", connect_four: "Connect Four", puzzle_fighter: "Puzzle Fighter", ticket_to_ride: "Ticket to Ride", axis_allies: "Axis & Allies", stratego: "Stratego", modern_art: "Modern Art", market_wars: "Wall Street: Market Wars", catan: "Settlers of Catan", coral_chorus: "Coral Chorus", stained_glass: "Stained Glass Together",
  } as Record<GameType, string>)[gameType];
}

function displayShell(content: string, className = "", shellClass = "") {
  const current = room!;
  app.innerHTML = pageShell(`
    <section class="display-room-head">
      <div><span>${current.isSpectator ? "WATCHING" : "SHARED DISPLAY"} · ${escapeHtml(displayGameTitle(current.gameType)).toUpperCase()}</span><strong>ROOM ${escapeHtml(current.roomCode)}</strong></div>
      <p><i></i> LIVE</p>
    </section>
    <div class="shared-display ${className}">${content}</div>
  `, true, shellClass);
  bindCommon();
}

function renderSharedDisplay() {
  cleanupTimer();
  const current = room!;
  if (current.gameType === "party_pets" && current.partyPets) return renderPartyPetsDisplay();
  if (current.phase === "menu" || current.phase === "lobby") return renderDisplayLobby();
  if (current.phase === "finished" && current.gameType !== "market_wars") return renderDisplayFinished();
  if (current.gameType === "sequence" && current.sequence) return renderDisplaySequence();
  if (current.gameType === "codenames" && current.codeNames) return renderDisplayCodeNames();
  if (current.gameType === "monopoly_deal" && current.monopolyDeal) return renderDisplayMonopolyDeal();
  if (current.gameType === "hearts" && current.hearts) return renderDisplayHearts();
  if (current.gameType === "spades" && current.spades) return renderDisplaySpades();
  if (current.gameType === "uno" && current.uno) return renderDisplayUno();
  if (["king_of_tokyo", KING_COURT_GAME_TYPE].includes(current.gameType) && current.kingTokyo) return renderDisplayKingTokyo();
  if (current.gameType === "family_feud" && current.familyFeud) return renderFamilyFeud(true);
  if (current.gameType === "hexchain" && current.hexchain && current.phase === "hexchain_battle") return renderHexchainDisplay();
  if (current.gameType === "connect_four" && current.connectFour) return renderConnectFourDisplay();
  if (current.gameType === "puzzle_fighter" && current.puzzleFighter) return renderPuzzleFighterDisplay();
  if (current.gameType === "ticket_to_ride" && current.ticketToRide) return renderTicketToRideDisplay();
  if (current.gameType === "axis_allies" && current.axisAllies) return renderAxisAlliesDisplay();
  if (current.gameType === "stratego" && current.stratego) return renderStrategoDisplay();
  if (current.gameType === "market_wars" && current.marketWars) { retainMarketDraft(app); setMarketUiRoom(current.roomCode); displayShell(marketGameHtml(current.marketWars, current.players, "", false, IS_LOCAL_RUNTIME, true), "shared-display--market"); bindMarket(app, current.marketWars, () => {}, renderSharedDisplay); return; }
  if (current.gameType === "modern_art" && current.modernArt) return renderModernArtDisplay();
  if (current.gameType === "catan" && current.catan) return renderCatanDisplay();
  return renderDisplayGameStatus();
}

function renderDisplayLobby() {
  const current = room!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const connected = current.players.filter((player) => player.connected);
  const choosing = current.phase === "menu";
  displayShell(`
    <section class="display-lobby">
      <div class="display-lobby__intro">
        <p class="eyebrow">${choosing ? "NEXT GAME" : "READY TO PLAY"}</p>
        <h1>${choosing ? "The group is choosing a game." : escapeHtml(displayGameTitle(current.gameType))}</h1>
        <p>${choosing ? "This display will switch automatically when the admin makes a choice." : "Keep this screen open. The board will appear automatically when the game starts."}</p>
        <div class="display-player-count"><strong>${connected.length}</strong><span>player${connected.length === 1 ? "" : "s"} connected</span></div>
        <ul>${connected.map((player, index) => `<li><span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><strong>${escapeHtml(player.name)}</strong>${player.isAdmin ? "<small>ADMIN</small>" : ""}</li>`).join("") || "<li class=\"is-empty\">Waiting for players…</li>"}</ul>
      </div>
      <aside class="display-join-qr glass-card"><canvas id="display-player-qr" aria-label="QR code for players to join"></canvas><strong>Players join room ${escapeHtml(current.roomCode)}</strong><span>Scan with a phone. This tablet remains a read-only display.</span></aside>
    </section>
  `, "shared-display--lobby");
  QRCode.toCanvas(document.querySelector("#display-player-qr"), joinUrl, { width: 260, margin: 1, color: { dark: "#171236", light: "#fff9ec" }, errorCorrectionLevel: "M" });
}

function renderDisplaySequence() {
  const current = room!;
  const game = current.sequence!;
  const activeTeams = (["coral", "aqua", "gold"] as SequenceTeam[]).slice(0, game.teamCount);
  const scores = activeTeams.map((team) => {
    const players = current.players.filter((player) => game.teams[player.id] === team);
    return `<div class="sequence-team-score sequence-team-score--${team}"><span>${escapeHtml(sequenceTeamName(team))}</span><strong>${game.teamScores[team]}</strong><small>of ${game.targetSequences}</small><p>${players.map((player) => escapeHtml(player.name)).join(" · ") || "Waiting for players"}</p></div>`;
  }).join("");
  const board = game.board.map((card, index) => {
    const chip = game.chips[index];
    const isCorner = card === null;
    return `<div class="sequence-board-space ${isCorner ? "is-corner" : ""} ${chip ? `has-chip has-chip--${chip}` : ""} ${game.locked[index] ? "is-locked" : ""} ${game.lastMove?.boardIndex === index ? "is-last-move" : ""}">
      ${isCorner ? `<span class="sequence-free"><b>FREE</b><i>★</i></span>` : sequenceCardFace(card, true)}
      ${chip ? `<span class="sequence-chip sequence-chip--${chip}"><i></i>${game.locked[index] ? "<b>◆</b>" : ""}</span>` : ""}
    </div>`;
  }).join("");
  const lastMove = game.lastMove ? `<p class="sequence-last-move"><span class="sequence-last-chip sequence-last-chip--${game.lastMove.team}"></span><strong>${escapeHtml(game.lastMove.playerName)}</strong> ${game.lastMove.action === "swap" ? "exchanged a dead card" : game.lastMove.action === "remove" ? "removed a rival chip" : "placed a chip"}${game.lastMove.sequencesMade ? ` and completed ${game.lastMove.sequencesMade} sequence${game.lastMove.sequencesMade === 1 ? "" : "s"}!` : "."}</p>` : `<p class="sequence-last-move">The first move will appear here.</p>`;
  displayShell(`
    ${sequenceTurnOrderHtml(game, false)}
    <section class="display-sequence-status"><div><span>TURN ${game.turnNumber}</span><strong>${game.currentPlayerName ? `${escapeHtml(game.currentPlayerName)}’S TURN` : "Waiting for a player"}</strong></div><p><i class="sequence-legend-chip sequence-legend-chip--${game.teams[game.currentPlayerId || ""] || "coral"}"></i>${escapeHtml(sequenceTeamName(game.teams[game.currentPlayerId || ""] || null))} to play</p></section>
    <section class="sequence-scoreboard sequence-scoreboard--${game.teamCount}">${scores}</section>
    ${lastMove}
    <section class="sequence-board-wrap"><div class="sequence-board" aria-label="Shared Sequence board">${board}</div></section>
    <aside class="display-board-note"><span>Players make every move on their phones.</span><strong>${game.deckRemaining} cards remain in the draw pile</strong></aside>
  `, "shared-display--sequence");
}

function renderDisplayCodeNames() {
  const current = room!;
  const game = current.codeNames!;
  const activeTeam = game.activeTeam || "red";
  const board = game.board.map((card, index) => {
    const roleClass = card.role ? `codenames-card--${card.role}` : "";
    const roleLabel = card.role === "assassin" ? "Assassin" : card.role === "neutral" ? "Neutral" : card.role ? `${codeNamesTeamName(card.role)} agent` : "Hidden agent";
    return `<div class="codenames-card ${roleClass} ${card.revealed ? "is-revealed" : ""} ${game.lastGuess?.index === index ? "is-last-guess" : ""}" aria-label="${escapeHtml(card.word)}. ${escapeHtml(roleLabel)}"><span>${escapeHtml(card.word)}</span>${card.revealed ? `<small>${escapeHtml(roleLabel)}</small>` : ""}</div>`;
  }).join("");
  const clue = current.phase === "codenames_guess" && game.clue
    ? `<section class="display-codenames-clue display-codenames-clue--${activeTeam}"><span>${game.bonusGuessAvailable ? "BONUS GUESS" : "THE CLUE"}</span><strong>${escapeHtml(game.clue.word)} <em>for ${game.clue.count}</em></strong><small>${game.guessesLeft} regular guess${game.guessesLeft === 1 ? "" : "es"} remaining</small></section>`
    : `<section class="display-codenames-clue display-codenames-clue--${activeTeam}"><span>${activeTeam.toUpperCase()} SPYMASTER</span><strong>${escapeHtml(game.spymasterNames[activeTeam] || "Thinking…")}</strong><small>Waiting for the next clue</small></section>`;
  displayShell(`
    <section class="display-codenames-scoreboard"><div class="is-red ${activeTeam === "red" ? "is-active" : ""}"><span>RED AGENTS</span><strong>${game.remaining.red}</strong></div><p>TURN ${game.turnNumber}<b>${activeTeam.toUpperCase()} TEAM</b></p><div class="is-blue ${activeTeam === "blue" ? "is-active" : ""}"><span>BLUE AGENTS</span><strong>${game.remaining.blue}</strong></div></section>
    ${clue}
    ${game.lastGuess ? `<p class="codenames-last">${escapeHtml(game.lastGuess.playerName)} uncovered <strong>${escapeHtml(game.board[game.lastGuess.index]?.word || "a card")}</strong></p>` : ""}
    <section class="codenames-board display-codenames-board" aria-label="Shared Code Names board">${board}</section>
    <aside class="display-board-note"><span>Only revealed identities appear here. Spymaster keys stay private on their phones.</span><strong>Players submit clues and guesses on their phones.</strong></aside>
  `, "shared-display--codenames");
}

const monopolyColorLabels: Record<MonopolyColor, string> = { brown: "Brown", light_blue: "Light Blue", pink: "Pink", orange: "Orange", red: "Red", yellow: "Yellow", green: "Green", dark_blue: "Dark Blue", railroad: "Railroads", utility: "Utilities" };
const monopolySetSizes: Record<MonopolyColor, number> = { brown: 2, light_blue: 3, pink: 3, orange: 3, red: 3, yellow: 3, green: 3, dark_blue: 2, railroad: 4, utility: 2 };
const monopolyColorOrder: MonopolyColor[] = ["brown", "light_blue", "pink", "orange", "red", "yellow", "green", "dark_blue", "railroad", "utility"];
const monopolyHandKindOrder: Record<MonopolyCard["kind"], number> = { property: 0, wild: 1, money: 2, rent: 3, improvement: 4, action: 5 };
const monopolyMoney = (value: number) => `$${value}M`;

function sortedMonopolyHand(cards: MonopolyCard[]) {
  return [...cards].sort((left, right) => {
    const kindDifference = monopolyHandKindOrder[left.kind] - monopolyHandKindOrder[right.kind];
    if (kindDifference) return kindDifference;
    if (left.kind === "money" && right.kind === "money" && left.value !== right.value) return left.value - right.value;
    if (["property", "wild", "rent"].includes(left.kind)) {
      const leftColor = Math.min(...(left.colors || []).map((color) => monopolyColorOrder.indexOf(color)), monopolyColorOrder.length);
      const rightColor = Math.min(...(right.colors || []).map((color) => monopolyColorOrder.indexOf(color)), monopolyColorOrder.length);
      if (leftColor !== rightColor) return leftColor - rightColor;
    }
    const nameDifference = left.name.localeCompare(right.name);
    return nameDifference || left.value - right.value || left.id.localeCompare(right.id);
  });
}

function monopolyActionCopy(card: MonopolyCard) {
  return ({
    pass_go: "Draw 2 cards", sly_deal: "Steal an unprotected property", forced_deal: "Swap unprotected properties", deal_breaker: "Steal a complete set",
    debt_collector: "Charge one player $5M", birthday: "Everyone pays you $2M", just_say_no: "Cancel an Action against you", double_rent: "Play with Rent to double it",
    house: "Add $3M rent to a complete set", hotel: "Add $4M rent after a House", rent: card.key === "rent_any" ? "Choose one player and one color" : "Everyone pays rent in one color",
  } as Record<string, string>)[card.action || ""] || "Bank it or play its action";
}

function monopolyCardHtml(card: MonopolyCard, compact = false, inspectable = false) {
  const colors = card.colors || [];
  const mainColor = colors[0] || "money";
  const secondColor = colors[1];
  const label = card.kind === "money" ? "MONEY" : card.kind === "property" ? "PROPERTY" : card.kind === "wild" ? "WILD PROPERTY" : card.kind === "rent" ? "RENT" : card.kind === "improvement" ? "BUILD" : "ACTION";
  return `<article class="monopoly-card monopoly-card--${card.kind} monopoly-card--${mainColor} ${secondColor ? `monopoly-card--split` : ""} ${compact ? "monopoly-card--compact" : ""}" ${secondColor ? `style="--split-color:var(--md-${secondColor})"` : ""}${inspectable ? ` data-monopoly-preview="${card.id}" role="button" tabindex="0" aria-label="Enlarge ${escapeHtml(card.name)}"` : ""}>
    <div class="monopoly-card__band"><small>${label}</small><strong>${escapeHtml(card.name)}</strong></div>
    <div class="monopoly-card__body">${card.kind === "money" ? `<b>${monopolyMoney(card.value)}</b><span>MONOPOLY BANK</span>` : `<b>${card.action === "deal_breaker" ? "⚡" : card.action === "just_say_no" ? "NO!" : card.action === "birthday" ? "★" : card.kind === "property" ? monopolySetSizes[mainColor as MonopolyColor] : card.kind === "wild" ? "↔" : card.action === "rent" ? "$" : card.action === "house" ? "⌂" : card.action === "hotel" ? "▥" : "+"}</b><span>${escapeHtml(card.kind === "property" ? `${monopolySetSizes[mainColor as MonopolyColor]} cards complete this set` : card.kind === "wild" ? "Choose either color for this property" : monopolyActionCopy(card))}</span>`}</div>
    <div class="monopoly-card__value">${monopolyMoney(card.value)}</div>
  </article>`;
}

const monopolyHandGroups: Array<{ label: string; kinds: MonopolyCard["kind"][] }> = [
  { label: "Properties", kinds: ["property"] },
  { label: "Wild properties", kinds: ["wild"] },
  { label: "Money", kinds: ["money"] },
  { label: "Rent", kinds: ["rent"] },
  { label: "Buildings", kinds: ["improvement"] },
  { label: "Actions", kinds: ["action"] },
];

function monopolyCardPreviewHtml(card: MonopolyCard) {
  const detail = card.kind === "money" ? "Bank this card as money." : card.kind === "property" ? `${monopolySetSizes[(card.colors || ["brown"])[0]]} cards complete this property set.` : card.kind === "wild" ? `Play it as ${card.colors?.map((color) => monopolyColorLabels[color]).join(" or ")}.` : monopolyActionCopy(card);
  return `<div class="monopoly-preview-overlay" id="monopoly-preview-overlay" role="presentation"><section class="monopoly-preview" role="dialog" aria-modal="true" aria-label="${escapeHtml(card.name)} card preview"><button type="button" id="monopoly-preview-close" aria-label="Close card preview">×</button><span>CARD PREVIEW</span>${monopolyCardHtml(card)}<footer><strong>${escapeHtml(card.name)}</strong><p>${escapeHtml(detail)}</p><b>${monopolyMoney(card.value)} value</b></footer></section></div>`;
}

function monopolyHistoryHtml(log: string[], open = false) {
  const recent = [...log].reverse();
  return `<details class="monopoly-info-panel monopoly-history" ${open ? "open" : ""}><summary><span>Recent actions</span><b>${log.length}</b></summary><ol>${recent.map((entry) => `<li>${escapeHtml(entry)}</li>`).join("") || "<li>The first deal is about to happen.</li>"}</ol></details>`;
}

function monopolyRulesHtml(open = false) {
  return `<details class="monopoly-info-panel monopoly-rulebook" ${open ? "open" : ""}><summary><span>Quick rules</span><b>?</b></summary><div><section><strong>Win</strong><p>Build three complete property sets in different colors.</p></section><section><strong>Your turn</strong><p>Draw two cards, then play up to three cards. Draw five instead if your hand starts empty.</p></section><section><strong>Play cards</strong><p>Bank money or Action cards, lay Properties and Wilds, collect Rent, or use an Action.</p></section><section><strong>Pay debts</strong><p>Pay only from your table using bank cards, properties, or buildings. There is no change, and hand cards stay safe.</p></section><section><strong>Wilds and buildings</strong><p>Move your Wilds on your turn. Houses require a complete street set; Hotels require a House. Railroads and Utilities cannot be improved.</p></section><section><strong>Just Say No</strong><p>Cancel an Action played against you. The attacker may answer with another Just Say No.</p></section><section><strong>Hand limit</strong><p>End your turn with no more than seven cards. Extra cards must be discarded.</p></section></div></details>`;
}

function monopolyRecapHtml(game: MonopolyView) {
  const stat = (playerId: string) => game.stats[playerId] || { rentCollected: 0, biggestRent: 0, propertiesStolen: 0, completeSetsStolen: 0 };
  const rentLeaderId = [...game.playerOrder].sort((left, right) => stat(right).biggestRent - stat(left).biggestRent)[0];
  const stealLeaderId = [...game.playerOrder].sort((left, right) => stat(right).propertiesStolen - stat(left).propertiesStolen)[0];
  const playerName = (playerId: string) => room!.players.find((player) => player.id === playerId)?.name || "Player";
  const cards = game.playerOrder.map((playerId) => {
    const board = game.boards[playerId] || { bank: [], properties: [], improvements: [], completeColors: [] };
    const bankTotal = board.bank.reduce((total, card) => total + card.value, 0);
    const playerStats = stat(playerId);
    return `<article class="monopoly-recap-player ${playerId === game.winnerId ? "is-winner" : ""}"><header><span class="avatar avatar--${Math.max(0, room!.players.findIndex((player) => player.id === playerId)) % 5}">${escapeHtml(initials(playerName(playerId)))}</span><div><strong>${escapeHtml(playerName(playerId))}</strong><small>${playerId === game.winnerId ? "WINNER" : `${board.completeColors.length} complete set${board.completeColors.length === 1 ? "" : "s"}`}</small></div></header><div class="monopoly-recap-sets">${board.completeColors.map((color) => `<span class="monopoly-recap-color monopoly-recap-color--${color}">${escapeHtml(monopolyColorLabels[color])}</span>`).join("") || "<small>No complete sets</small>"}</div><dl><div><dt>Bank</dt><dd>${monopolyMoney(bankTotal)}</dd></div><div><dt>Properties</dt><dd>${board.properties.length}</dd></div><div><dt>Rent collected</dt><dd>${monopolyMoney(playerStats.rentCollected)}</dd></div><div><dt>Properties stolen</dt><dd>${playerStats.propertiesStolen}</dd></div></dl></article>`;
  }).join("");
  const rentHighlight = rentLeaderId && stat(rentLeaderId).biggestRent > 0 ? `<div><span>BIGGEST RENT</span><strong>${monopolyMoney(stat(rentLeaderId).biggestRent)}</strong><small>${escapeHtml(playerName(rentLeaderId))}</small></div>` : `<div><span>BIGGEST RENT</span><strong>—</strong><small>No rent collected</small></div>`;
  const stealHighlight = stealLeaderId && stat(stealLeaderId).propertiesStolen > 0 ? `<div><span>BIGGEST STEAL</span><strong>${stat(stealLeaderId).propertiesStolen}</strong><small>${escapeHtml(playerName(stealLeaderId))}${stat(stealLeaderId).completeSetsStolen ? ` · ${stat(stealLeaderId).completeSetsStolen} complete set` : ""}</small></div>` : `<div><span>BIGGEST STEAL</span><strong>—</strong><small>No properties stolen</small></div>`;
  return `<section class="monopoly-recap"><div class="monopoly-recap-highlights">${rentHighlight}${stealHighlight}</div><div class="monopoly-recap-grid">${cards}</div>${monopolyHistoryHtml(game.log, false)}</section>`;
}

function monopolyBoardHtml(playerId: string, game: MonopolyView, interactive = false) {
  const player = room!.players.find((candidate) => candidate.id === playerId);
  const board = game.boards[playerId] || { bank: [], properties: [], improvements: [], completeColors: [] };
  const bankTotal = board.bank.reduce((total, card) => total + card.value, 0);
  const groups = game.colors.filter((color) => board.properties.some((propertyCard) => propertyCard.color === color)).map((color) => {
    const properties = board.properties.filter((propertyCard) => propertyCard.color === color);
    const upgrades = board.improvements.filter((improvement) => improvement.color === color);
    const complete = board.completeColors.includes(color);
    return `<section class="monopoly-set monopoly-set--${color} ${complete ? "is-complete" : ""}"><header><span>${escapeHtml(monopolyColorLabels[color])}</span><strong>${properties.length}/${monopolySetSizes[color]}</strong>${complete ? "<em>COMPLETE</em>" : ""}</header><div>${properties.map((propertyCard) => `<div class="monopoly-property-chip"><span>${escapeHtml(propertyCard.card.name)}</span><small>${monopolyMoney(propertyCard.card.value)}</small>${interactive && playerId === myId && propertyCard.card.kind === "wild" && game.canAct ? `<button data-move-wild="${propertyCard.cardId}" type="button">move</button>` : ""}</div>`).join("")}${upgrades.map((upgrade) => `<div class="monopoly-upgrade">${upgrade.card.action === "house" ? "⌂ HOUSE +$3M" : "▥ HOTEL +$4M"}</div>`).join("")}</div></section>`;
  }).join("");
  return `<article class="monopoly-board ${playerId === game.currentPlayerId ? "is-current" : ""} ${playerId === myId ? "is-mine" : ""}"><header><span class="avatar avatar--${Math.max(0, room!.players.findIndex((candidate) => candidate.id === playerId)) % 5}">${escapeHtml(initials(player?.name || "?"))}</span><div><strong>${escapeHtml(player?.name || "Player")}${playerId === myId ? " · YOU" : ""}</strong><small>${board.completeColors.length} / 3 complete sets</small></div><div class="monopoly-bank"><b>${monopolyMoney(bankTotal)}</b><span>${board.bank.length} bank cards</span></div></header><div class="monopoly-sets">${groups || "<p class=\"monopoly-empty\">No properties yet</p>"}</div></article>`;
}

function renderDisplayMonopolyDeal() {
  const game = room!.monopolyDeal!;
  displayShell(`<section class="display-monopoly"><header><div><span>TURN</span><strong>${escapeHtml(game.currentPlayerName)}</strong></div><div><span>PLAYS</span><strong>${game.playsThisTurn} / 3</strong></div><div><span>DRAW PILE</span><strong>${game.deckRemaining}</strong></div></header><p>${escapeHtml(game.lastAction || "The deal is underway.")}</p><div class="display-monopoly__boards">${game.playerOrder.map((playerId) => monopolyBoardHtml(playerId, game)).join("")}</div></section>`, "shared-display--monopoly");
}

function renderDisplayGameStatus() {
  const current = room!;
  let content = "";
  if ((current.gameType === "trivia" || current.gameType === "closest") && current.question) {
    const answer = current.phase === "result" ? current.result?.correctIndex : undefined;
    content = `<section class="display-question ${musicTriviaCategories.has(current.category) ? "display-question--eighties" : ""}"><p>${musicTriviaCategories.has(current.category) ? "♫ NAME THAT ARTIST" : escapeHtml(current.categoryLabel)} · ${current.questionNumber} OF ${current.totalQuestions}</p><h1>${escapeHtml(current.question.prompt)}</h1>${current.question.choices.length ? `<ol>${current.question.choices.map((choice, index) => `<li class="${answer === index ? "is-answer" : ""}"><span>${String.fromCharCode(65 + index)}</span><strong>${escapeHtml(choice)}</strong></li>`).join("")}</ol>` : ""}${current.phase === "result" ? `<aside><strong>${escapeHtml(current.result?.playerName || "Round complete")}</strong><span>${escapeHtml(current.question.reveal || "The answer is in.")}</span></aside>` : `<aside><strong>${current.attempts.length} answer${current.attempts.length === 1 ? "" : "s"} locked</strong><span>Players answer on their phones.</span></aside>`}</section>`;
  } else if (current.gameType === "word_match" && current.wordMatch) {
    const game = current.wordMatch;
    content = `<section class="display-feature-card display-feature-card--match"><p>ROUND ${game.roundNumber} · ${escapeHtml(game.judgeName)} IS JUDGING</p><span>THE PROMPT</span><h1>${escapeHtml(game.greenCard || "Get ready")}</h1><strong>${game.submittedCount} of ${game.eligibleCount} cards submitted</strong>${game.winner ? `<aside>${escapeHtml(game.winner.playerName || "The winner")} played <b>${escapeHtml(game.winner.card)}</b></aside>` : ""}</section>`;
  } else if (current.gameType === "impostor" && current.impostor) {
    const game = current.impostor;
    content = `<section class="display-feature-card"><p>ROUND ${game.roundNumber} · ${current.phase === "voting" ? "VOTING" : `${escapeHtml(game.currentCluePlayerName)} IS UP`}</p><h1>${current.phase === "voting" ? `${game.voteCount} of ${game.eligibleCount} votes locked` : "One word at a time."}</h1><ol class="display-clue-list">${game.clues.map((clue) => `<li><span>${escapeHtml(clue.playerName)}</span><strong>${clue.verbal ? "Said aloud" : escapeHtml(clue.clue)}</strong></li>`).join("") || "<li>Waiting for the first clue…</li>"}</ol></section>`;
  } else if (current.gameType === "liars_dice" && current.liarsDice) {
    const game = current.liarsDice;
    content = `<section class="display-feature-card"><p>ROUND ${game.roundNumber} · ${game.totalDice} DICE IN PLAY</p><h1>${game.currentBid ? `${game.currentBid.quantity} × ${game.currentBid.face}` : "Opening bid"}</h1><strong>${game.currentBid ? `${escapeHtml(game.currentBid.playerName)} holds the current bid` : `${escapeHtml(game.currentPlayerName)} starts`}</strong><div class="display-player-stats">${game.eligiblePlayerIds.map((id) => `<span>${escapeHtml(current.players.find((player) => player.id === id)?.name || "Player")}<b>${game.diceCounts[id] || 0} dice</b></span>`).join("")}</div></section>`;
  } else if (current.gameType === "taboo" && current.taboo) {
    const game = current.taboo;
    content = `<section class="display-feature-card"><p>ROUND ${game.roundNumber} · ${escapeHtml(tabooTeamName(game.activeTeam))}</p><h1>${escapeHtml(game.clueGiverName || "Next clue giver")} is clueing</h1><div class="display-team-scores"><span>${escapeHtml(tabooTeamName("a"))}<b>${game.teamScores.a}</b></span><span>${escapeHtml(tabooTeamName("b"))}<b>${game.teamScores.b}</b></span></div><strong>${game.correctThisTurn} correct · ${game.skippedThisTurn} skipped · ${game.taboosThisTurn} taboo</strong></section>`;
  } else if (current.gameType === "holdem" && current.poker) {
    const game = current.poker;
    const handStatus = current.phase === "poker_result" && game.result
      ? game.result.awards.map((award) => `${escapeHtml(award.playerName)} wins ${money(award.amount)}`).join(" · ")
      : `${escapeHtml(game.currentPlayerName || "Table")} to act`;
    content = `<section class="display-table-status"><p>HAND #${game.handNumber} · ${escapeHtml(game.street.toUpperCase())}</p><h1>${money(game.pot)} pot</h1><div class="display-card-row">${game.board.length ? game.board.map((card) => pokerCardHtml(card)).join("") : "<span class=\"display-empty-cards\">Waiting for the flop</span>"}</div><strong>${handStatus}</strong>${current.phase === "poker_result" ? `<p>${pokerNextHandStatusHtml(current)}</p>` : ""}<div class="display-player-stats">${game.seatOrder.map((id) => `<span>${escapeHtml(current.players.find((player) => player.id === id)?.name || "Player")}<b>${money(game.stacks[id] || 0)}</b></span>`).join("")}</div></section>`;
  } else if (current.gameType === "blackjack" && current.blackjack) {
    const game = current.blackjack;
    content = `<section class="display-table-status"><p>BLACKJACK · ROUND ${game.roundNumber}</p><h1>Dealer ${game.dealerTotal || ""}</h1><div class="display-card-row">${game.dealerHand.map((card) => card ? pokerCardHtml(card) : pokerCardHtml(undefined, true)).join("")}</div><strong>${current.phase === "blackjack_betting" ? `${game.bettingCount} of ${game.eligibleCount} bets placed` : `${escapeHtml(game.currentPlayerName || "Dealer")} to act`}</strong><div class="display-player-stats">${game.playerOrder.map((id) => `<span>${escapeHtml(current.players.find((player) => player.id === id)?.name || "Player")}<b>${money(game.stacks[id] || 0)}</b></span>`).join("")}</div></section>`;
  } else {
    content = `<section class="display-feature-card"><p>LIVE ROOM</p><h1>${escapeHtml(displayGameTitle(current.gameType))}</h1><strong>The game continues on every player’s phone.</strong></section>`;
  }
  displayShell(`${content}<aside class="display-board-note"><span>This screen is read-only.</span><strong>All player controls remain on their phones.</strong></aside>`, `shared-display--${current.gameType}`);
  if (current.deadline) startTimer();
}

function renderDisplayFinished() {
  const current = room!;
  if (current.gameType === "stratego" && current.stratego) {
    displayShell(strategoFinalHtml(current.stratego, current.players), "shared-display--stratego shared-display--finished", "app-shell--stratego-game");
    return;
  }
  if (current.gameType === "connect_four" && current.connectFour) {
    displayShell(connectFourFinalHtml(current.connectFour, current.players), "shared-display--connect-four shared-display--finished");
    return;
  }
  if (current.gameType === "puzzle_fighter" && current.puzzleFighter) {
    displayShell(puzzleFighterFinalHtml(current.puzzleFighter, current.players), "shared-display--puzzle-fighter shared-display--finished", "app-shell--puzzle-fighter");
    return;
  }
  if (current.gameType === "monopoly_deal" && current.monopolyDeal) {
    const winnerName = current.players.find((player) => player.id === current.monopolyDeal?.winnerId)?.name || "A player";
    displayShell(`<section class="display-monopoly-finished"><header><span>MONOPOLY DEAL COMPLETE</span><h1>${escapeHtml(winnerName)} owns the town!</h1><p>Three complete property sets closed the deal.</p></header>${monopolyRecapHtml(current.monopolyDeal)}</section>`, "shared-display--monopoly-finished");
    return;
  }
  if (current.gameType === "family_feud" && current.familyFeud) {
    const game = current.familyFeud;
    const title = game.winnerTeam ? `${familyFeudTeamName(game.winnerTeam)} wins!` : "The feud ends in a tie!";
    displayShell(`<section class="display-finished display-feud-finished"><div aria-hidden="true">★</div><p>FAMILY FEUD COMPLETE</p><h1>${escapeHtml(title)}</h1><section class="feud-final-scores">${(["a", "b"] as FamilyFeudTeam[]).map((team) => `<div class="feud-final-team feud-final-team--${team} ${game.winnerTeam === team ? "is-winner" : ""}"><span>${familyFeudTeamName(team)}</span><strong>${game.teamScores[team].toLocaleString()}</strong><small>${current.players.filter((player) => game.teams[player.id] === team).map((player) => escapeHtml(player.name)).join(" · ") || "No players"}</small></div>`).join("")}</section><small>Waiting for the admin to start the next game…</small></section>`, "shared-display--finished shared-display--feud-finished");
    return;
  }
  const hexchainWinners = current.gameType === "hexchain" ? current.hexchain?.winnerIds || [] : [];
  const standings = [...current.players].sort((first, second) => current.gameType === "hexchain"
    ? Number(hexchainWinners.includes(second.id)) - Number(hexchainWinners.includes(first.id)) || second.score - first.score
    : current.gameType === "hearts" ? first.score - second.score : second.score - first.score);
  let title = "Game complete";
  if (current.gameType === "sequence" && current.sequence?.winnerTeam) title = `${sequenceTeamName(current.sequence.winnerTeam)} wins!`;
  else if (current.gameType === "codenames" && current.codeNames?.winnerTeam) title = `${codeNamesTeamName(current.codeNames.winnerTeam)} wins!`;
  else if (current.gameType === "spades" && current.spades?.winnerTeam) title = `${current.spades.winnerTeam === "blue" ? "Blue" : "Gold"} team wins!`;
  else if (current.gameType === "hexchain" && hexchainWinners.length) title = `${hexchainWinners.map((id) => current.players.find((player) => player.id === id)?.name).filter(Boolean).join(" & ")} survived the siege!`;
  else if (standings[0]) title = `${standings[0].name} wins!`;
  displayShell(`<section class="display-finished"><div aria-hidden="true">★</div><p>${escapeHtml(displayGameTitle(current.gameType)).toUpperCase()} COMPLETE</p><h1>${escapeHtml(title)}</h1><ol>${standings.map((player, index) => `<li><span>${index + 1}</span><strong>${escapeHtml(player.name)}</strong><b>${current.gameType === "holdem" || current.gameType === "blackjack" ? money(player.score) : player.score}</b></li>`).join("")}</ol><small>Waiting for a player to choose the next game…</small></section>`, "shared-display--finished");
}

function renderPartyMenu() {
  turboRallyGame?.destroy();
  turboRallyGame = null;
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(`
    <section class="party-roster glass-card">
      <div><strong>${current.players.filter((player) => player.connected).length}</strong><span>players connected</span></div>
      <ul>${current.players.map((player, index) => `<li class="${player.connected ? "" : "player--away"}"><span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><b>${escapeHtml(player.name)}</b>${player.isAdmin ? "<small>ADMIN</small>" : ""}</li>`).join("")}</ul>
    </section>
    ${gameMenuHtml("switch")}
    <div class="party-menu-footer">
      <p>Choosing a game moves every connected player into its lobby.</p>
      <button id="leave" class="text-button">Leave room</button>
    </div>
  `, false, "app-shell--game-picker");
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-switch-game]").forEach((button) => button.addEventListener("click", () => {
    button.disabled = true;
    button.classList.add("is-busy");
    send({ type: "switch_game", gameType: button.dataset.switchGame as GameType });
  }));
  document.querySelector("#leave")!.addEventListener("click", leaveRoom);
}

function playerList(players = room!.players) {
  return players.map((player, index) => `
    <li class="player ${player.id === myId ? "player--me" : ""} ${!player.connected ? "player--away" : ""}">
      <span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span>
      <span class="player__name">${escapeHtml(player.name)}${player.isAdmin ? " <small class=\"admin-badge\">ADMIN</small>" : ""}${player.id === myId ? " <small>YOU</small>" : ""}</span>
      <strong>${player.score}</strong>
    </li>`).join("");
}

function renderHexchainLobby() {
  const context = hexchainContext();
  app.innerHTML = pageShell(hexchainLobbyHtml(context));
  bindCommon();
  bindHexchainLobby(context, leaveRoom);
}

function renderLobby() {
  turboRallyGame?.destroy();
  turboRallyGame = null;
  if (room!.gameType === "word_match") return renderWordLobby();
  if (room!.gameType === "impostor") return renderImpostorLobby();
  if (room!.gameType === "closest") return renderClosestLobby();
  if (room!.gameType === "liars_dice") return renderLiarsDiceLobby();
  if (room!.gameType === "taboo") return renderTabooLobby();
  if (room!.gameType === "codenames") return renderCodeNamesLobby();
  if (room!.gameType === "sequence") return renderSequenceLobby();
  if (room!.gameType === "holdem") return renderPokerLobby();
  if (room!.gameType === "blackjack") return renderBlackjackLobby();
  if (room!.gameType === "hearts") return renderHeartsLobby();
  if (room!.gameType === "spades") return renderSpadesLobby();
  if (room!.gameType === "uno") return renderUnoLobby();
  if (room!.gameType === "monopoly_deal") return renderMonopolyDealLobby();
  if (["king_of_tokyo", KING_COURT_GAME_TYPE].includes(room!.gameType)) return renderKingTokyoLobby();
  if (room!.gameType === "turbo_rally") return renderKartLobby();
  if (room!.gameType === "family_feud") return renderFamilyFeudLobby();
  if (room!.gameType === "hexchain" && room!.hexchain) return renderHexchainLobby();
  if (room!.gameType === "connect_four" && room!.connectFour) return renderConnectFourLobby();
  if (room!.gameType === "puzzle_fighter" && room!.puzzleFighter) return renderPuzzleFighterLobby();
  if (room!.gameType === "ticket_to_ride" && room!.ticketToRide) return renderTicketToRideLobby();
  if (room!.gameType === "axis_allies" && room!.axisAllies) return renderAxisAlliesLobby();
  if (room!.gameType === "stratego" && room!.stratego) return renderStrategoLobby();
  if (room!.gameType === "market_wars" && room!.marketWars) return renderMarket();
  if (room!.gameType === "modern_art" && room!.modernArt) return renderModernArtLobby();
  if (room!.gameType === "catan" && room!.catan) return renderCatanLobby();
  const isAdmin = room!.isAdmin;
  const joinUrl = `${location.origin}/?room=${room!.roomCode}`;
  const whatsappUrl = whatsappShareUrl(room!.roomCode, joinUrl);
  app.innerHTML = pageShell(`
    <section class="lobby-head">
      <p class="eyebrow">ROOM CODE</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${room!.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Anyone can choose the settings, and the admin starts the game.</p>
      <a class="whatsapp-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer" aria-label="Share this room on WhatsApp">
        <span aria-hidden="true">↗</span> Share on WhatsApp
      </a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card">
        <canvas id="qr-code" aria-label="QR code to join this room"></canvas>
        <strong>Scan to join this room</strong>
        <span>Open the camera on another phone and point it here.</span>
      </div>
      <div class="glass-card roster-card">
        <div class="section-title"><h2>Players</h2><span>${room!.players.filter((player) => player.connected).length} ready</span></div>
        <ul class="players">${playerList()}</ul>
      </div>
    </section>
    <section class="category-section">
      <div class="section-title"><h2>Choose a category</h2><span>Anyone can choose</span></div>
      <div class="category-grid">
        ${categories.map((category) => `<button class="category-card ${room!.category === category.id ? "category-card--active" : ""}" data-category="${category.id}">
          <i aria-hidden="true">${category.icon}</i><span><strong>${category.label}</strong><small>${category.description}</small></span><b aria-hidden="true">${room!.category === category.id ? "✓" : ""}</b>
        </button>`).join("")}
      </div>
      ${musicTriviaCategories.has(room!.category) ? `<aside class="eighties-lobby-card" aria-label="${escapeHtml(room!.categoryLabel)} game rules">
        <div class="eighties-cassette" aria-hidden="true"><i></i><i></i><b>${escapeHtml(room!.categoryLabel.replace(" Music", ""))}</b></div>
        <div><span>NOW IN THE MIX</span><strong>Name that artist</strong><p>We show the song title. You pick the performer before anyone else. Sixty tracks from ${escapeHtml(room!.categoryLabel)} span chart favorites, bands, dance hits, and one-hit wonders.</p></div>
      </aside>` : ""}
      <div class="question-count-section">
        <div class="section-title"><h2>Number of questions</h2><span>10 by default</span></div>
        <div class="question-count-grid" role="group" aria-label="Number of questions">
          ${questionCountPresets.map((count) => `<button class="count-button ${room!.totalQuestions === count ? "count-button--active" : ""}" data-question-count="${count}">${count}</button>`).join("")}
          <form class="custom-count ${questionCountPresets.includes(room!.totalQuestions) ? "" : "custom-count--active"}" id="custom-count-form">
            <input id="custom-question-count" type="number" min="1" max="${room!.maxQuestions}" step="1" inputmode="numeric" aria-label="Custom number of questions" placeholder="Custom" value="${questionCountPresets.includes(room!.totalQuestions) ? "" : room!.totalQuestions}">
            <button type="submit">Set</button>
          </form>
        </div>
        <p class="count-hint">Choose 1–${room!.maxQuestions} questions for ${escapeHtml(room!.categoryLabel)}.</p>
      </div>
    </section>
    ${room!.category === "rotten_tomatoes" ? `<section class="mode-section">
      <div class="section-title"><h2>Choose how to play</h2><span>Closest Wins is the default</span></div>
      <div class="mode-grid" role="group" aria-label="Rotten Tomatoes game mode">
        <button class="mode-card ${room!.gameMode === "closest" ? "mode-card--active" : ""}" data-game-mode="closest">
          <i aria-hidden="true">◎</i><span><strong>Closest Wins</strong><small>Enter a score from 0–100. Nearest guess gets the point.</small></span><b>${room!.gameMode === "closest" ? "✓" : ""}</b>
        </button>
        <button class="mode-card ${room!.gameMode === "multiple_choice" ? "mode-card--active" : ""}" data-game-mode="multiple_choice">
          <i aria-hidden="true">A</i><span><strong>Multiple Choice</strong><small>Pick the correct score range before anyone else.</small></span><b>${room!.gameMode === "multiple_choice" ? "✓" : ""}</b>
        </button>
      </div>
    </section>` : ""}
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large" ${room!.players.filter((player) => player.connected).length < 1 || !isAdmin ? "disabled" : ""}>
        <span>Start ${room!.gameMode === "closest" ? "Closest Wins" : escapeHtml(room!.categoryLabel)}</span><b aria-hidden="true">ϟ</b>
      </button>
      <p>${room!.gameMode === "closest" ? `${room!.totalQuestions} scores · no timer · waits for everyone · ties score too` : `${room!.totalQuestions} questions · 15 seconds each · first correct answer wins`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, {
    width: 210, margin: 1, color: { dark: "#171236", light: "#fff9ec" }, errorCorrectionLevel: "M",
  });
  document.querySelector("#copy-code")!.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(room!.roomCode);
    showToast("Room code copied!");
    tap();
  });
  document.querySelector("#start-game")!.addEventListener("click", () => {
    unlockAudio();
    send({ type: "start" });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-category]").forEach((button) => {
    button.addEventListener("click", () => {
      tap();
      send({ type: "category", category: button.dataset.category });
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-game-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      tap();
      send({ type: "game_mode", gameMode: button.dataset.gameMode });
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-question-count]").forEach((button) => {
    button.addEventListener("click", () => {
      tap();
      send({ type: "question_count", questionCount: Number(button.dataset.questionCount) });
    });
  });
  document.querySelector<HTMLFormElement>("#custom-count-form")!.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLInputElement>("#custom-question-count")!;
    const count = Number(input.value);
    if (!Number.isInteger(count) || count < 1 || count > room!.maxQuestions) {
      input.focus();
      return showToast(`Choose a whole number from 1 to ${room!.maxQuestions}.`);
    }
    tap();
    send({ type: "question_count", questionCount: count });
  });
}

function familyFeudTeamName(team: FamilyFeudTeam) {
  return team === "a" ? "Blue Team" : "Gold Team";
}

function familyFeudTeamPlayers(team: FamilyFeudTeam) {
  const game = room!.familyFeud!;
  return room!.players.filter((player) => player.connected && game.teams[player.id] === team);
}

function renderFamilyFeudLobby() {
  const current = room!;
  const game = current.familyFeud!;
  const isAdmin = current.isAdmin;
  const connected = current.players.filter((player) => player.connected);
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const teamCard = (team: FamilyFeudTeam) => {
    const members = familyFeudTeamPlayers(team);
    return `<section class="feud-lobby-team feud-lobby-team--${team}">
      <header><span>${team === "a" ? "BLUE" : "GOLD"}</span><strong>${familyFeudTeamName(team)}</strong><b>${members.length}</b></header>
      <ul>${members.map((player) => `<li><span class="avatar">${escapeHtml(initials(player.name))}</span><strong>${escapeHtml(player.name)}</strong></li>`).join("") || "<li class=\"is-empty\">Players can be assigned below.</li>"}</ul>
    </section>`;
  };
  app.innerHTML = pageShell(`
    <section class="feud-lobby-hero">
      <div><p class="eyebrow">100 SURVEY BOARDS</p><h1>Survey says…<br><span>bring your best guesses.</span></h1><p>Two teams call out the most popular answers. The admin runs the board, records strikes, awards the bank, and can skip any round the group already knows.</p></div>
      <aside class="feud-room-card"><span>ROOM CODE</span><button id="copy-code">${escapeHtml(current.roomCode)}</button><canvas id="qr-code" aria-label="QR code to join this room"></canvas><small>Scan to join</small></aside>
    </section>
    <section class="feud-source-note"><span aria-hidden="true">✓</span><p><strong>Real survey data, locally bundled.</strong> 51 boards come from roughly 100-person UMass crowd surveys; 49 are archived game-show survey boards.</p><a href="https://github.com/iesl/protoqa-data" target="_blank" rel="noopener noreferrer">ProtoQA · CC BY 4.0 ↗</a></section>
    <section class="feud-team-setup">
      ${teamCard("a")}${teamCard("b")}
      <div class="feud-player-assignments">
        <div class="section-title"><h2>Team assignments</h2><span>Anyone can set teams</span></div>
        ${connected.map((player) => `<div class="feud-assignment-row"><span class="avatar">${escapeHtml(initials(player.name))}</span><strong>${escapeHtml(player.name)}${player.id === myId ? " <small>YOU</small>" : ""}</strong><div>
          <button class="${game.teams[player.id] === "a" ? "is-selected" : ""}" data-feud-player="${escapeHtml(player.id)}" data-feud-team="a">Blue</button>
          <button class="${game.teams[player.id] === "b" ? "is-selected" : ""}" data-feud-player="${escapeHtml(player.id)}" data-feud-team="b">Gold</button>
        </div></div>`).join("")}
      </div>
    </section>
    <section class="feud-round-settings">
      <div><span>GAME LENGTH</span><strong>How many survey rounds?</strong><small>Round 3 scores double; round 5 and later score triple.</small></div>
      <div role="group" aria-label="Number of Family Feud rounds">${[3, 5, 7, 10].map((count) => `<button class="${current.totalQuestions === count ? "is-selected" : ""}" data-feud-count="${count}">${count}<small>rounds</small></button>`).join("")}</div>
    </section>
    <div class="sticky-action feud-start-action"><button id="start-game" class="primary-button primary-button--large" ${!isAdmin || connected.length < 2 ? "disabled" : ""}><span>${isAdmin ? connected.length < 2 ? "Waiting for 2 players" : "Start the feud" : "Waiting for the admin"}</span><b>→</b></button><p>${current.totalQuestions} rounds selected · 100 unique boards in the library</p></div>
  `);
  bindCommon();
  void QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 150, margin: 1, color: { dark: "#082c69", light: "#fff" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")?.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  document.querySelector("#start-game")?.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-feud-player]").forEach((button) => button.addEventListener("click", () => {
    tap(); send({ type: "feud_team_assign", playerId: button.dataset.feudPlayer, team: button.dataset.feudTeam });
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-feud-count]").forEach((button) => button.addEventListener("click", () => {
    tap(); send({ type: "question_count", questionCount: Number(button.dataset.feudCount) });
  }));
}

function renderFamilyFeud(asDisplay = false) {
  cleanupTimer();
  cleanupHexchain();
  const current = room!;
  const game = current.familyFeud!;
  const completed = current.phase === "feud_round_result";
  const myTeam = game.teams[myId];
  const canJudge = !asDisplay && game.canJudge && !completed;
  const activeTeamName = familyFeudTeamName(game.activeTeam);
  const judgingTeamName = familyFeudTeamName(game.activeTeam === "a" ? "b" : "a");
  const teamScore = (team: FamilyFeudTeam) => `<section class="feud-score feud-score--${team} ${game.activeTeam === team && !completed ? "is-active" : ""} ${game.roundAwardedTo === team ? "is-awarded" : ""}">
    <span>${familyFeudTeamName(team)}</span><strong>${game.teamScores[team].toLocaleString()}</strong><small>${room!.players.filter((player) => player.connected && game.teams[player.id] === team).map((player) => escapeHtml(player.name)).join(" · ") || "No players"}</small>
  </section>`;
  const roleBanner = completed ? "" : asDisplay
    ? `<section class="feud-role-banner feud-role-banner--display"><div aria-hidden="true">◆</div><p><span>NOW GUESSING</span><strong>${activeTeamName} is guessing</strong><small>Call out one answer at a time.</small></p></section>`
    : canJudge
      ? `<section class="feud-role-banner feud-role-banner--judge"><div aria-hidden="true">✓</div><p><span>YOUR ROLE THIS ROUND</span><strong>You’re checking ${activeTeamName}’s guess</strong><small>Tap their matching answer below to reveal it. If it isn’t on the board, record a strike.</small></p></section>`
      : myTeam === game.activeTeam
        ? `<section class="feud-role-banner feud-role-banner--guess"><div aria-hidden="true">!</div><p><span>YOUR TURN</span><strong>Your team is guessing</strong><small>Call out one answer. ${judgingTeamName} will check it against the survey.</small></p></section>`
        : `<section class="feud-role-banner feud-role-banner--display"><div aria-hidden="true">◆</div><p><span>NOW GUESSING</span><strong>${activeTeamName} is guessing</strong><small>Watch the round while the other team checks each answer.</small></p></section>`;
  const board = game.answers.map((answer, index) => {
    const revealed = answer.revealed;
    const missed = completed && !revealed;
    const judgePreview = canJudge && !revealed;
    const inside = revealed || missed
      ? `<span>${index + 1}</span><strong>${escapeHtml(answer.label)}</strong><b>${answer.points ?? "—"}</b>`
      : judgePreview
        ? `<span>${index + 1}</span><strong>${escapeHtml(answer.label)}</strong><em>tap to reveal · ${answer.points}</em>`
        : `<span>${index + 1}</span><strong>SURVEY SAYS</strong><i>?</i>`;
    return canJudge
      ? `<button class="feud-answer ${revealed ? "is-revealed" : ""} ${judgePreview ? "is-judge-preview" : ""}" data-feud-answer="${index}" aria-label="${revealed ? "Hide" : "Reveal"} answer ${index + 1}">${inside}</button>`
      : `<div class="feud-answer ${revealed ? "is-revealed" : ""} ${missed ? "is-missed" : ""}">${inside}</div>`;
  }).join("");
  const strikes = `<div class="feud-strikes" aria-label="${game.strikes} strikes">${[0, 1, 2].map((index) => `<b class="${index < game.strikes ? "is-on" : ""}">×</b>`).join("")}</div>`;
  const sourceLabel = game.sourceKind === "crowdsourced" ? "≈100-person crowd survey" : "Archived game-show survey";
  const sourceCitation = canJudge || completed
    ? `<a href="${escapeHtml(game.sourceUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(game.sourceTitle)} ↗</a>`
    : `<small>Source details unlock when the round ends</small>`;
  const judgeControls = canJudge ? `<section class="feud-judge-panel">
    <div><span>CHECK THEIR GUESS</span><strong>Did their answer miss the board?</strong><small>For a match, tap the correct answer above. Otherwise, record a strike.</small></div>
    <div><button class="feud-judge-strike" data-feud-strike="add" ${game.strikes >= 3 ? "disabled" : ""}>Record a strike <b>×</b></button><button class="feud-judge-undo" data-feud-strike="remove" ${game.strikes <= 0 ? "disabled" : ""}>Undo last strike</button></div>
  </section>` : "";
  const hostControls = current.isAdmin && !completed ? `<section class="feud-host-panel">
    <header><div><span>ROUND ADMIN</span><strong>Award points or change control</strong></div><button class="feud-skip" id="feud-skip-round">Skip familiar round <b>↷</b></button></header>
    <div class="feud-host-row"><span>Team in control</span>${(["a", "b"] as FamilyFeudTeam[]).map((team) => `<button class="feud-team-button feud-team-button--${team} ${game.activeTeam === team ? "is-selected" : ""}" data-feud-active="${team}">${familyFeudTeamName(team)}</button>`).join("")}</div>
    <div class="feud-award-row"><span>Award the ${game.roundBank.toLocaleString()}-point bank</span>${(["a", "b"] as FamilyFeudTeam[]).map((team) => `<button class="feud-award feud-award--${team}" data-feud-award="${team}">Give to ${familyFeudTeamName(team)}</button>`).join("")}</div>
  </section>` : completed ? `<section class="feud-round-result"><span>ROUND ${game.roundNumber} COMPLETE</span><h2>${game.roundAwardedTo ? `${familyFeudTeamName(game.roundAwardedTo)} wins ${game.roundBank.toLocaleString()} points!` : "No points awarded"}</h2><p>The complete survey board is now revealed.</p>${current.isAdmin ? `<button id="feud-next" class="primary-button"><span>${game.roundNumber >= current.totalQuestions ? "Show final score" : "Next survey"}</span><b>→</b></button>` : "<small>Waiting for the admin to continue…</small>"}</section>` : "";
  const content = `<section class="feud-game">
    ${roleBanner}
    <header class="feud-game-head">${teamScore("a")}<div class="feud-round-bank"><span>ROUND ${game.roundNumber} OF ${current.totalQuestions}</span><strong>${game.roundBank.toLocaleString()}</strong><small>POINT BANK · ${game.multiplier}×</small></div>${teamScore("b")}</header>
    <section class="feud-question"><span>${sourceLabel}</span><h1>${escapeHtml(game.prompt)}</h1>${sourceCitation}</section>
    ${strikes}
    <section class="feud-board">${board}</section>
    ${judgeControls}
    ${hostControls}
    ${game.skippedRounds ? `<p class="feud-skip-count">${game.skippedRounds} familiar round${game.skippedRounds === 1 ? "" : "s"} skipped this game</p>` : ""}
  </section>`;
  if (asDisplay) {
    displayShell(content, "shared-display--feud");
    return;
  }
  app.innerHTML = pageShell(content, true);
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-feud-answer]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "feud_reveal", answerIndex: Number(button.dataset.feudAnswer) }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-feud-strike]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "feud_strike", action: button.dataset.feudStrike === "remove" ? "remove" : "add" }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-feud-active]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "feud_active_team", team: button.dataset.feudActive }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-feud-award]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "feud_award", team: button.dataset.feudAward }); }));
  document.querySelector("#feud-next")?.addEventListener("click", () => { tap(); send({ type: "feud_next" }); });
  document.querySelector("#feud-skip-round")?.addEventListener("click", () => {
    if (!window.confirm("Skip this survey for everyone and replace it with a fresh one?")) return;
    tap(); send({ type: "feud_skip_round" });
  });
}

function renderKingTokyoLobby() {
  const current = room!;
  const view = current.kingTokyo!;
  app.innerHTML = pageShell(kingGameHtml(kingTokyoLobbyHtml(view, current.players, myId, current.isAdmin), current.gameType));
  bindCommon();
  const { saveLobbySettings } = bindKingTokyo(app, send, showToast);
  document.querySelector("#start-game")?.addEventListener("click", () => {
    if (!saveLobbySettings()) return;
    unlockAudio();
    playTone(105, .18, "sawtooth", .06);
    send({ type: "start" });
  });
}

function renderKingTokyo() {
  cleanupTimer();
  const current = room!;
  const view = current.kingTokyo!;
  const rollSoundContext = `${view.currentPlayerId || "none"}:${view.turnNumber}:${view.rollNumber}`;
  if (view.rollNumber > 0 && lastKingTokyoRollSoundContext !== rollSoundContext) {
    lastKingTokyoRollSoundContext = rollSoundContext;
    playTone(145, .07, "square", .04);
    window.setTimeout(() => playTone(92, .1, "sawtooth", .035), 65);
  }
  app.innerHTML = pageShell(kingGameHtml(kingTokyoGameHtml(view, current.players, myId, current.phase, IS_LOCAL_KING_RULES), current.gameType), true);
  bindCommon();
  bindKingTokyo(app, send, showToast);
}

function renderDisplayKingTokyo() {
  const current = room!;
  displayShell(kingGameHtml(kingTokyoDisplayHtml(current.kingTokyo!, current.players), current.gameType), isKingCourtGame(current.gameType) ? "shared-display--tokyo shared-display--court" : "shared-display--tokyo");
}

function renderHeartsLobby() {
  const current = room!;
  app.innerHTML = pageShell(heartsLobbyHtml(current.hearts!, current.players, myId, current.isAdmin));
  bindCommon();
  bindHearts(app, send, showToast);
  document.querySelector("#start-game")?.addEventListener("click", () => {
    unlockAudio();
    playTone(330, .08, "sine", .04);
    send({ type: "start" });
  });
}

function renderHearts() {
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(heartsGameHtml(current.hearts!, current.players, myId, current.phase, current.isAdmin), true);
  bindCommon();
  bindHearts(app, send, showToast);
}

function renderDisplayHearts() {
  const current = room!;
  displayShell(heartsDisplayHtml(current.hearts!, current.players, current.phase), "shared-display--hearts");
}

function renderSpadesLobby() {
  const current = room!;
  app.innerHTML = pageShell(spadesLobbyHtml(current.spades!, current.players, myId, current.isAdmin));
  bindCommon();
  bindSpades(app, send);
  document.querySelector("#start-game")?.addEventListener("click", () => {
    unlockAudio();
    playTone(196, .1, "triangle", .045);
    send({ type: "start" });
  });
}

function renderSpades() {
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(spadesGameHtml(current.spades!, current.players, current.phase, current.isAdmin), true);
  bindCommon();
  bindSpades(app, send);
}

function renderDisplaySpades() {
  const current = room!;
  displayShell(spadesDisplayHtml(current.spades!, current.players, current.phase), "shared-display--spades");
}

function renderUnoLobby() {
  const current = room!;
  app.innerHTML = pageShell(unoLobbyHtml(current.uno!, current.players, myId, current.isAdmin));
  bindCommon();
  bindUno(app, send, showToast);
  document.querySelector("#start-game")?.addEventListener("click", () => {
    unlockAudio();
    playTone(262, .08, "triangle");
    playTone(392, .11, "triangle", .08);
    send({ type: "start" });
  });
}

function renderUno() {
  cleanupTimer();
  const current = room!;
  app.innerHTML = pageShell(unoGameHtml(current.uno!, current.players, current.phase, current.isAdmin), true);
  bindCommon();
  bindUno(app, send, showToast);
}

function renderDisplayUno() {
  const current = room!;
  displayShell(unoDisplayHtml(current.uno!, current.players, current.phase), "shared-display--uno");
}

function renderKartLobby() {
  const current = room!;
  const race = current.kartRace!;
  const connected = current.players.filter((player) => player.connected);
  const myDriver = race.drivers[myId] || "nova";
  const track = KART_TRACKS.find((candidate) => candidate.id === race.trackId) || KART_TRACKS[0];
  app.innerHTML = pageShell(`
    <section class="kart-lobby-hero">
      <div><p class="eyebrow">LIVE MULTIPLAYER · NO AI RACERS</p><h1>Choose your machine.</h1><p>Every person in the lobby gets a place on the grid. Pick a driver, then race three laps with power-ups.</p></div>
      <button class="room-code kart-room-code" id="copy-code" aria-label="Copy room code">${escapeHtml(current.roomCode)}<span>copy</span></button>
    </section>
    <section class="kart-lobby-roster glass-card"><div class="section-title"><h2>Starting grid</h2><span>${connected.length} human racer${connected.length === 1 ? "" : "s"}</span></div><ul>${connected.map((player, index) => {
      const driver = KART_DRIVERS.find((candidate) => candidate.id === (race.drivers[player.id] || "nova")) || KART_DRIVERS[0];
      return `<li><b>${index + 1}</b><i style="--driver:${driver.color}">${escapeHtml(driver.name[0])}</i><span><strong>${escapeHtml(player.name)}</strong><small>${escapeHtml(driver.name)} · ${escapeHtml(driver.car)}</small></span>${player.id === myId ? "<em>YOU</em>" : ""}</li>`;
    }).join("")}</ul></section>
    <section class="kart-picker-section"><div class="section-title"><h2>Pick your driver</h2><span>Speed trades off against control</span></div><div class="kart-driver-grid">
      ${KART_DRIVERS.map((driver) => `<button class="kart-driver-card ${myDriver === driver.id ? "is-selected" : ""}" data-kart-driver="${driver.id}" type="button" style="--driver:${driver.color};--accent:${driver.accent}">
        <span class="kart-driver-avatar">${escapeHtml(driver.name[0])}<i></i></span><span class="kart-driver-copy"><small>${escapeHtml(driver.specialty)} · ${escapeHtml(driver.weight)}</small><strong>${escapeHtml(driver.name)}</strong><em>${escapeHtml(driver.car)}</em></span>
        <span class="kart-stats"><i><b style="width:${driver.speed * 100}%"></b></i><small>SPD</small><i><b style="width:${driver.accel * 100}%"></b></i><small>ACC</small><i><b style="width:${driver.handling * 100}%"></b></i><small>CTRL</small></span>
      </button>`).join("")}
    </div></section>
    <section class="kart-picker-section"><div class="section-title"><h2>Choose a circuit</h2><span>Anyone can choose</span></div><div class="kart-track-grid">
      ${KART_TRACKS.map((candidate, index) => `<button class="kart-track-card kart-track-card--${index} ${candidate.id === race.trackId ? "is-selected" : ""}" data-kart-track="${candidate.id}" type="button"  style="--sky:${candidate.sky};--ground:${candidate.ground};--edge:${candidate.edge};--accent:${candidate.accent}"><span><i></i><b>${index + 1}</b></span><strong>${escapeHtml(candidate.name)}</strong><small>${escapeHtml(candidate.kicker)}</small></button>`).join("")}
    </div></section>
    <section class="kart-powerup-strip"><div><span>⚡</span><strong>Turbo Bolt</strong><small>Raw speed</small></div><div><span>✦</span><strong>Homing Spark</strong><small>Zap the racer ahead</small></div><div><span>●</span><strong>Void Slick</strong><small>Leave a trap</small></div><div><span>◉</span><strong>Aura Shield</strong><small>Block one hit</small></div></section>
    <div class="sticky-action kart-start-action"><button id="start-game" class="primary-button primary-button--large" ${current.isAdmin && connected.length ? "" : "disabled"}><span>Race ${escapeHtml(track.name)}</span><b aria-hidden="true">🏁</b></button><p>${current.isAdmin ? `${connected.length} human racer${connected.length === 1 ? "" : "s"} · 3 laps · keyboard and touch controls` : "Waiting for the race director to start"}</p></div>
  `);
  bindCommon();
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); });
  document.querySelectorAll<HTMLButtonElement>("[data-kart-driver]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "kart_driver", driverId: button.dataset.kartDriver }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-kart-track]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "kart_track", trackId: button.dataset.kartTrack }); }));
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
}

function renderTurboRally() {
  cleanupTimer();
  if (!room?.kartRace) return renderRoomSyncing();
  turboRallyGame?.destroy();
  turboRallyGame = new TurboRallyGame(app, { ...room.kartRace, players: room.players }, myId, send);
}

function renderClosestLobby() {
  const current = room!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  app.innerHTML = pageShell(`
    <section class="lobby-head closest-lobby-head">
      <p class="eyebrow">CLOSEST WITHOUT GOING OVER</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Every guess happens privately on each phone.</p>
      <a class="whatsapp-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--closest">
        <canvas id="qr-code" aria-label="QR code to join this room"></canvas>
        <strong>Scan to join this room</strong>
        <span>No app, account, or answer sheet needed.</span>
      </div>
      <div class="glass-card roster-card">
        <div class="section-title"><h2>Players</h2><span>${connected} ready</span></div>
        <ul class="players">${playerList()}</ul>
      </div>
    </section>
    <section class="closest-rules glass-card">
      <div><b>1</b><span><strong>See a wild number</strong><small>Everybody gets the same ridiculous question.</small></span></div>
      <div><b>2</b><span><strong>Lock a private guess</strong><small>Go bold—but guesses above the answer are out.</small></span></div>
      <div><b>3</b><span><strong>Closest under wins</strong><small>Tied winning guesses each score one point.</small></span></div>
      <div class="closest-count-picker">
        <span><strong>Rounds</strong><small>200 questions are built in</small></span>
        <div>${questionCountPresets.map((count) => `<button class="count-button ${current.totalQuestions === count ? "count-button--active" : ""}" data-question-count="${count}">${count}</button>`).join("")}</div>
      </div>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large closest-primary" ${connected < 1 || !current.isAdmin ? "disabled" : ""}><span>Start the bidding</span><b aria-hidden="true">≤</b></button>
      <p>${current.isAdmin ? `${current.totalQuestions} rounds · no timer · waits for everyone · overbids score zero` : "Waiting for the group admin to start"}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, {
    width: 210, margin: 1, color: { dark: "#171236", light: "#fff9ec" }, errorCorrectionLevel: "M",
  });
  document.querySelector("#copy-code")!.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(current.roomCode);
    showToast("Room code copied!"); tap();
  });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-question-count]").forEach((button) => button.addEventListener("click", () => {
    tap(); send({ type: "question_count", questionCount: Number(button.dataset.questionCount) });
  }));
}

function renderLiarsDiceLobby() {
  const current = room!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  app.innerHTML = pageShell(`
    <section class="lobby-head dice-lobby-head">
      <p class="eyebrow">LIAR’S DICE ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Keep your dice hidden on your own phone.</p>
      <a class="whatsapp-share dice-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--dice">
        <canvas id="qr-code" aria-label="QR code to join this room"></canvas>
        <strong>Scan to join</strong><span>2+ players · five dice each</span>
      </div>
      <div class="glass-card roster-card">
        <div class="section-title"><h2>Bluffers</h2><span>${connected} ready</span></div>
        <ul class="players">${playerList()}</ul>
      </div>
    </section>
    <section class="dice-rules glass-card">
      <div><b>1</b><span><strong>Roll in secret</strong><small>Only you can see your dice.</small></span></div>
      <div><b>2</b><span><strong>Raise the bid</strong><small>Guess how many matching dice the room has.</small></span></div>
      <div><b>3</b><span><strong>Call “Liar!”</strong><small>The loser drops a die. Last player standing wins.</small></span></div>
      <p>Ones are ordinary dice—not wild. A bid is true when the room has at least that many matching dice.</p>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large dice-primary" ${connected < 2 || !current.isAdmin ? "disabled" : ""}><span>Roll the Dice</span><b aria-hidden="true">⚄</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 2 ? "At least 2 players are needed" : `${connected} players · private rolls · no wild dice`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, {
    width: 210, margin: 1, color: { dark: "#281832", light: "#fff8ed" }, errorCorrectionLevel: "M",
  });
  document.querySelector("#copy-code")!.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(current.roomCode);
    showToast("Room code copied!"); tap();
  });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}

function renderBlackjackLobby() {
  const current = room!;
  const game = current.blackjack!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  const canStart = current.isAdmin && connected >= 1 && connected <= 7;
  app.innerHTML = pageShell(`
    <section class="lobby-head blackjack-lobby-head">
      <p class="eyebrow">BLACKJACK TABLE</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Everyone plays the same dealer, one turn at a time.</p>
      <a class="whatsapp-share blackjack-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--blackjack"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to sit down</strong><span>1–7 players · play-money table</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Players</h2><span>${connected} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="blackjack-settings glass-card">
      <div><span>Starting bankroll</span><strong>Everyone begins with this amount</strong></div>
      <label class="blackjack-bankroll-setting" for="blackjack-starting-bankroll"><span>$</span><input id="blackjack-starting-bankroll" type="number" min="10" max="100000" step="10" inputmode="decimal" value="${(game.startingBankroll / 100).toFixed(0)}"></label>
      <p>The bet box starts at <strong>${money(game.defaultBet)}</strong> each hand, and every player can change their own wager before the deal.</p>
      <button id="blackjack-count-setting" class="blackjack-count-setting ${game.showCardCount ? "is-on" : ""}" type="button" aria-pressed="${game.showCardCount}"><i aria-hidden="true"></i><span><strong>Show Hi-Lo card count</strong><small>${game.showCardCount ? "On · visible to every player" : "Off · count hidden from the table"}</small></span></button>
    </section>
    <section class="blackjack-rules glass-card">
      <div><b>1</b><span><strong>Get close to 21</strong><small>Face cards are 10. Aces count as 1 or 11.</small></span></div>
      <div><b>2</b><span><strong>Beat the dealer</strong><small>The dealer stands on every 17. Ties push.</small></span></div>
      <div><b>3</b><span><strong>Blackjack pays 3:2</strong><small>A natural 21 wins one and a half times the bet. The deck changes only at 7 cards or fewer.</small></span></div>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large blackjack-primary" ${canStart ? "" : "disabled"}><span>Shuffle &amp; Deal</span><b aria-hidden="true">21</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to deal" : connected > 7 ? "Blackjack supports up to 7 players" : `${connected} player${connected === 1 ? "" : "s"} · ${money(game.startingBankroll)} each · ${money(game.defaultBet)} default bet`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#3b1920", light: "#fff7df" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  const readStartingBankroll = () => Math.round(Number(document.querySelector<HTMLInputElement>("#blackjack-starting-bankroll")!.value) * 100);
  const saveSettings = () => {
    const startingBankroll = readStartingBankroll();
    if (!Number.isSafeInteger(startingBankroll) || startingBankroll < 1_000 || startingBankroll > 10_000_000) {
      showToast("Choose a starting bankroll from $10 to $100,000.");
      return null;
    }
    send({ type: "blackjack_settings", startingBankroll });
    return startingBankroll;
  };
  document.querySelector("#start-game")!.addEventListener("click", () => { if (!saveSettings()) return; unlockAudio(); send({ type: "start" }); });
  document.querySelector<HTMLInputElement>("#blackjack-starting-bankroll")?.addEventListener("change", saveSettings);
  document.querySelector<HTMLButtonElement>("#blackjack-count-setting")?.addEventListener("click", () => send({ type: "blackjack_count_visibility", showCardCount: !game.showCardCount }));
}

function renderMonopolyDealLobby() {
  const current = room!;
  const connected = current.players.filter((player) => player.connected).length;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  app.innerHTML = pageShell(`
    <section class="lobby-head monopoly-lobby-head">
      <p class="eyebrow">MONOPOLY DEAL</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the room. Every hand stays private on its owner’s screen.</p>
      <a class="whatsapp-share monopoly-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--monopoly"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to make a deal</strong><span>2–5 players · about 15 minutes</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Deal makers</h2><span>${connected} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="monopoly-rules glass-card">
      <header><span>THE QUICK DEAL</span><strong>First to 3 complete sets wins</strong></header>
      <div><b>1</b><span><strong>Draw</strong><small>Take 2 cards—or 5 when your hand starts empty.</small></span></div>
      <div><b>2</b><span><strong>Play up to 3</strong><small>Bank money, lay properties, or fire an Action card.</small></span></div>
      <div><b>3</b><span><strong>Pay from the table</strong><small>Choose bank cards or properties. There’s never change, and your hand is safe.</small></span></div>
      <p>Wild properties may change color on your turn. Houses need a complete street set; Hotels need a House. Railroads and Utilities cannot be improved.</p>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large monopoly-primary" ${!current.isAdmin || connected < 2 || connected > 5 ? "disabled" : ""}><span>Shuffle &amp; Deal</span><b aria-hidden="true">M</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin" : connected < 2 ? "At least 2 players are needed" : connected > 5 ? "Monopoly Deal supports up to 5 players" : `${connected} players · 5 cards each · 3 sets to win`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#132d26", light: "#fff9e8" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
}

function renderPokerLobby() {
  const current = room!;
  const game = current.poker!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  const canStart = current.isAdmin && connected >= 2 && connected <= 10;
  app.innerHTML = pageShell(`
    <section class="lobby-head poker-lobby-head">
      <p class="eyebrow">TEXAS HOLD’EM ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Every player uses their own screen for private cards.</p>
      <a class="whatsapp-share poker-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--poker"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to take a seat</strong><span>2–10 players · seats shuffle at Start</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Players</h2><span>${connected} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="poker-settings glass-card">
      <div class="section-title"><h2>Table stakes</h2><span>Anyone can customize before the deal</span></div>
      <div class="poker-setting-grid">
        <label><span>Small blind</span><div><b>$</b><input id="poker-small-blind" type="number" min="0.01" step="0.01" inputmode="decimal" value="${(game.smallBlind / 100).toFixed(2)}"></div></label>
        <label><span>Big blind</span><div><b>$</b><input id="poker-big-blind" type="number" min="0.02" step="0.01" inputmode="decimal" value="${(game.bigBlind / 100).toFixed(2)}"></div></label>
        <label><span>Starting stack</span><div><b>$</b><input id="poker-starting-stack" type="number" min="0.04" step="0.01" inputmode="decimal" value="${(game.startingStack / 100).toFixed(2)}"></div></label>
      </div>
      <p><strong>Default:</strong> ${money(50)} / ${money(100)} blinds with ${money(10_000)} per player. No-limit betting; side pots are settled automatically.</p>
    </section>
    <section class="poker-rules glass-card"><div><b>1</b><span><strong>Random seats</strong><small>The whole table is shuffled once when play begins.</small></span></div><div><b>2</b><span><strong>Private hole cards</strong><small>Only your device receives your two cards.</small></span></div><div><b>3</b><span><strong>Complete history</strong><small>Every action, board, showdown, and payout is saved hand by hand.</small></span></div></section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large poker-primary" ${canStart ? "" : "disabled"}><span>Shuffle &amp; Deal</span><b aria-hidden="true">♠</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 2 ? "At least 2 players are needed" : connected > 10 ? "Hold’em supports up to 10 players" : `${connected} players · seating will be random`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#153d32", light: "#f8f2df" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  const settings = () => {
    const smallBlind = Math.round(Number(document.querySelector<HTMLInputElement>("#poker-small-blind")!.value) * 100);
    const bigBlind = Math.round(Number(document.querySelector<HTMLInputElement>("#poker-big-blind")!.value) * 100);
    const startingStack = Math.round(Number(document.querySelector<HTMLInputElement>("#poker-starting-stack")!.value) * 100);
    if (!Number.isSafeInteger(smallBlind) || !Number.isSafeInteger(bigBlind) || !Number.isSafeInteger(startingStack) || smallBlind < 1 || bigBlind <= smallBlind || startingStack < bigBlind * 2) {
      showToast("Use positive blinds, with the big blind above the small blind and at least two big blinds per stack.");
      return null;
    }
    return { smallBlind, bigBlind, startingStack };
  };
  document.querySelector("#start-game")!.addEventListener("click", () => {
    const values = settings();
    if (!values) return;
    unlockAudio(); send({ type: "poker_settings", ...values }); send({ type: "start" });
  });
  document.querySelectorAll<HTMLInputElement>(".poker-setting-grid input").forEach((input) => input.addEventListener("change", () => {
    const values = settings();
    if (values) send({ type: "poker_settings", ...values });
  }));
}

function tabooTeamName(team: TabooTeam | null) {
  return team === "a" ? "Team Coral" : team === "b" ? "Team Aqua" : "Spectator";
}

function codeNamesTeamName(team: CodeNamesTeam | null) {
  return team === "red" ? "Red Team" : team === "blue" ? "Blue Team" : "Spectator";
}

function sequenceTeamName(team: SequenceTeam | null) {
  return team === "coral" ? "Team Coral" : team === "aqua" ? "Team Aqua" : team === "gold" ? "Team Gold" : "Spectator";
}

function sequenceTurnOrderHtml(game: SequenceView, showViewer = true) {
  const seenPlayerIds = new Set<string>();
  const fullRound = game.turnOrder.filter((turn) => {
    if (seenPlayerIds.has(turn.playerId)) return false;
    seenPlayerIds.add(turn.playerId);
    return true;
  });
  for (const playerId of game.playerOrder) {
    if (seenPlayerIds.has(playerId)) continue;
    const player = room?.players.find((candidate) => candidate.id === playerId);
    const team = game.teams[playerId];
    if (!player || !team) continue;
    fullRound.push({ playerId, playerName: player.name, team });
    seenPlayerIds.add(playerId);
  }
  const turns = fullRound.map((turn, index) => `<li class="sequence-turn-order__player sequence-turn-order__player--${turn.team} ${index === 0 ? "is-current" : ""} ${showViewer && turn.playerId === myId ? "is-you" : ""}">
    <small>${index === 0 ? "NOW" : index === 1 ? "NEXT" : `THEN ${index}`}</small>
    <strong>${escapeHtml(turn.playerName)}${showViewer && turn.playerId === myId ? " · YOU" : ""}</strong>
  </li>`).join("");
  return `<section class="sequence-turn-order" aria-label="Complete Sequence turn order"><span>FULL ROUND · TURN ORDER</span><ol>${turns}</ol></section>`;
}

function renderTabooLobby() {
  const current = room!;
  const game = current.taboo!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connectedPlayers = current.players.filter((player) => player.connected);
  const connected = connectedPlayers.length;
  const teamA = connectedPlayers.filter((player) => game.teams[player.id] === "a");
  const teamB = connectedPlayers.filter((player) => game.teams[player.id] === "b");
  const manualReady = game.teamMode === "manual" && teamA.length >= 2 && teamB.length >= 2 && teamA.length + teamB.length === connected;
  const canStart = current.isAdmin && connected >= 4 && (game.teamMode === "random" || manualReady);
  const manualRoster = game.teamMode === "manual" ? `
    <section class="taboo-team-builder">
      ${(["a", "b"] as TabooTeam[]).map((team) => `<div class="taboo-team-card taboo-team-card--${team}">
        <div><span>${team === "a" ? "TEAM CORAL" : "TEAM AQUA"}</span><strong>${team === "a" ? teamA.length : teamB.length} players</strong></div>
        <ul>${connectedPlayers.map((player, index) => `<li class="${game.teams[player.id] === team ? "is-on-team" : ""}">
          <span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><b>${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}</b>
          <button data-taboo-player="${escapeHtml(player.id)}" data-taboo-team="${team}">${game.teams[player.id] === team ? "✓" : "Move"}</button>
        </li>`).join("")}</ul>
      </div>`).join("")}
    </section>` : `<section class="taboo-random-note glass-card"><span aria-hidden="true">↝</span><div><strong>Teams shuffle at Start</strong><p>Everyone is split as evenly as possible, then the first clue-givers are chosen.</p></div></section>`;
  app.innerHTML = pageShell(`
    <section class="lobby-head taboo-lobby-head">
      <p class="eyebrow">TABOO ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. You’ll describe secret words out loud.</p>
      <a class="whatsapp-share taboo-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--taboo"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to join</strong><span>4+ players · two teams · one phone each</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Players</h2><span>${connected} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="taboo-setup glass-card">
      <div class="section-title"><h2>Build the teams</h2><span>Random is the default</span></div>
      <div class="taboo-mode-switch" role="group" aria-label="Team assignment">
        <button class="${game.teamMode === "random" ? "is-active" : ""}" data-taboo-mode="random"><b>↝</b><span><strong>Random teams</strong><small>Shuffle when the game starts</small></span></button>
        <button class="${game.teamMode === "manual" ? "is-active" : ""}" data-taboo-mode="manual"><b>☷</b><span><strong>Set manually</strong><small>Choose every player’s side</small></span></button>
      </div>
      ${manualRoster}
      <div class="taboo-settings">
        <label><span>First to</span><select id="taboo-target">${[10, 15, 20, 25].map((score) => `<option value="${score}" ${game.targetScore === score ? "selected" : ""}>${score} points</option>`).join("")}</select></label>
        <label><span>Turn timer</span><select id="taboo-seconds">${[45, 60, 90].map((seconds) => `<option value="${seconds}" ${game.roundSeconds === seconds ? "selected" : ""}>${seconds} seconds</option>`).join("")}</select></label>
      </div>
    </section>
    <section class="taboo-rules glass-card"><div><b>1</b><span><strong>Describe the big word</strong><small>But don’t say any forbidden word.</small></span></div><div><b>2</b><span><strong>Teammates guess aloud</strong><small>Tap Got it for each correct answer.</small></span></div><div><b>3</b><span><strong>Opponents watch closely</strong><small>Hit Taboo to steal a point on a slip.</small></span></div></section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large taboo-primary" ${canStart ? "" : "disabled"}><span>Start Taboo</span><b aria-hidden="true">⊘</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 4 ? "At least 4 players are needed" : game.teamMode === "manual" && !manualReady ? "Put at least 2 players on each team" : `${connected} players · ${game.roundSeconds}-second turns · first to ${game.targetScore}`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#431b24", light: "#fff7ee" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-taboo-mode]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "taboo_team_mode", teamMode: button.dataset.tabooMode }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-taboo-player]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "taboo_team_assign", playerId: button.dataset.tabooPlayer, team: button.dataset.tabooTeam }); }));
  const updateSettings = () => send({ type: "taboo_settings", targetScore: Number(document.querySelector<HTMLSelectElement>("#taboo-target")?.value), roundSeconds: Number(document.querySelector<HTMLSelectElement>("#taboo-seconds")?.value) });
  document.querySelector("#taboo-target")?.addEventListener("change", updateSettings);
  document.querySelector("#taboo-seconds")?.addEventListener("change", updateSettings);
}

function renderCodeNamesLobby() {
  const current = room!;
  const game = current.codeNames!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connectedPlayers = current.players.filter((player) => player.connected);
  const red = connectedPlayers.filter((player) => game.teams[player.id] === "red");
  const blue = connectedPlayers.filter((player) => game.teams[player.id] === "blue");
  const manualReady = game.teamMode === "manual" && red.length >= 2 && blue.length >= 2 && red.length + blue.length === connectedPlayers.length;
  const canStart = current.isAdmin && connectedPlayers.length >= 4 && (game.teamMode === "random" || manualReady);
  const teamBuilder = game.teamMode === "manual" ? `<section class="codenames-team-builder">
    ${(["red", "blue"] as CodeNamesTeam[]).map((team) => `<div class="codenames-team-card codenames-team-card--${team}">
      <header><span>${team === "red" ? "RED TEAM" : "BLUE TEAM"}</span><strong>${team === "red" ? red.length : blue.length} players</strong></header>
      <ul>${connectedPlayers.map((player, index) => `<li class="${game.teams[player.id] === team ? "is-on-team" : ""}">
        <span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><b>${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}</b>
        <button data-codenames-player="${escapeHtml(player.id)}" data-codenames-team="${team}">${game.teams[player.id] === team ? "✓" : "Move"}</button>
      </li>`).join("")}</ul>
    </div>`).join("")}
  </section>` : `<section class="codenames-random-note glass-card"><span aria-hidden="true">↝</span><div><strong>Teams shuffle when the game starts</strong><p>Everyone gets a side, then each team gets one random spymaster.</p></div></section>`;

  app.innerHTML = pageShell(`
    <section class="lobby-head codenames-lobby-head">
      <p class="eyebrow">CODE NAMES ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Everyone joins one of two field teams.</p>
      <a class="whatsapp-share codenames-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--codenames"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to join</strong><span>4+ players · two teams · one shared board</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Agents</h2><span>${connectedPlayers.length} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="codenames-setup glass-card">
      <div class="section-title"><h2>Assign the teams</h2><span>Random is the default</span></div>
      <div class="codenames-mode-switch" role="group" aria-label="Team assignment">
        <button class="${game.teamMode === "random" ? "is-active" : ""}" data-codenames-mode="random"><b>↝</b><span><strong>Random teams</strong><small>Shuffle everyone at start</small></span></button>
        <button class="${game.teamMode === "manual" ? "is-active" : ""}" data-codenames-mode="manual"><b>☷</b><span><strong>Assign manually</strong><small>Place every joined player</small></span></button>
      </div>
      ${teamBuilder}
    </section>
    <section class="codenames-rules glass-card"><div><b>1</b><span><strong>Spymasters see the key</strong><small>One player per team knows every card.</small></span></div><div><b>2</b><span><strong>Give one word + number</strong><small>Connect as many of your agents as you can.</small></span></div><div><b>3</b><span><strong>Tap carefully</strong><small>Find your team first. The assassin ends it.</small></span></div></section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large codenames-primary" ${canStart ? "" : "disabled"}><span>Start Code Names</span><b aria-hidden="true">⌗</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connectedPlayers.length < 4 ? "At least 4 players are needed" : game.teamMode === "manual" && !manualReady ? "Put at least 2 players on each team" : `${connectedPlayers.length} players · two spymasters · 25 secret cards`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#14253d", light: "#f4ecd8" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-codenames-mode]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "codenames_team_mode", teamMode: button.dataset.codenamesMode }); }));
  document.querySelectorAll<HTMLButtonElement>("[data-codenames-player]").forEach((button) => button.addEventListener("click", () => { tap(); send({ type: "codenames_team_assign", playerId: button.dataset.codenamesPlayer, team: button.dataset.codenamesTeam }); }));
}

function renderSequenceLobby() {
  const current = room!;
  const game = current.sequence!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  const canStart = current.isAdmin && connected >= 1;
  app.innerHTML = pageShell(`
    <section class="lobby-head sequence-lobby-head">
      <p class="eyebrow">SEQUENCE ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Every player gets a private hand and a place in the team rotation.</p>
      <a class="whatsapp-share sequence-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--sequence"><canvas id="qr-code" aria-label="QR code to join this room"></canvas><strong>Scan to join</strong><span>Any number of players · one phone each</span></div>
      <div class="glass-card roster-card"><div class="section-title"><h2>Players</h2><span>${connected} ready</span></div><ul class="players">${playerList()}</ul></div>
    </section>
    <section class="sequence-setup glass-card">
      <div class="section-title"><h2>How many teams?</h2><span>Two teams is the default</span></div>
      <div class="sequence-team-count" role="group" aria-label="Number of teams">
        ${([2, 3] as const).map((count) => `<button class="${game.teamCount === count ? "is-active" : ""}" data-sequence-team-count="${count}"><b>${count}</b><span><strong>${count} teams</strong><small>${count === 2 ? "Coral vs Aqua · first to 2 runs" : "Coral vs Aqua vs Gold · first run wins"}</small></span><i>${game.teamCount === count ? "✓" : ""}</i></button>`).join("")}
      </div>
      <div class="sequence-auto-teams"><span aria-hidden="true">↝</span><div><strong>Balanced automatically</strong><p>Players shuffle into ${game.teamCount} teams when the game starts. There’s no player limit and late joiners take the smallest team.</p></div></div>
    </section>
    <section class="sequence-rules glass-card"><div><b>1</b><span><strong>Play a matching card</strong><small>Place your team chip on either matching board space.</small></span></div><div><b>2</b><span><strong>Use jacks wisely</strong><small>Two eyes place anywhere. One eye removes an unlocked rival.</small></span></div><div><b>3</b><span><strong>Connect five</strong><small>A five-chip run scores and those chips become protected.</small></span></div></section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large sequence-primary" ${canStart ? "" : "disabled"}><span>Deal Sequence</span><b aria-hidden="true">5</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 1 ? "At least 1 player is needed" : `${connected} player${connected === 1 ? "" : "s"} · ${game.teamCount} teams · ${game.targetSequences} run${game.targetSequences === 1 ? "" : "s"} to win`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, { width: 210, margin: 1, color: { dark: "#15362f", light: "#fffaf0" }, errorCorrectionLevel: "M" });
  document.querySelector("#copy-code")!.addEventListener("click", async () => { await navigator.clipboard?.writeText(current.roomCode); showToast("Room code copied!"); tap(); });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-sequence-team-count]").forEach((button) => button.addEventListener("click", () => {
    tap(); send({ type: "sequence_team_count", teamCount: Number(button.dataset.sequenceTeamCount) });
  }));
}

function renderWordLobby() {
  const current = room!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  const target = current.wordMatch?.targetScore || 5;
  app.innerHTML = pageShell(`
    <section class="lobby-head match-lobby-head">
      <p class="eyebrow">APPLES TO APPLES ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Everyone plays on their own phone.</p>
      <a class="whatsapp-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--match">
        <canvas id="qr-code" aria-label="QR code to join this room"></canvas>
        <strong>Scan to join</strong><span>No account or app needed.</span>
      </div>
      <div class="glass-card roster-card">
        <div class="section-title"><h2>Players</h2><span>${connected} ready</span></div>
        <ul class="players">${playerList()}</ul>
      </div>
    </section>
    <section class="match-rules glass-card">
      <div><span class="match-rules__icon" aria-hidden="true">✦</span><div><h2>How to play</h2><p>A judge reveals a prompt. Everyone else plays the card that fits best. The judge picks a favorite, then the role rotates.</p></div></div>
      <div class="target-picker">
        <span>First to</span>
        <div role="group" aria-label="Points to win">${[3, 5, 7, 10].map((score) => `<button class="target-button ${target === score ? "target-button--active" : ""}" data-target-score="${score}">${score}</button>`).join("")}</div>
        <span>points wins</span>
      </div>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large match-primary" ${connected < 2 || !current.isAdmin ? "disabled" : ""}><span>Start Apples to Apples</span><b aria-hidden="true">✦</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 2 ? "At least 2 players are needed" : `${connected} players · private hands · rotating judge`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, {
    width: 210, margin: 1, color: { dark: "#20291f", light: "#fbfff1" }, errorCorrectionLevel: "M",
  });
  document.querySelector("#copy-code")!.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(current.roomCode);
    showToast("Room code copied!");
    tap();
  });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-target-score]").forEach((button) => button.addEventListener("click", () => {
    tap();
    send({ type: "target_score", targetScore: Number(button.dataset.targetScore) });
  }));
}

function renderImpostorLobby() {
  const current = room!;
  const joinUrl = `${location.origin}/?room=${current.roomCode}`;
  const whatsappUrl = whatsappShareUrl(current.roomCode, joinUrl);
  const connected = current.players.filter((player) => player.connected).length;
  const target = current.impostor?.targetScore || 5;
  app.innerHTML = pageShell(`
    <section class="lobby-head impostor-lobby-head">
      <p class="eyebrow">ONE-WORD IMPOSTOR ROOM</p>
      <button class="room-code" id="copy-code" aria-label="Copy room code">${current.roomCode}<span>copy</span></button>
      <p>Share the code or QR. Keep every screen private.</p>
      <a class="whatsapp-share impostor-share" href="${whatsappUrl}" target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span> Share on WhatsApp</a>
    </section>
    <section class="lobby-grid">
      <div class="glass-card qr-card qr-card--impostor">
        <canvas id="qr-code" aria-label="QR code to join this room"></canvas>
        <strong>Scan to join</strong><span>2+ players · one phone each</span>
      </div>
      <div class="glass-card roster-card">
        <div class="section-title"><h2>Suspects</h2><span>${connected} ready</span></div>
        <ul class="players">${playerList()}</ul>
      </div>
    </section>
    <section class="impostor-rules glass-card">
      <div class="impostor-rules__steps">
        <div><b>1</b><span><strong>Check your role</strong><small>One player won’t see the topic.</small></span></div>
        <div><b>2</b><span><strong>Give one word</strong><small>Helpful—but never too obvious.</small></span></div>
        <div><b>3</b><span><strong>Vote together</strong><small>Catch the player who is bluffing.</small></span></div>
      </div>
      <div class="target-picker impostor-target-picker">
        <span>First to</span>
        <div role="group" aria-label="Points to win">${[3, 5, 7, 10].map((score) => `<button class="target-button ${target === score ? "target-button--active" : ""}" data-target-score="${score}">${score}</button>`).join("")}</div>
        <span>points wins</span>
      </div>
    </section>
    <div class="sticky-action">
      <button id="start-game" class="primary-button primary-button--large impostor-primary" ${connected < 2 || !current.isAdmin ? "disabled" : ""}><span>Deal Secret Roles</span><b aria-hidden="true">◉</b></button>
      <p>${!current.isAdmin ? "Waiting for the group admin to start" : connected < 2 ? "At least 2 players are needed" : `${connected} suspects · detectives +1 · escaped impostor +2`}</p>
    </div>
  `);
  bindCommon();
  QRCode.toCanvas(document.querySelector("#qr-code"), joinUrl, {
    width: 210, margin: 1, color: { dark: "#321a1f", light: "#fff4e7" }, errorCorrectionLevel: "M",
  });
  document.querySelector("#copy-code")!.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(current.roomCode);
    showToast("Room code copied!");
    tap();
  });
  document.querySelector("#start-game")!.addEventListener("click", () => { unlockAudio(); send({ type: "start" }); });
  document.querySelectorAll<HTMLButtonElement>("[data-target-score]").forEach((button) => button.addEventListener("click", () => {
    tap();
    send({ type: "target_score", targetScore: Number(button.dataset.targetScore) });
  }));
}

function renderWordMatch() {
  cleanupTimer();
  const current = room!;
  const match = current.wordMatch!;
  const isJudge = match.judgeId === myId;
  const isResult = current.phase === "round_result";
  const winner = match.winner;
  const prompt = escapeHtml(match.greenCard || "Pick the best match");
  let playArea = "";

  if (current.phase === "submitting") {
    if (isJudge) {
      playArea = `<section class="match-wait-card"><span class="judge-crown">JUDGE</span><h2>You’re choosing this round</h2><p>${match.submittedCount} of ${match.eligibleCount} cards are in. Player names stay hidden until you pick.</p><div class="submission-dots">${Array.from({ length: match.eligibleCount }, (_, index) => `<i class="${index < match.submittedCount ? "filled" : ""}"></i>`).join("")}</div></section>`;
    } else if (match.hasSubmitted) {
      playArea = `<section class="match-wait-card match-wait-card--submitted"><span class="submit-check">✓</span><h2>Card played</h2><p>Waiting for ${match.eligibleCount - match.submittedCount} more player${match.eligibleCount - match.submittedCount === 1 ? "" : "s"}. Your pick stays secret.</p></section>`;
    } else {
      playArea = `<section class="hand-area"><div class="hand-title"><div><span>YOUR HAND</span><h2>Pick the best match</h2></div><small>Tap one card to play it</small></div><div class="word-hand">${match.hand.map((card, index) => `<button class="word-card ${selectedWordCard === card ? "word-card--picked" : ""}" data-word-card="${escapeHtml(card)}"><span>${index + 1}</span><strong>${escapeHtml(card)}</strong><i>✦</i></button>`).join("")}</div></section>`;
    }
  } else if (current.phase === "judging") {
    if (isJudge) {
      playArea = `<section class="hand-area judging-area"><div class="hand-title"><div><span>THE CARDS ARE IN</span><h2>Choose your favorite</h2></div><small>${match.submissions.length} anonymous picks</small></div><div class="judging-grid">${match.submissions.map((submission) => `<button class="word-card word-card--judge" data-submission-id="${submission.id}"><span>✦</span><strong>${escapeHtml(submission.card)}</strong><i>pick</i></button>`).join("")}</div></section>`;
    } else {
      playArea = `<section class="match-wait-card"><span class="judge-crown">${escapeHtml(match.judgeName)}</span><h2>The judge is choosing</h2><p>All cards are anonymous. Prepare your poker face.</p><div class="card-stack" aria-hidden="true"><i></i><i></i><i></i></div></section>`;
    }
  } else if (isResult) {
    const won = winner?.playerId === myId;
    playArea = `<section class="round-winner ${won ? "round-winner--me" : ""}"><p>${won ? "YOUR CARD WON" : "ROUND WINNER"}</p><div class="winning-word-card"><span>${prompt}</span><strong>${escapeHtml(winner?.card || "")}</strong><i>✦</i></div><h2>${won ? "You got the point!" : `${escapeHtml(winner?.playerName || "Someone")} gets the point!`}</h2><small>Next judge coming up…</small></section>`;
  }

  app.innerHTML = pageShell(`
    <section class="game-top match-game-top">
      <div class="game-meta"><span>APPLES TO APPLES · ROUND ${match.roundNumber}</span><strong>First to ${match.targetScore}</strong></div>
    </section>
    <section class="prompt-stage ${isResult ? "prompt-stage--result" : ""}">
      <p>${isJudge ? "YOU ARE THE JUDGE" : `${escapeHtml(match.judgeName)} IS JUDGING`}</p>
      <div class="green-card"><span>THE PROMPT</span><h1>${prompt}</h1><i>✦</i></div>
    </section>
    ${playArea}
    <aside class="score-strip score-strip--match"><ul>${playerList([...current.players].sort((a, b) => b.score - a.score))}</ul></aside>
  `, true);
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-word-card]").forEach((button) => button.addEventListener("click", () => {
    if (selectedWordCard) return;
    selectedWordCard = button.dataset.wordCard!;
    document.querySelectorAll<HTMLButtonElement>("[data-word-card]").forEach((card) => card.disabled = true);
    button.classList.add("word-card--picked");
    unlockAudio(); playTone(510, .07, "sine"); tap();
    send({ type: "word_submit", card: selectedWordCard });
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-submission-id]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-submission-id]").forEach((card) => card.disabled = true);
    button.classList.add("word-card--picked");
    unlockAudio(); tap();
    send({ type: "word_pick", submissionId: button.dataset.submissionId });
  }));
}

function impostorClueBoard(game: ImpostorView) {
  const currentIndex = game.turnOrder.findIndex((turn) => turn.playerId === game.currentCluePlayerId);
  return `
    <section class="clue-tally">
      <div class="clue-tally__head"><div><span>RUNNING CLUES</span><h2>${game.clues.length} of ${game.eligibleCount}</h2></div><small>Turn ${Math.min(currentIndex + 1, game.eligibleCount)} of ${game.eligibleCount}</small></div>
      <div class="turn-queue" aria-label="Random clue order">${game.turnOrder.map((turn, index) => `<span class="${turn.submitted ? "turn-done" : ""} ${turn.playerId === game.currentCluePlayerId ? "turn-current" : ""}"><b>${index + 1}</b>${escapeHtml(turn.playerName)}${turn.playerId === myId ? " · YOU" : ""}</span>`).join("")}</div>
      ${game.clues.length ? `<ol class="clue-ledger">${game.clues.map((clue, index) => `<li class="${index === game.clues.length - 1 ? "clue-ledger--latest" : ""} ${clue.verbal ? "clue-ledger--verbal" : ""}"><b>${index + 1}</b><div><small>${index === game.clues.length - 1 ? "LATEST · " : ""}${escapeHtml(clue.playerName)}</small><strong>${clue.verbal ? "Said aloud" : escapeHtml(clue.clue)}</strong></div></li>`).join("")}</ol>` : `<div class="clue-ledger-empty">The first clue will appear here.</div>`}
    </section>`;
}

function renderImpostor() {
  cleanupTimer();
  const current = room!;
  const game = current.impostor!;
  const isImpostor = game.role === "impostor";
  const isSpectator = game.role === null;
  const topic = game.topic ? escapeHtml(game.topic) : "???";
  const category = escapeHtml(game.topicCategory || "Secret topic");
  const result = game.result;
  const isMyTurn = game.currentCluePlayerId === myId;
  const clueBoard = impostorClueBoard(game);
  let roleCard = "";
  let playArea = "";

  if (current.phase === "impostor_result" && result) {
    roleCard = `
      <section class="identity-card identity-card--reveal ${result.caught ? "identity-card--caught" : ""}">
        <span class="identity-card__label">THE IMPOSTOR WAS</span>
        <strong>${escapeHtml(result.impostorName)}</strong>
        <small>The secret topic was <b>${escapeHtml(result.topic)}</b></small>
      </section>`;
    const myPoint = isImpostor ? !result.caught : result.correctVoterIds.includes(myId);
    playArea = `
      <section class="impostor-result ${result.caught ? "impostor-result--caught" : ""}">
        <div class="verdict-mark" aria-hidden="true">${result.caught ? "✓" : "◌"}</div>
        <p>${result.caught ? "CASE CLOSED" : "THEY GOT AWAY"}</p>
        <h1>${result.caught ? "Impostor caught!" : "The bluff worked."}</h1>
        <p class="verdict-copy">${result.caught ? "The room’s top vote found the outsider." : "A split or incorrect top vote lets the impostor escape."} ${myPoint ? "You scored this round." : ""}</p>
        <div class="reveal-clues">${game.clues.map((clue) => `
          <div class="reveal-clue ${clue.playerId === result.impostorId ? "reveal-clue--impostor" : ""}">
            <span>${escapeHtml(initials(clue.playerName))}</span><div><small>${escapeHtml(clue.playerName)}</small><strong>${clue.verbal ? "Said aloud" : escapeHtml(clue.clue)}</strong></div><b>${result.tally[clue.playerId] || 0} vote${(result.tally[clue.playerId] || 0) === 1 ? "" : "s"}</b>
          </div>`).join("")}</div>
        <small class="next-round-note">${!result.caught ? "+2 to the impostor" : "Correct detectives earn +1"} · next deal coming up…</small>
      </section>`;
  } else if (isSpectator) {
    roleCard = `<section class="identity-card identity-card--spectator"><span class="identity-card__label">ROUND IN PROGRESS</span><strong>You’re watching</strong><small>You’ll receive a secret role next round.</small></section>`;
    playArea = current.phase === "clueing"
      ? `<section class="impostor-wait impostor-wait--turn"><div class="radar-loader" aria-hidden="true"><i></i></div><h2>${escapeHtml(game.currentCluePlayerName)} is choosing a clue</h2><p>Watch the running list. You join the action on the next deal.</p></section>${clueBoard}`
      : `<section class="impostor-wait"><div class="radar-loader" aria-hidden="true"><i></i></div><h2>The room is voting</h2><p>Keep an eye on the suspects. You join the action on the next deal.</p></section>`;
  } else {
    roleCard = isImpostor
      ? `<section class="identity-card identity-card--impostor"><span class="identity-card__label">YOUR SECRET ROLE</span><strong>You’re the impostor</strong><small>You only know the category: <b>${category}</b></small><i aria-hidden="true">?</i></section>`
      : `<section class="identity-card identity-card--insider"><span class="identity-card__label">THE SECRET TOPIC</span><strong>${topic}</strong><small>Category: <b>${category}</b> · hide it in one clever word</small><i aria-hidden="true">●</i></section>`;

    if (current.phase === "clueing") {
      if (isMyTurn) playArea = `<section class="clue-entry-card"><div><span>YOUR TURN</span><h2>Say it in one word</h2><p>${isImpostor ? "Use the clues so far and the category to blend in." : "Build on the clues without giving the topic away."}</p></div><form id="impostor-clue-form"><input id="impostor-clue" maxlength="24" autocomplete="off" spellcheck="false" placeholder="Type one word…" aria-label="Your one-word clue"><button class="impostor-primary" type="submit">Lock clue <b>→</b></button></form><small>Your typed clue appears for everyone as soon as you submit.</small><div class="verbal-divider"><span>or play verbally</span></div><button class="verbal-next" id="impostor-verbal-next" type="button"><span>I said my clue aloud</span><b>Next player →</b></button></section>${clueBoard}`;
      else playArea = `<section class="impostor-wait impostor-wait--turn">${game.hasSubmitted ? `<span class="submit-check impostor-check">✓</span>` : `<div class="radar-loader" aria-hidden="true"><i></i></div>`}<h2>${escapeHtml(game.currentCluePlayerName)} is up</h2><p>${game.hasSubmitted ? "Your clue is on the board." : "Watch the clues, then get ready for your turn."}</p></section>${clueBoard}`;
    } else if (current.phase === "voting") {
      playArea = `
        <section class="suspect-section">
          <div class="suspect-heading"><div><span>CLUES REVEALED</span><h2>Who’s bluffing?</h2></div><small>${game.voteCount} of ${game.eligibleCount} votes locked</small></div>
          <div class="suspect-grid">${game.clues.map((clue) => {
            const isMe = clue.playerId === myId;
            const picked = game.votedForId === clue.playerId;
            return `<button class="suspect-card ${isMe ? "suspect-card--me" : ""} ${picked ? "suspect-card--picked" : ""}" data-suspect-id="${escapeHtml(clue.playerId)}" ${isMe || game.hasVoted ? "disabled" : ""}><span class="avatar">${escapeHtml(initials(clue.playerName))}</span><small>${escapeHtml(clue.playerName)}${isMe ? " · YOU" : ""}</small><strong>${clue.verbal ? "Said aloud" : escapeHtml(clue.clue)}</strong><b>${isMe ? "YOUR CLUE" : picked ? "VOTE LOCKED" : "VOTE"}</b></button>`;
          }).join("")}</div>
          ${game.hasVoted ? `<p class="vote-locked-note">Vote locked. Waiting for the rest of the room…</p>` : `<p class="vote-locked-note">Tap one suspect. You can’t vote for yourself.</p>`}
        </section>`;
      }
    }

  app.innerHTML = pageShell(`
    <section class="game-top impostor-game-top"><div class="game-meta"><span>ONE-WORD IMPOSTOR · ROUND ${game.roundNumber}</span><strong>First to ${game.targetScore}</strong></div></section>
    ${roleCard}
    ${playArea}
    <aside class="score-strip score-strip--impostor"><ul>${playerList([...current.players].sort((a, b) => b.score - a.score))}</ul></aside>
  `, true);
  bindCommon();
  document.querySelector<HTMLFormElement>("#impostor-clue-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLInputElement>("#impostor-clue")!;
    const clue = input.value.trim();
    if (!/^[\p{L}\p{N}][\p{L}\p{N}'’-]{0,23}$/u.test(clue)) {
      input.focus();
      return showToast("One word only—no spaces or symbols.");
    }
    input.disabled = true;
    document.querySelector<HTMLButtonElement>("#impostor-clue-form button")!.disabled = true;
    unlockAudio(); playTone(470, .08, "sine"); tap();
    send({ type: "impostor_clue", clue });
  });
  document.querySelector<HTMLButtonElement>("#impostor-verbal-next")?.addEventListener("click", () => {
    document.querySelector<HTMLInputElement>("#impostor-clue")!.disabled = true;
    document.querySelector<HTMLButtonElement>("#impostor-clue-form button")!.disabled = true;
    document.querySelector<HTMLButtonElement>("#impostor-verbal-next")!.disabled = true;
    unlockAudio(); playTone(390, .08, "sine"); tap();
    send({ type: "impostor_next" });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-suspect-id]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-suspect-id]").forEach((suspect) => suspect.disabled = true);
    button.classList.add("suspect-card--picked");
    unlockAudio(); playTone(240, .08, "triangle"); tap();
    send({ type: "impostor_vote", targetId: button.dataset.suspectId });
  }));
}

const dieFaces = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

function dieGlyph(face: number) {
  return dieFaces[face - 1] || "?";
}

function dieFaceClass(face: number) {
  return Number.isInteger(face) && face >= 1 && face <= 6 ? `dice-face--${face}` : "";
}

function diceRow(dice: number[], hidden = false) {
  return `<div class="dice-row">${dice.map((die) => `<span class="die ${hidden ? "die--hidden" : dieFaceClass(die)}" aria-label="${hidden ? "Hidden die" : `Die showing ${die}`}">${hidden ? "?" : dieGlyph(die)}</span>`).join("")}</div>`;
}

function renderLiarsDice() {
  cleanupTimer();
  const current = room!;
  const game = current.liarsDice!;
  const result = game.result;
  const isMyTurn = game.currentPlayerId === myId && game.isPlaying && current.phase === "dice_bidding";
  const bid = game.currentBid;
  const { minQuantity, defaultQuantity, defaultFace } = diceBidDefaults(bid, game.totalDice);
  const canRaise = minQuantity <= game.totalDice;
  const currentBidCard = bid
    ? `<section class="dice-bid-card"><span>CURRENT BID</span><strong>${bid.quantity} × <i class="dice-bid-face ${dieFaceClass(bid.face)}" aria-label="Die showing ${bid.face}">${dieGlyph(bid.face)}</i></strong><small>${escapeHtml(bid.playerName)} says there are at least ${bid.quantity} ${bid.face}${bid.quantity === 1 ? "" : "s"}.</small></section>`
    : `<section class="dice-bid-card dice-bid-card--open"><span>OPENING BID</span><strong>No bid yet</strong><small>${escapeHtml(game.currentPlayerName)} starts the round.</small></section>`;

  let actionArea = "";
  if (current.phase === "dice_result" && result) {
    const verdict = result.bidWasTrue ? "The bid was good." : "That was a bluff.";
    actionArea = `
      <section class="dice-verdict ${result.loserId === myId ? "dice-verdict--lost" : ""}">
        <div class="verdict-mark" aria-hidden="true">${result.bidWasTrue ? "✓" : "!"}</div>
        <p>${result.bidWasTrue ? "BID HOLDS" : "LIAR CAUGHT"}</p>
        <h1>${verdict}</h1>
        <p class="verdict-copy">The table revealed <b>${result.actualCount}</b> ${result.bid.face}${result.actualCount === 1 ? "" : "s"}. ${escapeHtml(result.loserName)} loses one die.</p>
        <div class="dice-reveal-grid">${result.revealedHands.map((entry) => `
          <article class="dice-reveal-hand ${entry.playerId === result.loserId ? "dice-reveal-hand--loser" : ""}">
            <div><strong>${escapeHtml(entry.playerName)}${entry.playerId === myId ? " · YOU" : ""}</strong><small>${entry.playerId === result.bidderId ? "made the bid" : entry.playerId === result.challengerId ? "called liar" : "revealed"}</small></div>
            ${diceRow(entry.dice)}
          </article>`).join("")}</div>
        <small class="next-round-note">${(game.diceCounts[result.loserId] || 0) > 0 ? `${escapeHtml(result.loserName)} opens the next round` : `${escapeHtml(result.loserName)} is out`} · new dice in a moment…</small>
      </section>`;
  } else if (!game.isPlaying) {
    actionArea = `<section class="dice-wait"><span class="die die--hidden">?</span><h2>You’re joining the table</h2><p>${(game.diceCounts[myId] || 0) === 0 ? "You’re out of dice. Stay sharp—the last player standing wins." : "This bid was already underway. Your five dice enter at the start of the next round."}</p></section>`;
  } else if (isMyTurn) {
    const bidForm = canRaise ? `
      <form class="dice-action-card" id="dice-bid-form">
        <div><span>YOUR TURN</span><h2>${bid ? "Raise the bid" : "Make the first bid"}</h2><p>How many matching dice do you think are under every cup?</p></div>
        <div class="dice-bid-builder">
          <label>Quantity<select id="dice-quantity" aria-label="Bid quantity">${Array.from({ length: game.totalDice - minQuantity + 1 }, (_, index) => minQuantity + index).map((quantity) => `<option value="${quantity}" ${quantity === defaultQuantity ? "selected" : ""}>${quantity} ${quantity === 1 ? "die" : "dice"}</option>`).join("")}</select></label>
          <fieldset><legend>Face</legend><input id="dice-face" type="hidden" value="${defaultFace}">${dieFaces.map((glyph, index) => `<button class="face-button ${dieFaceClass(index + 1)} ${index + 1 === defaultFace ? "face-button--active" : ""}" type="button" data-dice-face="${index + 1}" ${bid && defaultQuantity === bid.quantity && index + 1 <= bid.face ? "disabled" : ""} aria-label="Bid on ${index + 1}s">${glyph}</button>`).join("")}</fieldset>
        </div>
        <button class="dice-primary dice-bid-submit" type="submit"><span>Lock Bid</span><b>→</b></button>
      </form>` : `<section class="dice-max-bid"><strong>That’s the highest possible bid.</strong><span>There’s only one move left.</span></section>`;
    actionArea = `${bidForm}${bid ? `<button class="liar-button" id="dice-challenge" type="button"><span>Call Liar!</span><small>Reveal every die</small></button>` : ""}`;
  } else {
    actionArea = `<section class="dice-wait"><div class="dice-cup" aria-hidden="true">◆</div><h2>${escapeHtml(game.currentPlayerName)} is deciding</h2><p>They must raise the bid${bid ? " or call liar" : ""}. Keep your dice secret.</p></section>`;
  }

  const diceStandings = [...current.players]
    .filter((player) => game.diceCounts[player.id] !== undefined)
    .sort((a, b) => (game.diceCounts[b.id] || 0) - (game.diceCounts[a.id] || 0) || a.name.localeCompare(b.name));
  const activePlayerCount = game.eligiblePlayerIds.filter((playerId) => (game.diceCounts[playerId] || 0) > 0).length;
  const playerStatus = (player: Player) => {
    const count = game.diceCounts[player.id] || 0;
    if (count === 0) return "OUT";
    if (!game.eligiblePlayerIds.includes(player.id)) return "NEXT ROUND";
    if (game.currentPlayerId === player.id && current.phase === "dice_bidding") return "TO ACT";
    if (game.currentBid?.playerId === player.id) return "LAST BID";
    return "ROLLING";
  };
  const tableOverview = `<section class="dice-table-overview">
    <article class="dice-player-board glass-card">
      <header><div><span>PLAYERS AT THE TABLE</span><strong>${activePlayerCount} player${activePlayerCount === 1 ? "" : "s"} currently rolling</strong></div><small>${game.totalDice} dice remain</small></header>
      <ul>${diceStandings.map((player, index) => {
        const count = game.diceCounts[player.id] || 0;
        return `<li class="${count === 0 ? "is-out" : ""} ${game.currentPlayerId === player.id ? "is-turn" : ""}"><span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><div><strong>${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}</strong><small>${playerStatus(player)}</small></div><b>${count}<small>${count === 1 ? " die" : " dice"}</small></b></li>`;
      }).join("")}</ul>
    </article>
    <article class="dice-bid-history glass-card">
      <header><div><span>THIS ROUND</span><strong>Bid history</strong></div><small>${game.bidHistory.length} bid${game.bidHistory.length === 1 ? "" : "s"}</small></header>
      ${game.bidHistory.length ? `<ol>${[...game.bidHistory].reverse().map((historyBid, reverseIndex) => {
        const bidNumber = game.bidHistory.length - reverseIndex;
        return `<li class="${bidNumber === game.bidHistory.length ? "is-latest" : ""}"><b>${bidNumber}</b><span><strong>${escapeHtml(historyBid.playerName)}</strong><small>${bidNumber === 1 ? "OPENED" : "RAISED"}</small></span><em>${historyBid.quantity} × <i class="${dieFaceClass(historyBid.face)}" aria-label="Die showing ${historyBid.face}">${dieGlyph(historyBid.face)}</i></em></li>`;
      }).join("")}</ol>` : `<p>No bids yet. ${escapeHtml(game.currentPlayerName)} will open the round.</p>`}
    </article>
  </section>`;
  app.innerHTML = pageShell(`
    <section class="game-top dice-game-top"><div class="game-meta"><span>LIAR’S DICE · ROUND ${game.roundNumber}</span><strong>${game.totalDice} dice in play</strong></div></section>
    <section class="private-dice ${game.isPlaying ? "" : "private-dice--out"}">
      <div><span>${game.isPlaying ? "YOUR SECRET ROLL" : "YOUR CUP IS EMPTY"}</span><strong>${game.isPlaying ? `${game.hand.length} ${game.hand.length === 1 ? "die" : "dice"}` : "Spectating"}</strong></div>
      ${game.isPlaying ? diceRow([...game.hand].sort()) : `<div class="dice-row"><span class="die die--hidden">×</span></div>`}
      <small>${game.isPlaying ? "Don’t show anyone." : "You can still watch every bid."}</small>
    </section>
    ${currentBidCard}
    ${tableOverview}
    ${actionArea}
  `, true);
  bindCommon();

  const quantity = document.querySelector<HTMLSelectElement>("#dice-quantity");
  const faceInput = document.querySelector<HTMLInputElement>("#dice-face");
  const faceButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-dice-face]")];
  const chooseFace = (face: number) => {
    if (!faceInput) return;
    faceInput.value = String(face);
    faceButtons.forEach((button) => button.classList.toggle("face-button--active", Number(button.dataset.diceFace) === face));
  };
  const syncValidFaces = () => {
    if (!quantity || !bid) return;
    const sameQuantity = Number(quantity.value) === bid.quantity;
    faceButtons.forEach((button) => button.disabled = sameQuantity && Number(button.dataset.diceFace) <= bid.face);
    const selected = Number(faceInput?.value);
    if (faceButtons.find((button) => Number(button.dataset.diceFace) === selected)?.disabled) {
      const first = faceButtons.find((button) => !button.disabled);
      if (first) chooseFace(Number(first.dataset.diceFace));
    }
  };
  faceButtons.forEach((button) => button.addEventListener("click", () => { chooseFace(Number(button.dataset.diceFace)); tap(); }));
  quantity?.addEventListener("change", syncValidFaces);
  document.querySelector<HTMLFormElement>("#dice-bid-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const quantityValue = Number(quantity?.value);
    const face = Number(faceInput?.value);
    if (!Number.isInteger(quantityValue) || !Number.isInteger(face)) return;
    document.querySelectorAll<HTMLButtonElement>("#dice-bid-form button, #dice-challenge").forEach((button) => button.disabled = true);
    unlockAudio(); playTone(430, .08, "triangle"); tap();
    send({ type: "dice_bid", quantity: quantityValue, face });
  });
  document.querySelector<HTMLButtonElement>("#dice-challenge")?.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("#dice-bid-form button, #dice-challenge").forEach((button) => button.disabled = true);
    unlockAudio(); playTone(175, .22, "sawtooth"); tap();
    send({ type: "dice_challenge" });
  });
}

function renderTaboo() {
  cleanupTimer();
  const current = room!;
  const game = current.taboo!;
  const isReady = current.phase === "taboo_ready" || (current.phase === "taboo_turn" && !current.deadline);
  const team = game.activeTeam;
  const isGiver = game.clueGiverId === myId;
  const isOpponent = Boolean(game.viewerTeam && team && game.viewerTeam !== team);
  const isTeammate = Boolean(game.viewerTeam && game.viewerTeam === team && !isGiver);
  const teamAPlayers = current.players.filter((player) => game.teams[player.id] === "a");
  const teamBPlayers = current.players.filter((player) => game.teams[player.id] === "b");
  const teamRoster = (players: Player[]) => players.map((player) => `<li class="${player.connected ? "" : "is-away"}"><span class="avatar avatar--${Math.max(0, current.players.findIndex((candidate) => candidate.id === player.id)) % 5}">${escapeHtml(initials(player.name))}</span><strong>${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}</strong>${player.id === game.clueGiverId ? "<b>CLUE GIVER</b>" : ""}</li>`).join("");
  const turnLabel = (rosterTeam: TabooTeam) => current.phase === "taboo_result" ? "TURN COMPLETE" : game.activeTeam === rosterTeam ? isReady ? "UP NEXT" : "GUESSING NOW" : "WAITING";
  const viewerStatus = current.phase === "taboo_result"
    ? { title: "Turn complete", detail: "The next clue-giver is coming up." }
    : isReady
      ? isGiver
        ? { title: "Your turn is ready", detail: "Start when everyone is ready. Your first card and the clock will appear together." }
        : { title: `${escapeHtml(game.clueGiverName)} is up next`, detail: "The card and clock stay hidden until the clue-giver starts the turn." }
    : isGiver
      ? { title: "You’re the clue-giver", detail: `Your ${escapeHtml(tabooTeamName(game.viewerTeam))} teammates are guessing.` }
      : isTeammate
        ? { title: "Your team is guessing", detail: `${escapeHtml(game.clueGiverName)} is giving clues—call out your answers.` }
        : isOpponent
          ? { title: "Your team is waiting", detail: `Watch ${escapeHtml(tabooTeamName(game.activeTeam))} and buzz if the clue-giver says a forbidden word.` }
          : { title: "You’re watching this turn", detail: `${escapeHtml(tabooTeamName(game.activeTeam))} is guessing now.` };
  let playArea = "";
  if (current.phase === "taboo_result") {
    const result = game.result;
    playArea = `<section class="taboo-turn-result taboo-turn-result--${result?.team || "a"}">
      <p>TIME’S UP · ${escapeHtml(tabooTeamName(result?.team || null))}</p>
      <h1>${escapeHtml(result?.clueGiverName || "That turn")} scored ${result?.correct || 0}</h1>
      <div><span><b>${result?.correct || 0}</b><small>GOT IT</small></span><span><b>${result?.skipped || 0}</b><small>SKIPPED</small></span><span><b>${result?.taboos || 0}</b><small>TABOOS</small></span></div>
      <small>Next clue-giver coming up…</small>
    </section>`;
  } else if (isReady) {
    playArea = isGiver
      ? `<section class="taboo-ready-card taboo-ready-card--giver"><div aria-hidden="true">▶</div><p>YOU’RE THE NEXT CLUE-GIVER</p><h1>Ready when you are.</h1><small>Your first word is still hidden. Starting reveals it and begins the ${game.roundSeconds}-second clock.</small><button id="taboo-start-turn"><span>Start Turn</span><b>→</b></button></section>`
      : `<section class="taboo-ready-card"><div aria-hidden="true">…</div><p>NEXT CLUE-GIVER</p><h1>${escapeHtml(game.clueGiverName)}</h1><strong>Waiting to start</strong><small>No card is visible and the clock is paused.</small></section>`;
  } else if (isGiver && game.card) {
    playArea = `<section class="taboo-card taboo-card--giver">
      <span class="taboo-card__label">GET YOUR TEAM TO SAY</span><h1>${escapeHtml(game.card.word)}</h1>
      <div class="taboo-forbidden"><span>YOU CANNOT SAY</span>${game.card.forbidden.map((word) => `<b>${escapeHtml(word)}</b>`).join("")}</div>
    </section>
    <div class="taboo-giver-actions"><button data-taboo-action="skip" class="taboo-skip"><span>Skip</span><b>→</b></button><button data-taboo-action="correct" class="taboo-correct"><span>Got it!</span><b>✓</b></button></div>`;
  } else if (isOpponent && game.card) {
    playArea = `<section class="taboo-watch-card"><p>WATCH FOR A SLIP</p><h1>${escapeHtml(game.card.word)}</h1><div>${game.card.forbidden.map((word) => `<span>${escapeHtml(word)}</span>`).join("")}</div></section>
      <button id="taboo-buzzer" class="taboo-buzzer"><span>TABOO!</span><small>They said a forbidden word</small></button>`;
  } else if (isTeammate) {
    playArea = `<section class="taboo-guessing"><div aria-hidden="true">?</div><p>YOUR CLUE-GIVER</p><h1>${escapeHtml(game.clueGiverName)}</h1><strong>Guess out loud!</strong><small>The secret card stays hidden on your screen.</small></section>`;
  } else {
    playArea = `<section class="taboo-guessing"><div aria-hidden="true">◎</div><p>TURN IN PROGRESS</p><h1>${escapeHtml(game.clueGiverName)} is clueing</h1><strong>Watch the room</strong><small>You’ll join a team next game.</small></section>`;
  }
  app.innerHTML = pageShell(`
    <section class="game-top taboo-game-top">
      <div class="game-meta"><span>${current.phase === "taboo_result" ? "TURN COMPLETE" : isReady ? `${escapeHtml(tabooTeamName(team))} · ${escapeHtml(game.clueGiverName)} UP NEXT` : `${escapeHtml(tabooTeamName(team))} · ${escapeHtml(game.clueGiverName)} CLUEING`}</span><strong>TURN ${game.roundNumber}</strong></div>
      ${current.phase === "taboo_turn" && !isReady ? `<div class="timer-track taboo-timer"><i id="timer-fill"></i></div>` : ""}
    </section>
    <section class="taboo-scoreboard">
      <div class="taboo-score taboo-score--a ${team === "a" ? "is-active" : ""} ${game.viewerTeam === "a" ? "is-viewer-team" : ""}"><div><span>TEAM CORAL${game.viewerTeam === "a" ? " · YOUR TEAM" : ""}</span><em>${turnLabel("a")}</em></div><strong>${game.teamScores.a}</strong><ul>${teamRoster(teamAPlayers)}</ul></div>
      <div class="taboo-score-target"><span>FIRST TO</span><b>${game.targetScore}</b></div>
      <div class="taboo-score taboo-score--b ${team === "b" ? "is-active" : ""} ${game.viewerTeam === "b" ? "is-viewer-team" : ""}"><div><span>TEAM AQUA${game.viewerTeam === "b" ? " · YOUR TEAM" : ""}</span><em>${turnLabel("b")}</em></div><strong>${game.teamScores.b}</strong><ul>${teamRoster(teamBPlayers)}</ul></div>
    </section>
    <aside class="taboo-role-banner taboo-role-banner--${game.viewerTeam || "watching"}"><span>${current.phase === "taboo_result" ? "NEXT TURN SOON" : isReady ? isGiver ? "START WHEN READY" : "CLOCK PAUSED" : isGiver ? "YOUR ROLE" : "RIGHT NOW"}</span><div><strong>${viewerStatus.title}</strong><small>${viewerStatus.detail}</small></div></aside>
    <section class="taboo-play-area ${game.lastAction ? `taboo-last--${game.lastAction}` : ""}">${playArea}</section>
    ${current.phase === "taboo_turn" && !isReady ? `<aside class="taboo-turn-tally"><span><b>${game.correctThisTurn}</b> got it</span><span><b>${game.skippedThisTurn}</b> skipped</span><span><b>${game.taboosThisTurn}</b> taboo</span></aside>` : ""}
  `, true);
  bindCommon();
  document.querySelector<HTMLButtonElement>("#taboo-start-turn")?.addEventListener("click", (event) => {
    (event.currentTarget as HTMLButtonElement).disabled = true;
    unlockAudio(); playTone(520, .08, "triangle"); tap();
    send({ type: "taboo_start_turn" });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-taboo-action]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-taboo-action]").forEach((action) => action.disabled = true);
    tap();
    if (button.dataset.tabooAction === "correct") playTone(660, .07, "triangle");
    send({ type: "taboo_card", action: button.dataset.tabooAction });
  }));
  document.querySelector<HTMLButtonElement>("#taboo-buzzer")?.addEventListener("click", (event) => {
    (event.currentTarget as HTMLButtonElement).disabled = true;
    unlockAudio(); playTone(150, .2, "sawtooth"); tap();
    send({ type: "taboo_buzzer" });
  });
  if (current.phase === "taboo_turn" && !isReady) startTimer(game.roundSeconds * 1_000);
}

function renderCodeNames() {
  cleanupTimer();
  const current = room!;
  const game = current.codeNames!;
  const activeTeam = game.activeTeam || "red";
  const activeSpymasterId = game.spymasters[activeTeam];
  const activeSpymasterName = game.spymasterNames[activeTeam] || "Your spymaster";
  const isActiveSpymaster = activeSpymasterId === myId;
  const spymasterTeam = game.viewerTeam || "red";
  const canGuess = current.phase === "codenames_guess" && game.viewerTeam === activeTeam && !game.isSpymaster;
  const teamRoster = (team: CodeNamesTeam) => current.players
    .filter((player) => game.teams[player.id] === team)
    .sort((first, second) => Number(game.spymasters[team] === second.id) - Number(game.spymasters[team] === first.id))
    .map((player) => `<li class="${player.connected ? "" : "is-away"}"><span>${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}</span>${game.spymasters[team] === player.id ? "<b>SPYMASTER</b>" : "<small>FIELD AGENT</small>"}</li>`)
    .join("");
  const previousClues = (game.clue ? game.clueHistory.slice(0, -1) : game.clueHistory).slice().reverse();
  const clueHistory = `<section class="codenames-clue-history" aria-label="Clue history for both teams"><h3>Clue history · both teams</h3>${previousClues.length ? `<ol>${previousClues.map((entry) => `<li class="codenames-clue-history--${entry.team}"><span>TURN ${entry.turnNumber} · ${entry.team.toUpperCase()}</span><strong>${escapeHtml(entry.word)} <em>for ${entry.count}</em></strong></li>`).join("")}</ol>` : `<p class="codenames-clue-history__empty">No previous clues yet.</p>`}</section>`;
  const cluePanel = current.phase === "codenames_clue"
    ? isActiveSpymaster
      ? `<section class="codenames-clue-card codenames-clue-card--${activeTeam}"><p>YOUR TURN, SPYMASTER</p><h2>Connect your agents.</h2><form id="codenames-clue-form"><label><span>One-word clue</span><input id="codenames-clue" maxlength="24" autocomplete="off" placeholder="Orbit" aria-label="One-word clue"></label><fieldset class="codenames-count-picker"><legend>Cards linked</legend><input id="codenames-count" type="hidden" value="1"><button class="is-active" type="button" data-codenames-count="1">1</button><button type="button" data-codenames-count="2">2</button><button type="button" data-codenames-count="3">3</button><button type="button" data-codenames-count="4">4</button><select id="codenames-count-more" aria-label="Choose five or more cards"><option value="">5+</option>${Array.from({ length: 5 }, (_, index) => `<option value="${index + 5}">${index + 5}</option>`).join("")}</select></fieldset><button type="submit">Send clue <b>→</b></button></form><small>Your clue cannot be a word visible on the board.</small></section>`
      : `<section class="codenames-wait-card"><span class="codenames-pulse" aria-hidden="true"></span><p>${activeTeam.toUpperCase()} SPYMASTER IS THINKING</p><h2>${escapeHtml(activeSpymasterName)} is studying the key.</h2><small>${game.viewerTeam === activeTeam ? "Get ready to make the connections." : "Your team is up next."}</small></section>`
    : `<section class="codenames-live-clue codenames-live-clue--${activeTeam} ${game.bonusGuessAvailable ? "is-bonus" : ""}"><span>${game.bonusGuessAvailable ? "BONUS GUESS UNLOCKED" : "THE CLUE"}</span><strong>${escapeHtml(game.clue?.word || "—")} <em>for ${game.clue?.count || 0}</em></strong><small>${game.bonusGuessAvailable ? "The clue count is complete. Take one optional bonus guess—or end the turn." : canGuess ? `${game.guessesLeft} clue guess${game.guessesLeft === 1 ? "" : "es"} left` : `${codeNamesTeamName(activeTeam)} is guessing`}</small></section>`;

  const board = game.board.map((card, index) => {
    const roleClass = card.role ? `codenames-card--${card.role}` : "";
    const revealedClass = card.revealed ? "is-revealed" : "";
    const lastClass = game.lastGuess?.index === index ? "is-last-guess" : "";
    const roleLabel = card.role === "assassin" ? "Assassin" : card.role === "neutral" ? "Neutral" : card.role ? `${codeNamesTeamName(card.role)} agent` : "Hidden agent";
    return `<button class="codenames-card ${roleClass} ${revealedClass} ${lastClass}" data-codenames-card="${index}" ${canGuess && !card.revealed ? "" : "disabled"} aria-label="${escapeHtml(card.word)}. ${roleLabel}${canGuess && !card.revealed ? ". Select this card" : ""}"><span>${escapeHtml(card.word)}</span>${card.revealed ? `<small>${escapeHtml(roleLabel)}</small>` : ""}</button>`;
  }).join("");

  app.innerHTML = pageShell(`
    <section class="codenames-game-head">
      <div><span>ROOM ${escapeHtml(current.roomCode)} · TURN ${game.turnNumber}</span><strong>${game.isSpymaster ? "SPYMASTER VIEW" : escapeHtml(codeNamesTeamName(game.viewerTeam)).toUpperCase()}</strong></div>
      <p class="codenames-turn codenames-turn--${activeTeam}"><i></i>${activeTeam.toUpperCase()} TEAM’S TURN</p>
    </section>
    <section class="codenames-scoreboard">
      <div class="codenames-team-score codenames-team-score--red ${activeTeam === "red" ? "is-active" : ""}"><span>RED AGENTS</span><strong>${game.remaining.red}</strong><ul>${teamRoster("red")}</ul></div>
      <div class="codenames-versus"><span>FIRST TO</span><b>0</b></div>
      <div class="codenames-team-score codenames-team-score--blue ${activeTeam === "blue" ? "is-active" : ""}"><span>BLUE AGENTS</span><strong>${game.remaining.blue}</strong><ul>${teamRoster("blue")}</ul></div>
    </section>
    ${game.isSpymaster ? `<aside class="codenames-spymaster-identity codenames-spymaster-identity--${spymasterTeam}"><span>YOUR ROLE</span><strong>YOU ARE THE ${spymasterTeam.toUpperCase()} SPYMASTER</strong><small>Your team is ${escapeHtml(codeNamesTeamName(spymasterTeam))}.</small></aside><aside class="codenames-spymaster-notice"><b>FULL KEY UNLOCKED</b><span>Both spymasters always see every card’s identity from the start of the game.</span></aside>` : ""}
    ${cluePanel}
    ${clueHistory}
    ${game.isSpymaster ? `<aside class="codenames-key"><span><i class="red"></i> Red</span><span><i class="blue"></i> Blue</span><span><i class="neutral"></i> Neutral</span><span><i class="assassin"></i> Assassin</span></aside>` : ""}
    ${game.lastGuess ? `<p class="codenames-last">${escapeHtml(game.lastGuess.playerName)} uncovered <strong>${escapeHtml(game.board[game.lastGuess.index]?.word || "a card")}</strong></p>` : ""}
    <section class="codenames-board ${game.isSpymaster ? "codenames-board--key" : ""}" aria-label="Code Names word grid">${board}</section>
    ${canGuess ? `<div class="codenames-guess-actions ${game.bonusGuessAvailable ? "is-bonus" : ""}"><p>${game.bonusGuessAvailable ? "Bonus unlocked! Tap one more card, or stop safely and pass the turn." : `Any ${escapeHtml(codeNamesTeamName(activeTeam))} field agent can tap a card. The first tap locks the team’s official guess.`}</p><button id="codenames-end-turn">${game.bonusGuessAvailable ? "Skip bonus & end turn" : "End turn"} <b>→</b></button></div>` : `<p class="codenames-board-note">${game.isSpymaster ? "The key is visible only to spymasters. Spymasters cannot guess." : current.phase === "codenames_guess" ? `Any ${escapeHtml(codeNamesTeamName(activeTeam))} field agent can lock the team’s official guess.` : "The board unlocks when the clue arrives."}</p>`}
  `, true);
  bindCommon();
  document.querySelector<HTMLFormElement>("#codenames-clue-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLInputElement>("#codenames-clue")!;
    const clue = input.value.trim();
    const count = Number(document.querySelector<HTMLInputElement>("#codenames-count")!.value);
    if (!/^[\p{L}\p{N}][\p{L}\p{N}'’-]{0,23}$/u.test(clue)) { input.focus(); return showToast("Use exactly one clue word."); }
    if (game.board.some((card) => card.word.toLocaleLowerCase() === clue.toLocaleLowerCase())) { input.focus(); return showToast("That clue is already on the board."); }
    document.querySelectorAll<HTMLButtonElement>("#codenames-clue-form button").forEach((button) => button.disabled = true);
    unlockAudio(); playTone(520, .08, "triangle"); tap();
    send({ type: "codenames_clue", clue, clueCount: count });
  });
  const clueCountInput = document.querySelector<HTMLInputElement>("#codenames-count");
  const clueCountMore = document.querySelector<HTMLSelectElement>("#codenames-count-more");
  const clueCountButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-codenames-count]")];
  clueCountButtons.forEach((button) => button.addEventListener("click", () => {
    if (!clueCountInput) return;
    clueCountInput.value = button.dataset.codenamesCount || "1";
    clueCountButtons.forEach((candidate) => candidate.classList.toggle("is-active", candidate === button));
    if (clueCountMore) clueCountMore.value = "";
    tap();
  }));
  clueCountMore?.addEventListener("change", () => {
    if (!clueCountInput || !clueCountMore.value) return;
    clueCountInput.value = clueCountMore.value;
    clueCountButtons.forEach((button) => button.classList.remove("is-active"));
    tap();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-codenames-card]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-codenames-card]").forEach((card) => card.disabled = true);
    unlockAudio(); playTone(390, .08, "sine"); tap();
    send({ type: "codenames_guess", cardIndex: Number(button.dataset.codenamesCard) });
  }));
  document.querySelector<HTMLButtonElement>("#codenames-end-turn")?.addEventListener("click", (event) => {
    (event.currentTarget as HTMLButtonElement).disabled = true;
    tap(); send({ type: "codenames_end_turn" });
  });
}

function sequenceCardDetails(card: SequenceCard) {
  const suitCode = card.slice(-1);
  const rank = card.slice(0, -1);
  const suit = ({ S: "♠", H: "♥", D: "♦", C: "♣" } as Record<string, string>)[suitCode] || "";
  const suitClass = ({ S: "spades", H: "hearts", D: "diamonds", C: "clubs" } as Record<string, string>)[suitCode] || "spades";
  return { rank, suit, suitClass, twoEyed: card === "JH" || card === "JD", oneEyed: card === "JS" || card === "JC" };
}

function sequenceCardFace(card: SequenceCard, compact = false, statusLabel = "") {
  const details = sequenceCardDetails(card);
  const jackLabel = details.twoEyed ? "WILD" : details.oneEyed ? "REMOVE" : "";
  const cardLabel = statusLabel || jackLabel;
  return `<span class="sequence-card-face suit-${details.suitClass} ${details.rank === "J" ? "is-jack" : ""}"><b>${escapeHtml(details.rank)}</b><i>${details.suit}</i>${!compact && cardLabel ? `<small>${escapeHtml(cardLabel)}</small>` : ""}</span>`;
}

function renderSequence() {
  const current = room!;
  const game = current.sequence!;
  const activeTeams = (["coral", "aqua", "gold"] as SequenceTeam[]).slice(0, game.teamCount);
  const selectedIndex = selectedSequenceCardIndex !== null && selectedSequenceCardIndex < game.hand.length ? selectedSequenceCardIndex : null;
  const selectedCard = selectedIndex === null ? null : game.hand[selectedIndex];
  const legalSpaces = new Set(selectedIndex === null ? [] : game.legalSpaces[selectedIndex] || []);
  const allHandSpaces = new Set(game.legalSpaces.flatMap((spaces, index) => sequenceCardDetails(game.hand[index]).twoEyed ? [] : spaces));
  const selectedIsDead = selectedIndex !== null && game.deadCards[selectedIndex];
  const selectedDetails = selectedCard ? sequenceCardDetails(selectedCard) : null;
  const teamScores = activeTeams.map((team) => {
    const players = current.players.filter((player) => game.teams[player.id] === team);
    return `<div class="sequence-team-score sequence-team-score--${team} ${game.viewerTeam === team ? "is-yours" : ""}"><span>${escapeHtml(sequenceTeamName(team))}</span><strong>${game.teamScores[team]}</strong><small>of ${game.targetSequences}</small><p>${players.map((player) => `${escapeHtml(player.name)}${player.id === myId ? " · YOU" : ""}`).join(" · ") || "Waiting for a player"}</p></div>`;
  }).join("");
  const board = game.board.map((card, index) => {
    const chip = game.chips[index];
    const isCorner = card === null;
    const isSelectedTarget = legalSpaces.has(index);
    const isLegal = game.canAct && isSelectedTarget;
    const isHandOption = allHandSpaces.has(index);
    const lastMove = game.lastMove?.boardIndex === index;
    const label = isCorner ? "Free corner" : `${sequenceCardDetails(card).rank} of ${sequenceCardDetails(card).suit}`;
    return `<button class="sequence-board-space ${isCorner ? "is-corner" : ""} ${chip ? `has-chip has-chip--${chip}` : ""} ${game.locked[index] ? "is-locked" : ""} ${isHandOption ? "has-hand-option" : ""} ${isSelectedTarget ? "is-preview" : ""} ${isLegal ? "is-legal" : ""} ${lastMove ? "is-last-move" : ""}" data-sequence-space="${index}" ${isLegal ? "" : "disabled"} aria-label="${escapeHtml(label)}${chip ? `, ${sequenceTeamName(chip)} chip${game.locked[index] ? ", protected" : ""}` : ""}${isHandOption ? ", available from your hand" : ""}${isLegal ? ", play here" : ""}">
      ${isCorner ? `<span class="sequence-free"><b>FREE</b><i>★</i></span>` : sequenceCardFace(card, true)}
      ${isHandOption ? `<span class="sequence-hand-option-marker" aria-hidden="true"></span>` : ""}
      ${isSelectedTarget ? `<span class="sequence-play-marker" aria-hidden="true">${selectedDetails?.oneEyed ? "−" : "+"}</span>` : ""}
      ${chip ? `<span class="sequence-chip sequence-chip--${chip}"><i></i>${game.locked[index] ? "<b>◆</b>" : ""}</span>` : ""}
    </button>`;
  }).join("");
  const hand = game.hand.map((card, index) => {
    const details = sequenceCardDetails(card);
    const dead = game.deadCards[index];
    const available = game.legalSpaces[index]?.length || 0;
    const description = details.twoEyed ? `${available} open spaces` : details.oneEyed ? available ? `${available} rival chip${available === 1 ? "" : "s"} removable` : "No unlocked rival chips yet" : dead ? "No open matching spaces" : `${available} open matches`;
    return `<button class="sequence-hand-card ${selectedIndex === index ? "is-selected" : ""} ${dead ? "is-dead" : ""}" data-sequence-hand="${index}" aria-pressed="${selectedIndex === index}">${sequenceCardFace(card, false, dead ? "DEAD CARD" : "")}<span>${escapeHtml(description)}</span></button>`;
  }).join("");
  const turnCopy = game.canAct
    ? selectedIsDead
      ? "That card is blocked. Exchange it free, then keep your turn."
      : selectedCard
        ? legalSpaces.size === 0
          ? selectedDetails?.oneEyed ? "No unlocked rival chips are available yet. Choose another card." : "That card has no available move. Choose another card."
          : selectedDetails?.oneEyed ? `Choose one of ${legalSpaces.size} marked rival chips to remove.` : selectedDetails?.twoEyed ? `Choose one of ${legalSpaces.size} marked open spaces.` : `Choose one of ${legalSpaces.size} marked matching spaces.`
        : "Choose a card from your hand to reveal its legal spaces."
    : selectedCard
      ? legalSpaces.size === 0
        ? `Preview: this card has no available move right now. ${game.currentPlayerName || "The next player"} is taking the current turn.`
        : selectedDetails?.oneEyed
          ? `Preview: this card can remove ${legalSpaces.size} marked rival chip${legalSpaces.size === 1 ? "" : "s"}. ${game.currentPlayerName || "The next player"} is taking the current turn.`
          : `Preview: this card can use ${legalSpaces.size} marked space${legalSpaces.size === 1 ? "" : "s"}. ${game.currentPlayerName || "The next player"} is taking the current turn.`
      : `Waiting for ${game.currentPlayerName || "the next player"} to play. You can still select any card to preview its spaces.`;
  const placementNotice = selectedCard ? `<p class="sequence-placement-notice ${game.canAct ? "" : "is-preview"} ${legalSpaces.size === 0 ? "has-no-moves" : ""}" role="status">${escapeHtml(turnCopy)}</p>` : "";
  const lastMove = game.lastMove ? `<p class="sequence-last-move"><span class="sequence-last-chip sequence-last-chip--${game.lastMove.team}"></span><strong>${escapeHtml(game.lastMove.playerName)}</strong> ${game.lastMove.action === "swap" ? "exchanged a dead card" : game.lastMove.action === "remove" ? "removed a rival chip" : "placed a chip"}${game.lastMove.sequencesMade ? ` and caught ${game.lastMove.sequencesMade} sequence${game.lastMove.sequencesMade === 1 ? "" : "s"}!` : "."}</p>` : "";

  app.innerHTML = pageShell(`
    ${sequenceTurnOrderHtml(game)}
    <section class="sequence-game-head">
      <div><span>TURN ${game.turnNumber}</span><strong>${escapeHtml(sequenceTeamName(game.viewerTeam)).toUpperCase()}</strong></div>
      <p class="sequence-current ${game.canAct ? "is-you" : ""}" role="status" aria-live="polite"><i></i>${game.canAct ? "YOUR TURN" : `${escapeHtml(game.currentPlayerName).toUpperCase()}’S TURN`}</p>
    </section>
    <section class="sequence-scoreboard sequence-scoreboard--${game.teamCount}">${teamScores}</section>
    ${lastMove}
    ${placementNotice}
    <section class="sequence-board-wrap"><div class="sequence-board" aria-label="Sequence board">${board}</div></section>
    <section class="sequence-hand-area">
      ${game.canAct ? `<div class="sequence-your-turn-banner" role="status"><strong>IT’S YOUR TURN</strong><span>Choose a card and make your move</span></div>` : ""}
      <div class="sequence-hand-head"><div><span>YOUR HAND</span><strong>${turnCopy}</strong></div><small>${game.deckRemaining} cards in the draw pile</small></div>
      <div class="sequence-hand">${hand}</div>
      ${game.canAct && selectedIsDead ? `<button id="sequence-swap-dead" class="sequence-swap-dead">Exchange dead card <b>↻</b></button>` : ""}
    </section>
    <aside class="sequence-legend"><span><i class="sequence-legend-chip sequence-legend-chip--${game.viewerTeam || "coral"}"></i>Your team</span><span><i class="sequence-legend-option"></i>Your possible moves</span><span><b>◆</b>Protected sequence chip</span><span><strong>J♥ J♦</strong>Place anywhere</span><span><strong>J♠ J♣</strong>Remove rival</span></aside>
  `, true);
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-sequence-hand]").forEach((button) => button.addEventListener("click", () => {
    selectedSequenceCardIndex = Number(button.dataset.sequenceHand);
    tap(); renderSequence();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-sequence-space]").forEach((button) => button.addEventListener("click", () => {
    if (selectedSequenceCardIndex === null) return;
    document.querySelectorAll<HTMLButtonElement>("[data-sequence-space], [data-sequence-hand]").forEach((candidate) => candidate.disabled = true);
    unlockAudio(); playTone(430, .07, "sine"); tap();
    send({ type: "sequence_play", handIndex: selectedSequenceCardIndex, boardIndex: Number(button.dataset.sequenceSpace) });
  }));
  document.querySelector<HTMLButtonElement>("#sequence-swap-dead")?.addEventListener("click", (event) => {
    if (selectedSequenceCardIndex === null) return;
    (event.currentTarget as HTMLButtonElement).disabled = true;
    tap(); send({ type: "sequence_swap_dead", handIndex: selectedSequenceCardIndex });
  });
}

function pokerCardHtml(card?: PokerCard, hidden = false) {
  if (!card || hidden) return `<span class="poker-card poker-card--back" aria-label="Hidden card">♠</span>`;
  const rank = card[0] === "T" ? "10" : card[0];
  const suit = ({ c: "♣", d: "♦", h: "♥", s: "♠" } as Record<string, string>)[card[1]] || "";
  const red = card[1] === "d" || card[1] === "h";
  return `<span class="poker-card ${red ? "poker-card--red" : ""}" aria-label="${escapeHtml(`${rank}${suit}`)}"><b>${escapeHtml(rank)}</b><i>${suit}</i></span>`;
}

function pokerActionText(action: PokerAction) {
  const labels: Record<string, string> = { small_blind: "posts small blind", big_blind: "posts big blind", fold: "folds", check: "checks", call: "calls", bet: "bets", raise: "raises", all_in: "is all-in" };
  const amount = action.type === "fold" || action.type === "check" ? "" : action.toAmount !== undefined && ["raise", "bet", "all_in"].includes(action.type) ? ` to ${money(action.toAmount)}` : ` ${money(action.amount)}`;
  return `${action.playerName} ${labels[action.type] || action.type}${amount}`;
}

function pokerHistoryHtml(history: PokerHistory[]) {
  return pokerHandHistoryHtml(history, { escapeHtml, money, cardHtml: pokerCardHtml, actionText: pokerActionText }, Boolean(room?.isAdmin && room.isSpectator && !room.isDisplay));
}

function renderPoker() {
  cleanupTimer();
  const current = room!;
  const game = current.poker!;
  const seatCount = Math.max(1, game.seatOrder.length);
  const seats = game.seatOrder.map((playerId, index) => {
    const player = current.players.find((candidate) => candidate.id === playerId);
    const status = game.statuses[playerId] || "out";
    const shown = game.result?.shownHands.find((hand) => hand.playerId === playerId);
    const angle = Math.PI / 2 + (index / seatCount) * Math.PI * 2;
    const seatX = 50 + Math.cos(angle) * 45;
    const seatY = 50 + Math.sin(angle) * 48;
    return `<li class="poker-seat ${game.currentPlayerId === playerId ? "is-turn" : ""} ${status === "folded" || status === "out" ? "is-out" : ""} ${playerId === myId ? "is-me" : ""}" style="--seat-x:${seatX.toFixed(2)}%;--seat-y:${seatY.toFixed(2)}%">
      <div class="poker-seat__badges">${index === game.dealerIndex ? '<b class="poker-dealer-button" aria-label="Dealer" title="Dealer">D</b>' : ""}${status === "all_in" ? "<em>ALL IN</em>" : status === "folded" ? "<em>FOLDED</em>" : ""}</div>
      <span class="avatar avatar--${index % 5}">${escapeHtml(initials(player?.name || "?"))}</span><strong>${escapeHtml(player?.name || "Player")}${playerId === myId ? " · YOU" : ""}</strong><b>${money(game.stacks[playerId] || 0)}</b>
      ${game.streetBets[playerId] ? `<small>${money(game.streetBets[playerId])} in front</small>` : ""}
      ${shown ? `<div class="poker-seat__shown">${shown.cards.map((card) => pokerCardHtml(card)).join("")}<small>${escapeHtml(shown.handLabel)}</small></div>` : ""}
    </li>`;
  }).join("");
  const board = Array.from({ length: 5 }, (_, index) => game.board[index] ? pokerCardHtml(game.board[index]) : `<span class="poker-card poker-card--empty"></span>`).join("");
  const recentActions = game.actions.slice(-8).reverse();
  let actionPanel = "";
  if (current.phase === "poker_result" && game.result) {
    actionPanel = `<section class="poker-result glass-card"><span>HAND #${game.handNumber} COMPLETE</span><h2>${game.result.awards.map((award) => `${escapeHtml(award.playerName)} wins ${money(award.amount)}`).join(" · ")}</h2><p>${game.result.awards.map((award) => escapeHtml(award.handLabel)).join(" · ")}</p>${current.isAdmin ? `<button id="poker-next-hand" class="primary-button"><span>Deal next hand</span><b>→</b></button>` : ""}<small>${pokerNextHandStatusHtml(current)}</small></section>`;
  } else if (game.canAct) {
    const minimumRaise = Math.min(game.minimumRaiseTo, game.maximumRaiseTo);
    const defaultRaise = Math.min(game.maximumRaiseTo, Math.max(minimumRaise, game.currentBet + game.bigBlind));
    const potAfterCall = game.pot + game.callAmount;
    const presetRaise = (fraction: number) => Math.min(game.maximumRaiseTo, Math.max(minimumRaise, game.currentBet + Math.round(potAfterCall * fraction)));
    actionPanel = `<section class="poker-controls glass-card"><span>YOUR ACTION</span><h2>${game.callAmount ? `${money(game.callAmount)} to call` : "You can check"}</h2><div class="poker-main-actions"><button data-poker-action="fold" class="poker-fold">Fold</button><button data-poker-action="${game.canCheck ? "check" : "call"}" class="poker-call">${game.canCheck ? "Check" : game.callAmount === (game.stacks[myId] || 0) ? `Call all-in ${money(game.callAmount)}` : `Call ${money(game.callAmount)}`}</button></div>
      ${game.canRaise ? `<form id="poker-raise-form">
        <div class="poker-bet-presets" aria-label="Quick bet sizes"><button type="button" data-poker-preset="${presetRaise(.25)}">¼ pot</button><button type="button" data-poker-preset="${presetRaise(.5)}">½ pot</button><button type="button" data-poker-preset="${presetRaise(1)}">Full pot</button></div>
        <label class="poker-raise-entry" for="poker-raise-to"><span>Raise total to</span><div><b>$</b><input id="poker-raise-to" type="number" step="0.01" min="${(minimumRaise / 100).toFixed(2)}" max="${(game.maximumRaiseTo / 100).toFixed(2)}" value="${(defaultRaise / 100).toFixed(2)}" placeholder="Enter amount" inputmode="decimal" autocomplete="off"></div></label>
        <div class="poker-bet-slider"><input id="poker-raise-slider" type="range" min="${minimumRaise}" max="${game.maximumRaiseTo}" step="1" value="${defaultRaise}" aria-label="Raise amount slider"><div><span>${money(minimumRaise)}</span><span>All in · ${money(game.maximumRaiseTo)}</span></div></div>
        <div class="poker-raise-actions"><button type="submit">${game.maximumRaiseTo < game.minimumRaiseTo ? "All in" : "Raise"}</button><button type="button" id="poker-all-in">All in ${money(game.maximumRaiseTo)}</button></div>
      </form>` : ""}</section>`;
  } else {
    const status = game.statuses[myId];
    actionPanel = `<section class="poker-wait glass-card"><span>${status === "folded" ? "HAND FOLDED" : status === "all_in" ? "ALL IN" : status === "out" ? "WATCHING" : `${escapeHtml(game.currentPlayerName || "Table")} TO ACT`}</span><h2>${status === "folded" ? "Watch the hand play out." : status === "all_in" ? "The cards decide it now." : status === "out" ? "You’ll be seated when a new game starts." : `Waiting on ${escapeHtml(game.currentPlayerName)}.`}</h2></section>`;
  }
  app.innerHTML = pageShell(`
    <section class="poker-game-head"><div><span>ROOM ${escapeHtml(current.roomCode)}</span><strong>HAND #${game.handNumber} · ${escapeHtml(game.street.toUpperCase())}</strong></div><p>BLINDS ${money(game.smallBlind)} / ${money(game.bigBlind)}</p></section>
    <section class="poker-table" aria-label="Poker table"><div class="poker-pot"><span>POT</span><strong>${money(game.pot)}</strong></div><div class="poker-board">${board}</div><ol class="poker-seats">${seats}</ol></section>
    <section class="poker-pocket"><div><span>YOUR HAND</span><strong>${money(game.stacks[myId] || 0)} behind</strong></div><div>${game.holeCards.length ? game.holeCards.map((card) => pokerCardHtml(card)).join("") : `${pokerCardHtml(undefined, true)}${pokerCardHtml(undefined, true)}`}</div></section>
    ${actionPanel}
    <section class="poker-live-log"><div class="section-title"><h2>Table action</h2><span>Latest first</span></div>${recentActions.length ? `<ol>${recentActions.map((action) => `<li><span>${escapeHtml(action.street.toUpperCase())}</span><strong>${escapeHtml(pokerActionText(action))}</strong></li>`).join("")}</ol>` : "<p>Waiting for the first action.</p>"}</section>
    ${pokerHistoryHtml(game.history)}
  `, true);
  bindCommon();
  if (current.phase === "poker_result" && current.deadline) startTimer(7_000);
  document.querySelectorAll<HTMLButtonElement>("[data-poker-action]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-poker-action]").forEach((candidate) => candidate.disabled = true);
    tap(); send({ type: "poker_action", action: button.dataset.pokerAction });
  }));
  const submitRaise = (raiseTo: number) => {
    if (!Number.isSafeInteger(raiseTo) || raiseTo <= game.currentBet || raiseTo > game.maximumRaiseTo || (raiseTo < game.minimumRaiseTo && raiseTo !== game.maximumRaiseTo)) return showToast(`Raise to at least ${money(Math.min(game.minimumRaiseTo, game.maximumRaiseTo))}.`);
    document.querySelectorAll<HTMLButtonElement>("#poker-raise-form button").forEach((button) => button.disabled = true);
    tap(); send({ type: "poker_action", action: "raise", raiseTo });
  };
  document.querySelector<HTMLFormElement>("#poker-raise-form")?.addEventListener("submit", (event) => {
    event.preventDefault(); submitRaise(Math.round(Number(document.querySelector<HTMLInputElement>("#poker-raise-to")!.value) * 100));
  });
  const raiseInput = document.querySelector<HTMLInputElement>("#poker-raise-to");
  const raiseSlider = document.querySelector<HTMLInputElement>("#poker-raise-slider");
  const setRaiseAmount = (amount: number) => {
    if (!raiseInput || !raiseSlider) return;
    const legalAmount = Math.min(game.maximumRaiseTo, Math.max(Math.min(game.minimumRaiseTo, game.maximumRaiseTo), amount));
    raiseInput.value = (legalAmount / 100).toFixed(2);
    raiseSlider.value = String(legalAmount);
  };
  raiseInput?.addEventListener("focus", () => { raiseInput.value = ""; });
  raiseInput?.addEventListener("input", () => {
    const cents = Math.round(Number(raiseInput.value) * 100);
    if (Number.isSafeInteger(cents) && cents >= Number(raiseSlider?.min) && cents <= Number(raiseSlider?.max) && raiseSlider) raiseSlider.value = String(cents);
  });
  raiseSlider?.addEventListener("input", () => setRaiseAmount(Number(raiseSlider.value)));
  document.querySelectorAll<HTMLButtonElement>("[data-poker-preset]").forEach((button) => button.addEventListener("click", () => {
    setRaiseAmount(Number(button.dataset.pokerPreset)); tap();
  }));
  document.querySelector<HTMLButtonElement>("#poker-all-in")?.addEventListener("click", () => submitRaise(game.maximumRaiseTo));
  document.querySelector<HTMLButtonElement>("#poker-next-hand")?.addEventListener("click", (event) => { (event.currentTarget as HTMLButtonElement).disabled = true; tap(); send({ type: "poker_next_hand" }); });
}

function pokerNextHandStatusHtml(current: RoomState) {
  if (current.deadline !== null) {
    const tableComplete = current.poker!.seatOrder.filter((id) => (current.poker!.stacks[id] || 0) > 0).length < 2;
    return `${tableComplete ? "Final results" : "Next hand deals automatically"} in <span id="poker-next-hand-countdown">${Math.max(0, Math.ceil((current.deadline - Date.now()) / 1_000))}</span> seconds.`;
  }
  if (current.spectators?.some((player) => player.isAdmin)) return "Waiting for two players with chips to reconnect. The next hand will deal automatically.";
  return "Waiting for the group admin to deal the next hand.";
}

function renderBlackjack() {
  const current = room!;
  const game = current.blackjack!;
  const currentBaseBet = game.bets[myId] || 0;
  if (currentBaseBet >= 100) localStorage.setItem("quiz-spark-blackjack-last-bet", String(currentBaseBet));
  const storedLastBet = Number(localStorage.getItem("quiz-spark-blackjack-last-bet"));
  const lastBet = currentBaseBet || (Number.isSafeInteger(storedLastBet) && storedLastBet >= 100 ? storedLastBet : 0);
  const myCurrentStack = game.stacks[myId] || 0;
  const autoBetKey = `${current.roomCode}:${game.roundNumber}`;
  const canPlaceAutoBet = game.canBet && lastBet > 0 && lastBet <= myCurrentStack;
  const autoBetStatus = !lastBet
    ? "Place one bet first to set the repeat amount."
    : game.canBet && lastBet > myCurrentStack
      ? `You need ${money(lastBet)}. Place a smaller bet to update it.`
      : blackjackAutoBetEnabled
        ? `${money(lastBet)} will be placed automatically whenever betting opens.`
        : `Uses your last wager of ${money(lastBet)}.`;
  const autoBetControl = `<label class="blackjack-autobet glass-card ${blackjackAutoBetEnabled ? "is-on" : ""} ${lastBet ? "" : "is-disabled"}" for="blackjack-autobet"><input id="blackjack-autobet" type="checkbox" ${blackjackAutoBetEnabled ? "checked" : ""} ${lastBet ? "" : "disabled"}><span><strong>Auto-bet last amount${lastBet ? ` · ${money(lastBet)}` : ""}</strong><small>${autoBetStatus}</small></span></label>`;
  const countLabel = game.cardCount === null ? "—" : game.cardCount > 0 ? `+${game.cardCount}` : String(game.cardCount);
  const countControl = `<${current.isAdmin ? "button" : "div"} class="blackjack-count-display ${game.showCardCount ? "" : "is-hidden"}" ${current.isAdmin ? `id="blackjack-count-toggle" type="button" aria-pressed="${game.showCardCount}"` : ""}><strong>${countLabel}</strong><span>${game.showCardCount ? "Hi-Lo count" : "count hidden"}</span><small>${current.isAdmin ? `tap to ${game.showCardCount ? "hide" : "show"}` : "set by admin"}</small></${current.isAdmin ? "button" : "div"}>`;
  const dealerCards = game.dealerHand.map((card) => card ? pokerCardHtml(card) : pokerCardHtml(undefined, true)).join("");
  const playerSeats = [...game.playerOrder].reverse().map((playerId, index) => {
    const player = current.players.find((candidate) => candidate.id === playerId);
    const statuses = game.statuses[playerId] || [];
    const playerResults = game.results.filter((candidate) => candidate.playerId === playerId);
    const insuranceNet = (game.insurancePayouts[playerId] || 0) - (game.insuranceBets[playerId] || 0);
    const totalNet = playerResults.reduce((total, result) => total + result.net, 0) + insuranceNet;
    const netLabel = playerResults.length ? `${totalNet > 0 ? "+" : totalNet < 0 ? "−" : ""}${money(Math.abs(totalNet))}` : "";
    const statusLabel = current.phase === "blackjack_betting"
      ? game.hasBet[playerId] ? `BET ${money(game.bets[playerId])}` : "BETTING"
      : current.phase === "blackjack_insurance" ? game.insuranceDecisions[playerId] ? (game.insuranceBets[playerId] ? `INSURED ${money(game.insuranceBets[playerId])}` : "NO INSURANCE") : "INSURANCE?"
        : playerResults.length ? `${playerResults.length > 1 ? `${playerResults.length} HANDS ` : ""}${netLabel}`
          : game.currentPlayerId === playerId ? `HAND ${game.currentHandIndex + 1} PLAYING` : statuses.includes("blackjack") ? "BLACKJACK" : statuses.every((status) => status !== "playing") ? "STANDING" : "WAITING";
    const hands = game.hands[playerId] || [];
    const handCards = hands.length ? hands.map((cards, handIndex) => `<div class="blackjack-seat__hand ${cards.length > 4 ? "is-stacked" : ""} ${game.currentPlayerId === playerId && game.currentHandIndex === handIndex ? "is-active" : ""}">${cards.map((card) => pokerCardHtml(card)).join("")}<em>H${handIndex + 1} · ${game.totals[playerId]?.[handIndex] || 0} · ${money(game.handBets[playerId]?.[handIndex] || 0)}</em></div>`).join("") : `<div class="blackjack-seat__hand"><span class="blackjack-card-slot"></span><span class="blackjack-card-slot"></span></div>`;
    return `<li class="blackjack-seat ${playerId === myId ? "is-me" : ""} ${game.currentPlayerId === playerId ? "is-turn" : ""} ${playerResults.length && totalNet > 0 ? "is-win" : playerResults.length && totalNet < 0 ? "is-loss" : ""}">
      <div class="blackjack-seat__cards">${handCards}</div>
      <div class="blackjack-seat__score">${game.totals[playerId]?.length ? `<strong>${game.totals[playerId].join(" / ")}</strong>` : ""}<span>${statusLabel}</span></div>
      <span class="avatar avatar--${index % 5}">${escapeHtml(initials(player?.name || "?"))}</span>
      <strong class="blackjack-seat__name">${escapeHtml(player?.name || "Player")}${playerId === myId ? " · YOU" : ""}</strong>
      <small>${money(game.stacks[playerId] || 0)}${game.handBets[playerId]?.length ? ` behind · ${money(game.handBets[playerId].reduce((sum, bet) => sum + bet, 0))} out` : ""}</small>
    </li>`;
  }).join("");
  let actionPanel = "";
  if (current.phase === "blackjack_betting") {
    const myStack = game.stacks[myId] || 0;
    const suggestedBet = Math.min(game.defaultBet, myStack);
    if (game.canBet) {
      actionPanel = `<section class="blackjack-action blackjack-betting glass-card"><span>PLACE YOUR BET</span><h2>${money(myStack)} available</h2><form id="blackjack-bet-form"><label for="blackjack-bet"><span>$</span><input id="blackjack-bet" type="number" min="1" max="${(myStack / 100).toFixed(0)}" step="1" value="${(suggestedBet / 100).toFixed(0)}" inputmode="decimal" autocomplete="off"></label><button type="submit">Bet ${money(suggestedBet)}</button></form><p>Default ${money(game.defaultBet)} · choose any whole-dollar amount up to your bankroll.</p></section>`;
    } else {
      actionPanel = `<section class="blackjack-action blackjack-wait glass-card"><span>${game.hasBet[myId] ? "BET LOCKED" : "TABLE BETTING"}</span><h2>${game.hasBet[myId] ? `Your ${money(game.bets[myId])} bet is down.` : "Waiting for active players."}</h2><p>${game.bettingCount} of ${game.eligibleCount} bets placed</p></section>`;
    }
  } else if (current.phase === "blackjack_insurance") {
    if (game.canInsure) {
      actionPanel = `<section class="blackjack-action blackjack-insurance glass-card"><span>DEALER SHOWS AN ACE</span><h2>Insurance?</h2><p>${game.insuranceOffer ? `Pay ${money(game.insuranceOffer)} now. It pays 2:1 if the dealer has blackjack.` : "Your full bankroll is already in play, so insurance is unavailable."}</p><div><button data-blackjack-insurance="false" class="blackjack-no-insurance">No insurance</button><button data-blackjack-insurance="true" class="blackjack-take-insurance" ${game.insuranceOffer ? "" : "disabled"}>${game.insuranceOffer ? `Insure ${money(game.insuranceOffer)}` : "No funds"}</button></div></section>`;
    } else {
      actionPanel = `<section class="blackjack-action blackjack-wait glass-card"><span>INSURANCE DECISION LOCKED</span><h2>${game.insuranceBets[myId] ? `${money(game.insuranceBets[myId])} insurance is down.` : "No insurance."}</h2><p>Waiting for the rest of the table.</p></section>`;
    }
  } else if (current.phase === "blackjack_result") {
    const myResults = game.results.filter((result) => result.playerId === myId);
    const insuranceNet = (game.insurancePayouts[myId] || 0) - (game.insuranceBets[myId] || 0);
    const totalNet = myResults.reduce((total, result) => total + result.net, 0) + insuranceNet;
    const headline = totalNet > 0 ? `You win ${money(totalNet)}.` : totalNet < 0 ? `Dealer wins ${money(Math.abs(totalNet))}.` : "You break even.";
    const nextRoundControl = current.isAdmin
      ? `<button id="blackjack-next-round" class="primary-button">Open betting</button><small>Players will be asked for round ${game.roundNumber + 1} bets.</small>`
      : `<small>Waiting for the admin to open betting for round ${game.roundNumber + 1}.</small>`;
    actionPanel = `<section class="blackjack-action blackjack-result glass-card"><span>ROUND ${game.roundNumber} COMPLETE</span><h2>${headline}</h2><p>Dealer finished on ${game.dealerTotal}. ${myResults.length > 1 ? `${myResults.length} split hands settled. ` : ""}${game.insuranceBets[myId] ? `Insurance ${game.insurancePayouts[myId] ? `paid ${money(game.insurancePayouts[myId])}` : "lost"}. ` : ""}Naturals paid 3:2.</p>${nextRoundControl}</section>`;
  } else if (game.canAct) {
    const handIndex = game.currentHandIndex;
    actionPanel = `<section class="blackjack-action glass-card"><span>YOUR MOVE · HAND ${handIndex + 1} · ${money(game.handBets[myId]?.[handIndex] || 0)} BET</span><h2>${game.totals[myId]?.[handIndex]} — choose your play</h2><div class="blackjack-play-actions"><button data-blackjack-action="stand" class="blackjack-stand">Stand</button><button data-blackjack-action="hit" class="blackjack-hit">Hit</button>${game.canDouble ? `<button data-blackjack-action="double" class="blackjack-double">Double</button>` : ""}${game.canSplit ? `<button data-blackjack-action="split" class="blackjack-split">Split pair</button>` : ""}</div></section>`;
  } else {
    const myStatuses = game.statuses[myId] || [];
    const label = myStatuses.includes("blackjack") ? "Natural 21 — nice." : myStatuses.length && myStatuses.every((status) => status === "busted") ? "Your hand is busted." : myStatuses.length && myStatuses.every((status) => status !== "playing") ? "Your hand is set." : `Waiting on ${game.currentPlayerName}.`;
    actionPanel = `<section class="blackjack-action blackjack-wait glass-card"><span>${myStatuses.includes("blackjack") ? "BLACKJACK" : myStatuses.length && myStatuses.every((status) => status !== "playing") ? "STANDING" : `${escapeHtml(game.currentPlayerName || "Dealer")} TO ACT`}</span><h2>${label}</h2></section>`;
  }
  app.innerHTML = pageShell(`
    <section class="blackjack-game-head"><div><span>ROOM ${escapeHtml(current.roomCode)}</span><strong>ROUND ${game.roundNumber} · BLACKJACK PAYS 3:2</strong></div><div class="blackjack-table-status"><div class="blackjack-deck-count ${game.reshuffledThisRound ? "is-fresh" : ""}"><strong>${game.deckRemaining}</strong><span>cards left</span><small>${game.reshuffledThisRound ? "RESHUFFLED AT 7" : `SHUFFLE #${game.shuffleCount}`}</small></div>${countControl}</div></section>
    <section class="blackjack-table">
      <div class="blackjack-dealer"><span>DEALER</span><div>${dealerCards || `${pokerCardHtml(undefined, true)}${pokerCardHtml(undefined, true)}`}</div><strong>${current.phase === "blackjack_result" ? game.dealerTotal : current.phase === "blackjack_playing" ? `${game.dealerTotal} + ?` : "BETS OPEN"}</strong></div>
      <div class="blackjack-table-mark" aria-hidden="true">BLACKJACK PAYS 3 TO 2 · DEALER STANDS ON 17 · PLAY MOVES RIGHT TO LEFT</div>
      <ol class="blackjack-seats">${playerSeats}</ol>
    </section>
    ${actionPanel}
    ${autoBetControl}
  `, true);
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-blackjack-action]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-blackjack-action]").forEach((candidate) => candidate.disabled = true);
    const actionTones: Record<string, number> = { stand: 280, hit: 430, double: 540, split: 620 };
    unlockAudio(); playTone(actionTones[button.dataset.blackjackAction || ""] || 430, .07, "sine"); tap();
    send({ type: "blackjack_action", action: button.dataset.blackjackAction });
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-blackjack-insurance]").forEach((button) => button.addEventListener("click", () => {
    document.querySelectorAll<HTMLButtonElement>("[data-blackjack-insurance]").forEach((candidate) => candidate.disabled = true);
    const takeInsurance = button.dataset.blackjackInsurance === "true";
    unlockAudio(); playTone(takeInsurance ? 520 : 260, .07, "sine"); tap();
    send({ type: "blackjack_insurance", takeInsurance });
  }));
  document.querySelector<HTMLButtonElement>("#blackjack-count-toggle")?.addEventListener("click", () => {
    unlockAudio(); playTone(game.showCardCount ? 250 : 500, .06, "sine"); tap();
    send({ type: "blackjack_count_visibility", showCardCount: !game.showCardCount });
  });
  document.querySelector<HTMLButtonElement>("#blackjack-next-round")?.addEventListener("click", (event) => {
    (event.currentTarget as HTMLButtonElement).disabled = true;
    unlockAudio(); playTone(520, .08, "sine"); tap(); send({ type: "blackjack_next_round" });
  });
  const betInput = document.querySelector<HTMLInputElement>("#blackjack-bet");
  const betButton = document.querySelector<HTMLButtonElement>("#blackjack-bet-form button");
  const submitAutoBet = () => {
    if (!blackjackAutoBetEnabled || !canPlaceAutoBet || blackjackAutoBetSentKey === autoBetKey || socket?.readyState !== WebSocket.OPEN) return;
    blackjackAutoBetSentKey = autoBetKey;
    if (betInput) betInput.disabled = true;
    if (betButton) betButton.disabled = true;
    unlockAudio(); playTone(390, .07, "sine"); tap();
    send({ type: "blackjack_bet", bet: lastBet });
  };
  document.querySelector<HTMLInputElement>("#blackjack-autobet")?.addEventListener("change", (event) => {
    blackjackAutoBetEnabled = (event.currentTarget as HTMLInputElement).checked;
    localStorage.setItem("quiz-spark-blackjack-auto-bet", blackjackAutoBetEnabled ? "on" : "off");
    document.querySelector(".blackjack-autobet")?.classList.toggle("is-on", blackjackAutoBetEnabled);
    unlockAudio(); playTone(blackjackAutoBetEnabled ? 520 : 260, .06, "sine"); tap();
    if (blackjackAutoBetEnabled) submitAutoBet();
  });
  betInput?.addEventListener("input", () => {
    const bet = Math.round(Number(betInput.value) * 100);
    if (betButton && Number.isSafeInteger(bet) && bet >= 100) betButton.textContent = `Bet ${money(bet)}`;
  });
  document.querySelector<HTMLFormElement>("#blackjack-bet-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const bet = Math.round(Number(betInput?.value) * 100);
    if (!Number.isSafeInteger(bet) || bet < 100 || bet % 100 !== 0 || bet > (game.stacks[myId] || 0)) return showToast(`Choose a whole-dollar bet from $1 to ${money(game.stacks[myId] || 0)}.`);
    if (betButton) betButton.disabled = true;
    localStorage.setItem("quiz-spark-blackjack-last-bet", String(bet));
    blackjackAutoBetSentKey = autoBetKey;
    unlockAudio(); playTone(390, .07, "sine"); tap(); send({ type: "blackjack_bet", bet });
  });
  if (blackjackAutoBetEnabled && canPlaceAutoBet) window.setTimeout(submitAutoBet, 0);
}

function monopolyActionPanel(card: MonopolyCard, game: MonopolyView) {
  const opponents = game.playerOrder.filter((playerId) => playerId !== myId);
  const opponentOptions = opponents.map((playerId) => `<option value="${playerId}">${escapeHtml(room!.players.find((player) => player.id === playerId)?.name || "Player")}</option>`).join("");
  const ownedColors = new Set((game.boards[myId]?.properties || []).map((propertyCard) => propertyCard.color));
  const rentColors = (card.colors || game.colors).filter((color) => ownedColors.has(color));
  const colorOptions = rentColors.map((color) => `<option value="${color}">${escapeHtml(monopolyColorLabels[color])}</option>`).join("");
  const completeOwn = game.boards[myId]?.completeColors || [];
  const ownUpgrades = game.boards[myId]?.improvements || [];
  const buildColors = completeOwn.filter((color) => !["railroad", "utility"].includes(color)).filter((color) => card.action === "house" ? !ownUpgrades.some((upgrade) => upgrade.color === color && upgrade.card.action === "house") : ownUpgrades.some((upgrade) => upgrade.color === color && upgrade.card.action === "house") && !ownUpgrades.some((upgrade) => upgrade.color === color && upgrade.card.action === "hotel"));
  const targetProperties = opponents.flatMap((playerId) => (game.boards[playerId]?.properties || []).filter((propertyCard) => !game.boards[playerId].completeColors.includes(propertyCard.color)).map((propertyCard) => ({ playerId, ...propertyCard })));
  const ownTradeable = (game.boards[myId]?.properties || []).filter((propertyCard) => !game.boards[myId].completeColors.includes(propertyCard.color));
  const completeTargets = opponents.flatMap((playerId) => (game.boards[playerId]?.completeColors || []).map((color) => ({ playerId, color })));
  const doubleRents = game.hand.filter((candidate) => candidate.action === "double_rent" && candidate.id !== card.id).slice(0, Math.max(0, 2 - game.playsThisTurn));
  const action = card.action;
  let controls = "";
  if (action === "pass_go" || action === "birthday") controls = `<p>${escapeHtml(monopolyActionCopy(card))}</p>`;
  else if (action === "house" || action === "hotel") controls = `<label>Build on<select name="color" required>${buildColors.map((color) => `<option value="${color}">${escapeHtml(monopolyColorLabels[color])}</option>`).join("")}</select></label>`;
  else if (action === "debt_collector") controls = `<label>Charge<select name="targetPlayerId" required>${opponentOptions}</select></label>`;
  else if (action === "rent") controls = `<label>Property color<select name="color" required>${colorOptions}</select></label>${card.key === "rent_any" ? `<label>Charge<select name="targetPlayerId" required>${opponentOptions}</select></label>` : `<p>Every opponent pays the rent for the chosen color.</p>`}${doubleRents.length ? `<fieldset><legend>Boost it (each card counts as a play)</legend>${doubleRents.map((doubleCard) => `<label class="monopoly-check"><input type="checkbox" name="doubleRent" value="${doubleCard.id}"><span>Double the Rent ×2</span></label>`).join("")}</fieldset>` : ""}`;
  else if (action === "sly_deal") controls = `<label>Steal<select name="targetProperty" required>${targetProperties.map((propertyCard) => `<option value="${propertyCard.playerId}|${propertyCard.cardId}">${escapeHtml(room!.players.find((player) => player.id === propertyCard.playerId)?.name || "Player")} · ${escapeHtml(propertyCard.card.name)}</option>`).join("")}</select></label>`;
  else if (action === "forced_deal") controls = `<label>Take<select name="targetProperty" required>${targetProperties.map((propertyCard) => `<option value="${propertyCard.playerId}|${propertyCard.cardId}">${escapeHtml(room!.players.find((player) => player.id === propertyCard.playerId)?.name || "Player")} · ${escapeHtml(propertyCard.card.name)}</option>`).join("")}</select></label><label>Give<select name="offeredCardId" required>${ownTradeable.map((propertyCard) => `<option value="${propertyCard.cardId}">${escapeHtml(propertyCard.card.name)}</option>`).join("")}</select></label>`;
  else if (action === "deal_breaker") controls = `<label>Steal complete set<select name="completeTarget" required>${completeTargets.map((target) => `<option value="${target.playerId}|${target.color}">${escapeHtml(room!.players.find((player) => player.id === target.playerId)?.name || "Player")} · ${escapeHtml(monopolyColorLabels[target.color])}</option>`).join("")}</select></label>`;
  const unavailable = (action === "house" || action === "hotel") && !buildColors.length || action === "rent" && !rentColors.length || action === "sly_deal" && !targetProperties.length || action === "forced_deal" && (!targetProperties.length || !ownTradeable.length) || action === "deal_breaker" && !completeTargets.length;
  return `<form class="monopoly-action-builder" id="monopoly-action-form" data-card-id="${card.id}"><header><span>ACTION SELECTED</span><strong>${escapeHtml(card.name)}</strong><button type="button" id="monopoly-cancel-action">×</button></header>${controls}<button class="monopoly-play-action" type="submit" ${unavailable ? "disabled" : ""}>${unavailable ? "No legal target" : `Play ${escapeHtml(card.name)}`}</button></form>`;
}

function monopolyPropertyPanel(card: MonopolyCard) {
  return `<form class="monopoly-action-builder" id="monopoly-property-form" data-card-id="${card.id}"><header><span>PROPERTY SELECTED</span><strong>${escapeHtml(card.name)}</strong><button type="button" id="monopoly-cancel-action">×</button></header><label>Play as<select name="color" required>${(card.colors || []).map((color) => `<option value="${color}">${escapeHtml(monopolyColorLabels[color])}</option>`).join("")}</select></label><button class="monopoly-play-action" type="submit">Add property</button></form>`;
}

function renderMonopolyDeal() {
  cleanupTimer();
  const current = room!;
  const game = current.monopolyDeal!;
  if (!game.hand.some((card) => card.id === selectedMonopolyActionCardId)) selectedMonopolyActionCardId = null;
  if (!game.hand.some((card) => card.id === previewMonopolyCardId)) previewMonopolyCardId = null;
  const selectedCard = game.hand.find((card) => card.id === selectedMonopolyActionCardId);
  const previewCard = game.hand.find((card) => card.id === previewMonopolyCardId);
  const isMyTurn = game.currentPlayerId === myId;
  const payment = game.pendingPayment;
  const pending = game.pendingAction;
  const meResponding = pending?.responderId === myId && !payment;
  const noCard = game.hand.find((card) => card.action === "just_say_no");
  const debtorBoard = payment?.debtorId ? game.boards[payment.debtorId] : null;
  const paymentAssets = payment?.debtorId === myId && debtorBoard ? [
    ...debtorBoard.bank.map((card) => ({ card, zone: "Bank" })),
    ...debtorBoard.properties.filter((item) => item.card.value > 0).map((item) => ({ card: item.card, zone: monopolyColorLabels[item.color] })),
    ...debtorBoard.improvements.map((item) => ({ card: item.card, zone: monopolyColorLabels[item.color] })),
  ] : [];
  const paymentAvailableTotal = paymentAssets.reduce((total, asset) => total + asset.card.value, 0);
  const interrupt = payment ? (payment.debtorId === myId ? `<form class="monopoly-interrupt monopoly-payment" id="monopoly-payment-form"><span>PAYMENT DUE</span><h2>Choose at least ${monopolyMoney(payment.amount)} from your table</h2><p>There is no change. Cards in your hand cannot be used.</p><div class="monopoly-payment-summary"><span>Selected <b id="monopoly-payment-selected">$0M</b></span><span>Owed <b>${monopolyMoney(payment.amount)}</b></span><span>Available <b>${monopolyMoney(paymentAvailableTotal)}</b></span><strong id="monopoly-payment-guidance">Select assets to cover the payment.</strong></div><div>${paymentAssets.map(({ card, zone }) => `<label><input type="checkbox" name="paymentCard" value="${card.id}" data-value="${card.value}"><span>${escapeHtml(card.name)}<small>${escapeHtml(zone)}</small></span><b>${monopolyMoney(card.value)}</b></label>`).join("")}</div><button type="submit" id="monopoly-payment-submit" disabled>Pay selected assets</button></form>` : `<section class="monopoly-interrupt"><span>PAYMENT IN PROGRESS</span><h2>${escapeHtml(current.players.find((player) => player.id === payment.debtorId)?.name || "Player")} is choosing ${monopolyMoney(payment.amount)} in assets</h2></section>`) : pending ? (meResponding ? `<section class="monopoly-interrupt monopoly-reaction"><span>${pending.noCount ? "NO CARD PLAYED" : "ACTION AGAINST YOU"}</span><h2>${pending.noCount % 2 ? "Let the cancellation stand?" : `Accept ${pending.kind.replace(/_/g, " ")}?`}</h2><div><button data-monopoly-react="accept" type="button">${pending.noCount % 2 ? "Accept cancellation" : "Accept action"}</button>${noCard ? `<button data-monopoly-react="no" class="monopoly-no" type="button">Just Say No!</button>` : ""}</div></section>` : `<section class="monopoly-interrupt"><span>ACTION PENDING</span><h2>Waiting for ${escapeHtml(current.players.find((player) => player.id === pending.responderId)?.name || "Player")}</h2><p>They may accept or play Just Say No.</p></section>`) : "";
  const renderHandCard = (card: MonopolyCard) => {
    const selected = card.id === selectedMonopolyActionCardId;
    const playable = game.canAct && game.playsThisTurn < 3;
    const directProperty = ["property", "wild"].includes(card.kind);
    const canBank = !directProperty && card.value > 0;
    const canUse = ["action", "rent", "improvement"].includes(card.kind) && !["just_say_no", "double_rent"].includes(card.action || "");
    return `<div class="monopoly-hand-card ${selected ? "is-selected" : ""}">${monopolyCardHtml(card, false, true)}<div class="monopoly-card-actions">${directProperty ? `<button data-monopoly-select="${card.id}" ${playable ? "" : "disabled"}>Play</button>` : `${canUse ? `<button data-monopoly-select="${card.id}" ${playable ? "" : "disabled"}>Use</button>` : ""}${canBank ? `<button data-monopoly-bank="${card.id}" ${playable ? "" : "disabled"}>Bank ${monopolyMoney(card.value)}</button>` : ""}`}${isMyTurn && game.hand.length > 7 && !pending && !payment ? `<button data-monopoly-discard="${card.id}" class="monopoly-discard">Discard</button>` : ""}</div></div>`;
  };
  const sortedHand = sortedMonopolyHand(game.hand);
  const handGroups = monopolyHandGroups.map((group) => {
    const cards = sortedHand.filter((card) => group.kinds.includes(card.kind));
    return cards.length ? `<section class="monopoly-hand-group"><header><span>${group.label}</span><b>${cards.length}</b></header><div class="monopoly-hand-group__cards">${cards.map(renderHandCard).join("")}</div></section>` : "";
  }).join("");
  app.innerHTML = pageShell(`
    <section class="monopoly-game-head"><div><span>MONOPOLY DEAL · ROOM ${escapeHtml(current.roomCode)}</span><strong>${isMyTurn ? "YOUR TURN" : `${escapeHtml(game.currentPlayerName)}’S TURN`}</strong></div><div><span>PLAYS</span><strong>${game.playsThisTurn} / 3</strong><small>${game.deckRemaining} in draw pile</small></div></section>
    <section class="monopoly-status"><i></i><p>${escapeHtml(game.lastAction || "Build three complete property sets to win.")}</p></section>
    <div class="monopoly-info-row">${monopolyHistoryHtml(game.log || [], monopolyHistoryOpen)}${monopolyRulesHtml(monopolyRulesOpen)}</div>
    ${interrupt}
    <section class="monopoly-table"><div class="monopoly-table__boards">${game.playerOrder.map((playerId) => monopolyBoardHtml(playerId, game, true)).join("")}</div></section>
    <section class="monopoly-hand"><header><div><span>YOUR HAND</span><strong>${game.hand.length} card${game.hand.length === 1 ? "" : "s"}</strong></div><p>${game.hand.length > 7 && isMyTurn ? `Discard ${game.hand.length - 7} before ending your turn.` : game.canAct ? "Tap a card to enlarge it. Play up to three cards—or end now." : "Tap a card to enlarge it. Your cards stay private."}</p></header><div class="monopoly-hand__groups">${handGroups || "<p class=\"monopoly-empty-hand\">Your hand is empty. You’ll draw five at the start of your next turn.</p>"}</div>${selectedCard && game.canAct ? ["property", "wild"].includes(selectedCard.kind) ? monopolyPropertyPanel(selectedCard) : monopolyActionPanel(selectedCard, game) : ""}</section>
    ${game.canAct ? `<div class="monopoly-turn-controls"><button id="monopoly-end-turn" ${game.hand.length > 7 ? "disabled" : ""}>End turn <span>→</span></button><small>${game.playsThisTurn === 3 ? "All three plays used" : `${3 - game.playsThisTurn} play${3 - game.playsThisTurn === 1 ? "" : "s"} remaining`}</small></div>` : ""}
    ${previewCard ? monopolyCardPreviewHtml(previewCard) : ""}
  `, true);
  bindCommon();
  document.querySelector<HTMLDetailsElement>(".monopoly-history")?.addEventListener("toggle", (event) => { monopolyHistoryOpen = (event.currentTarget as HTMLDetailsElement).open; });
  document.querySelector<HTMLDetailsElement>(".monopoly-rulebook")?.addEventListener("toggle", (event) => { monopolyRulesOpen = (event.currentTarget as HTMLDetailsElement).open; });
  const openMonopolyPreview = (cardId: string | undefined) => { if (!cardId) return; previewMonopolyCardId = cardId; renderMonopolyDeal(); };
  document.querySelectorAll<HTMLElement>("[data-monopoly-preview]").forEach((card) => {
    card.addEventListener("click", () => openMonopolyPreview(card.dataset.monopolyPreview));
    card.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openMonopolyPreview(card.dataset.monopolyPreview); } });
  });
  const closeMonopolyPreview = () => { previewMonopolyCardId = null; renderMonopolyDeal(); };
  const previewCloseButton = document.querySelector<HTMLButtonElement>("#monopoly-preview-close");
  const previewOverlay = document.querySelector<HTMLElement>("#monopoly-preview-overlay");
  previewCloseButton?.addEventListener("click", closeMonopolyPreview);
  previewOverlay?.addEventListener("click", (event) => { if (event.target === event.currentTarget) closeMonopolyPreview(); });
  previewOverlay?.addEventListener("keydown", (event) => { if (event.key === "Escape") closeMonopolyPreview(); });
  previewCloseButton?.focus();
  document.querySelectorAll<HTMLButtonElement>("[data-monopoly-select]").forEach((button) => button.addEventListener("click", () => { selectedMonopolyActionCardId = button.dataset.monopolySelect || null; renderMonopolyDeal(); }));
  document.querySelector("#monopoly-cancel-action")?.addEventListener("click", () => { selectedMonopolyActionCardId = null; renderMonopolyDeal(); });
  document.querySelectorAll<HTMLButtonElement>("[data-monopoly-bank]").forEach((button) => button.addEventListener("click", () => send({ type: "monopoly_play", cardId: button.dataset.monopolyBank, mode: "bank" })));
  document.querySelectorAll<HTMLButtonElement>("[data-monopoly-discard]").forEach((button) => button.addEventListener("click", () => send({ type: "monopoly_discard", cardId: button.dataset.monopolyDiscard })));
  document.querySelectorAll<HTMLButtonElement>("[data-monopoly-react]").forEach((button) => button.addEventListener("click", () => send({ type: "monopoly_react", action: button.dataset.monopolyReact })));
  document.querySelectorAll<HTMLButtonElement>("[data-move-wild]").forEach((button) => button.addEventListener("click", () => {
    const propertyCard = game.boards[myId]?.properties.find((item) => item.cardId === button.dataset.moveWild);
    if (!propertyCard) return;
    const options = propertyCard.card.colors || [];
    const nextColor = options[(options.indexOf(propertyCard.color) + 1) % options.length];
    send({ type: "monopoly_move_wild", cardId: propertyCard.cardId, color: nextColor });
  }));
  document.querySelector<HTMLFormElement>("#monopoly-action-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const card = game.hand.find((candidate) => candidate.id === form.dataset.cardId);
    if (!card) return;
    const payload: Record<string, unknown> = { type: "monopoly_play", cardId: card.id, mode: "action", color: data.get("color"), targetPlayerId: data.get("targetPlayerId"), offeredCardId: data.get("offeredCardId"), doubleRentCardIds: data.getAll("doubleRent") };
    const targetProperty = String(data.get("targetProperty") || "").split("|");
    if (targetProperty.length === 2) { payload.targetPlayerId = targetProperty[0]; payload.targetCardId = targetProperty[1]; }
    const completeTarget = String(data.get("completeTarget") || "").split("|");
    if (completeTarget.length === 2) { payload.targetPlayerId = completeTarget[0]; payload.color = completeTarget[1]; }
    selectedMonopolyActionCardId = null;
    send(payload);
  });
  document.querySelector<HTMLFormElement>("#monopoly-property-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    selectedMonopolyActionCardId = null;
    send({ type: "monopoly_play", cardId: form.dataset.cardId, mode: "property", color: data.get("color") });
  });
  const paymentForm = document.querySelector<HTMLFormElement>("#monopoly-payment-form");
  const updateMonopolyPayment = () => {
    if (!paymentForm || !payment) return;
    const selected = [...paymentForm.querySelectorAll<HTMLInputElement>('input[name="paymentCard"]:checked')];
    const selectedTotal = selected.reduce((total, input) => total + Number(input.dataset.value || 0), 0);
    const valid = selectedTotal >= payment.amount || (paymentAvailableTotal < payment.amount && selectedTotal === paymentAvailableTotal);
    const selectedOutput = paymentForm.querySelector("#monopoly-payment-selected");
    const guidance = paymentForm.querySelector("#monopoly-payment-guidance");
    const submit = paymentForm.querySelector<HTMLButtonElement>("#monopoly-payment-submit");
    if (selectedOutput) selectedOutput.textContent = monopolyMoney(selectedTotal);
    if (guidance) guidance.textContent = selectedTotal === 0 ? "Select assets to cover the payment." : selectedTotal === payment.amount ? "Exact payment selected." : selectedTotal > payment.amount ? `Overpaying by ${monopolyMoney(selectedTotal - payment.amount)}—there is no change.` : valid ? `All available assets selected; ${monopolyMoney(payment.amount - selectedTotal)} remains unpaid.` : `Still owe ${monopolyMoney(payment.amount - selectedTotal)}.`;
    if (submit) { submit.disabled = !valid; submit.textContent = valid ? `Pay ${monopolyMoney(selectedTotal)}` : "Pay selected assets"; }
  };
  paymentForm?.querySelectorAll<HTMLInputElement>('input[name="paymentCard"]').forEach((input) => input.addEventListener("change", updateMonopolyPayment));
  paymentForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    const cardIds = new FormData(event.currentTarget as HTMLFormElement).getAll("paymentCard");
    if (!cardIds.length) return showToast("Select at least one tabled asset.");
    send({ type: "monopoly_pay", cardIds });
  });
  document.querySelector("#monopoly-end-turn")?.addEventListener("click", () => { selectedMonopolyActionCardId = null; send({ type: "monopoly_end_turn" }); });
}

function renderQuestion() {
  const current = room!;
  const question = current.question!;
  const locked = current.phase === "result";
  const winner = current.result;
  const attempts = current.attempts || [];
  const myAttempt = attempts.find((attempt) => attempt.playerId === myId);
  const isClosest = current.gameMode === "closest";
  const isWithoutOver = current.gameMode === "without_over";
  const isNumeric = isClosest || isWithoutOver;
  const isMusicTrivia = musicTriviaCategories.has(current.category);
  const [scoreTypeLabel, movieLabel] = isClosest
    ? question.prompt.split(/;\s*/, 2)
    : ["", question.prompt];
  const scoreTypeClass = scoreTypeLabel === "Critic score" ? "critic" : "audience";
  const latestMiss = !isNumeric && !locked ? [...attempts].reverse().find((attempt) => !attempt.correct) : null;
  const missedChoices = new Set(isNumeric ? [] : attempts.filter((attempt) => !attempt.correct).map((attempt) => attempt.selectedIndex));
  const myAnswerWon = winner?.playerId === myId;
  const winnerIds = winner?.winnerIds || [];
  const closestWinners = current.players.filter((player) => winnerIds.includes(player.id));
  const allSkipped = locked && attempts.length > 0 && attempts.every((attempt) => attempt.skipped);
  const closestHeadline = winnerIds.length === 0
    ? allSkipped ? "Everyone skipped!" : "No guesses this round!"
    : winnerIds.includes(myId)
      ? winnerIds.length > 1 ? `You tied for closest${isWithoutOver ? " without going over" : ""}!` : `You were closest${isWithoutOver ? " without going over" : ""}!`
      : closestWinners.length > 1
        ? `${closestWinners.map((player) => player.name).join(" & ")} tied for closest${isWithoutOver ? " without going over" : ""}!`
        : `${closestWinners[0]?.name || winner?.playerName} was closest${isWithoutOver ? " without going over" : ""}!`;
  const closestSubmissions = [...attempts]
    .sort((a, b) => Number(a.skipped) - Number(b.skipped) || Number(Boolean(a.guess !== undefined && winner?.targetValue !== undefined && a.guess > winner.targetValue)) - Number(Boolean(b.guess !== undefined && winner?.targetValue !== undefined && b.guess > winner.targetValue)) || (a.distance ?? Number.MAX_SAFE_INTEGER) - (b.distance ?? Number.MAX_SAFE_INTEGER) || a.answeredAt - b.answeredAt);
  const answerArea = isNumeric ? (locked ? `
      <section class="closest-reveal" aria-label="Score reveal">
        <div class="actual-score ${isWithoutOver ? "actual-score--wild" : ""}"><span>${isWithoutOver ? "The answer" : "Actual score"}</span><strong>${winner?.targetValue?.toLocaleString() ?? "—"}<i>${isClosest ? "%" : ""}</i></strong>${isWithoutOver && question.answerUnit ? `<small>${escapeHtml(question.answerUnit)}</small>` : ""}</div>
        ${closestSubmissions.length ? `<ol class="guess-results">${closestSubmissions.map((attempt) => `
          <li class="${attempt.correct ? "guess-result--winner" : ""} ${attempt.skipped ? "guess-result--skipped" : ""} ${isWithoutOver && attempt.guess !== undefined && winner?.targetValue !== undefined && attempt.guess > winner.targetValue ? "guess-result--over" : ""}">
            <span class="avatar avatar--${Math.max(0, current.players.findIndex((player) => player.id === attempt.playerId)) % 5}">${escapeHtml(initials(attempt.playerName))}</span>
            <strong>${escapeHtml(attempt.playerName)}${attempt.playerId === myId ? " <small>YOU</small>" : ""}</strong>
            ${attempt.skipped ? `<b>Skipped</b><em>—</em>` : `<b>${attempt.guess?.toLocaleString()}${isClosest ? "%" : ""}</b><em>${isWithoutOver && attempt.guess !== undefined && winner?.targetValue !== undefined && attempt.guess > winner.targetValue ? "OVER" : attempt.distance === 0 ? "exact" : `${attempt.distance?.toLocaleString()} away`}</em>`}
          </li>`).join("")}</ol>` : `<p class="answer-hint">Nobody submitted a guess this round.</p>`}
      </section>` : myAttempt ? `
      <div class="guess-card guess-card--locked">
        <span class="guess-lock-icon" aria-hidden="true">✓</span>
        <div><strong>${selectedSkip ? "Skip locked in" : selectedGuess === null ? "Submission locked" : `${selectedGuess.toLocaleString()}${isClosest ? "%" : question.answerUnit ? ` ${escapeHtml(question.answerUnit)}` : ""} locked in`}</strong><p>Waiting for the rest of the room…</p></div>
      </div>` : `
      <form class="guess-card" id="closest-form">
        <label for="score-guess">${isWithoutOver ? "Your best guess" : "Your score guess"}</label>
        <div class="guess-entry"><input id="score-guess" name="guess" type="number" min="0" max="${isClosest ? 100 : Number.MAX_SAFE_INTEGER}" step="1" inputmode="numeric" autocomplete="off" placeholder="${isClosest ? "0–100" : "0"}" required><span>${isClosest ? "%" : escapeHtml(question.answerUnit || "")}</span></div>
        <div class="guess-actions">
          <button class="primary-button" type="submit"><span>Lock in guess</span><b aria-hidden="true">→</b></button>
          <button class="skip-button" id="skip-guess" type="button">Skip this question</button>
        </div>
      </form>`) : `
      <div class="answers" role="group" aria-label="Answer choices">
        ${question.choices.map((choice, index) => {
          const correct = locked && index === winner?.correctIndex;
          const wrong = missedChoices.has(index) || (locked && index === winner?.selectedIndex && !winner.correct);
          const picked = (myAttempt?.selectedIndex ?? selectedAnswer) === index;
          return `<button class="answer answer--${palette[index]} ${correct ? "answer--correct" : ""} ${wrong ? "answer--wrong" : ""} ${picked ? "answer--picked" : ""}" data-answer="${index}" ${locked || myAttempt || selectedAnswer !== null ? "disabled" : ""}>
            <span>${String.fromCharCode(65 + index)}</span><strong>${escapeHtml(choice)}</strong><i aria-hidden="true">${correct ? "✓" : wrong ? "×" : ""}</i>
          </button>`;
        }).join("")}
      </div>`;
  const feedback = isNumeric ? (locked ? `<div class="result-banner ${winnerIds.includes(myId) ? "result-banner--correct" : "result-banner--closest"}">
        <span class="result-icon">${winnerIds.length ? "◎" : "×"}</span>
        <div><strong>${escapeHtml(closestHeadline)}</strong><p>${question.reveal ? escapeHtml(question.reveal) : ""}${allSkipped ? " No point awarded." : winner?.distance === 0 ? " Exact hit!" : winner?.distance !== undefined ? ` Winning guess was ${winner.distance.toLocaleString()} away.` : isWithoutOver ? " Every guess went over, so no point was awarded." : ""}</p></div>
        <em>Next up…</em>
      </div>` : `<p class="answer-hint">Guesses stay hidden until everyone has answered.</p>`) : (locked ? `<div class="result-banner ${winner?.correct ? "result-banner--correct" : "result-banner--wrong"}">
        <span class="result-icon">${winner?.correct ? "⚡" : "×"}</span>
        <div><strong>${winner?.playerId ? (myAnswerWon ? "You got it first!" : `${escapeHtml(winner.playerName)} got it first!`) : "Time’s up!"}</strong>
        <p>${question.reveal ? escapeHtml(question.reveal) : winner?.correct ? "Correct — point scored." : "Nobody found the correct answer."}</p></div>
        <em>Next up…</em>
      </div>` : latestMiss ? `<div class="miss-banner"><span>×</span><p><strong>${escapeHtml(latestMiss.playerName)} missed.</strong> ${myAttempt ? "You’re locked in—cheer on the room!" : "Keep guessing!"}</p></div>` : `<p class="answer-hint">Wrong answers lock only that player. First correct answer wins.</p>`);
  app.innerHTML = pageShell(`
    <section class="game-top ${isMusicTrivia ? "game-top--eighties" : ""}">
      <div class="game-meta">
        <span>${escapeHtml(current.categoryLabel)}</span>
        <strong>${current.questionNumber}<i>/</i>${current.totalQuestions}</strong>
      </div>
      ${isNumeric ? "" : `<div class="timer-track"><i id="timer-fill"></i></div>`}
    </section>
    <section class="question-wrap ${locked ? "question-wrap--locked" : ""} ${isMusicTrivia ? "question-wrap--eighties" : ""}">
      <div class="question-card">
        ${isClosest ? `
          <p class="score-type score-type--${scoreTypeClass}">${escapeHtml(scoreTypeLabel)}</p>
          <h1 class="movie-title">${escapeHtml(movieLabel)}</h1>
        ` : isWithoutOver ? `
          <p class="score-type score-type--wild">DON’T GO OVER</p>
          <h1 class="wild-number-question">${escapeHtml(question.prompt)}</h1>
        ` : isMusicTrivia ? `
          <div class="eighties-track-badge"><span aria-hidden="true">♫</span><strong>NAME THAT ARTIST</strong><i>${escapeHtml(question.era)}</i></div>
          <h1>${escapeHtml(question.prompt)}</h1>
        ` : `
          <div class="question-tags"><span>${escapeHtml(question.era)}</span><span>${escapeHtml(question.difficulty)}</span></div>
          <h1>${escapeHtml(question.prompt)}</h1>
        `}
      </div>
      ${answerArea}
      ${feedback}
    </section>
    <aside class="score-strip"><ul>${playerList([...current.players].sort((a, b) => b.score - a.score))}</ul></aside>
  `, true);
  bindCommon();
  document.querySelectorAll<HTMLButtonElement>("[data-answer]").forEach((button) => {
    button.addEventListener("click", () => {
      if (selectedAnswer !== null) return;
      selectedAnswer = Number(button.dataset.answer);
      button.classList.add("answer--picked");
      document.querySelectorAll<HTMLButtonElement>("[data-answer]").forEach((answer) => answer.disabled = true);
      unlockAudio();
      playTone(440, .045, "sine");
      tap();
      send({ type: "answer", answerIndex: selectedAnswer, questionId: question.id });
    });
  });
  document.querySelector<HTMLFormElement>("#closest-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLInputElement>("#score-guess")!;
    if (!input.value.trim()) return showToast(isWithoutOver ? "Enter a whole-number guess." : "Enter a score from 0 to 100.");
    const guess = Number(input.value);
    if (!Number.isSafeInteger(guess) || guess < 0 || (isClosest && guess > 100)) {
      input.focus();
      return showToast(isWithoutOver ? "Use a positive whole number." : "Use a whole number from 0 to 100.");
    }
    selectedGuess = guess;
    input.disabled = true;
    document.querySelector<HTMLButtonElement>("#closest-form button")!.disabled = true;
    unlockAudio();
    playTone(440, .045, "sine");
    tap();
    send({ type: "answer", guess, questionId: question.id });
  });
  document.querySelector<HTMLButtonElement>("#skip-guess")?.addEventListener("click", () => {
    selectedSkip = true;
    document.querySelector<HTMLInputElement>("#score-guess")!.disabled = true;
    document.querySelectorAll<HTMLButtonElement>("#closest-form button").forEach((button) => button.disabled = true);
    unlockAudio();
    playTone(330, .06, "sine");
    tap();
    send({ type: "skip", questionId: question.id });
  });
  startTimer();
}

function renderFinished() {
  turboRallyGame?.destroy();
  turboRallyGame = null;
  cleanupTimer();
  cleanupHexchain();
  if (room!.gameType === "ticket_to_ride" && room!.ticketToRide) return renderTicketToRideFinished();
  if (room!.gameType === "axis_allies" && room!.axisAllies) return renderAxisAlliesFinished();
  if (room!.gameType === "stratego" && room!.stratego) return renderStrategoFinished();
  if (room!.gameType === "market_wars" && room!.marketWars) return renderMarket();
  if (room!.gameType === "modern_art" && room!.modernArt) return renderModernArtFinished();
  if (room!.gameType === "catan" && room!.catan) return renderCatanFinished();
  if (room!.gameType === "puzzle_fighter" && room!.puzzleFighter) return renderPuzzleFighterFinished();
  const isHearts = room!.gameType === "hearts";
  const isSpades = room!.gameType === "spades";
  const isHexchain = room!.gameType === "hexchain";
  const hexchainWinnerIds = room!.hexchain?.winnerIds || [];
  const standings = [...room!.players].sort((a, b) => isHexchain
    ? Number(hexchainWinnerIds.includes(b.id)) - Number(hexchainWinnerIds.includes(a.id)) || b.score - a.score
    : isHearts ? a.score - b.score : b.score - a.score);
  const winningScore = standings[0]?.score || 0;
  const winners = isHexchain ? standings.filter((player) => hexchainWinnerIds.includes(player.id)) : standings.filter((player) => player.score === winningScore);
  const isMatch = room!.gameType === "word_match";
  const isImpostor = room!.gameType === "impostor";
  const isWild = room!.gameType === "closest";
  const isDice = room!.gameType === "liars_dice";
  const isTaboo = room!.gameType === "taboo";
  const isCodeNames = room!.gameType === "codenames";
  const isSequence = room!.gameType === "sequence";
  const isPoker = room!.gameType === "holdem";
  const isBlackjack = room!.gameType === "blackjack";
  const isMonopoly = room!.gameType === "monopoly_deal";
  const isTokyo = ["king_of_tokyo", KING_COURT_GAME_TYPE].includes(room!.gameType);
  const isKart = room!.gameType === "turbo_rally";
  const isFeud = room!.gameType === "family_feud";
  const isConnectFour = room!.gameType === "connect_four";
  const feudGame = room!.familyFeud;
  const feudWinner = feudGame?.winnerTeam;
  const tabooGame = room!.taboo;
  const tabooWinner: TabooTeam = (tabooGame?.teamScores.a || 0) >= (tabooGame?.teamScores.b || 0) ? "a" : "b";
  const codeNamesGame = room!.codeNames;
  const codeNamesWinner = codeNamesGame?.winnerTeam || "red";
  const codeNamesDeathTeam: CodeNamesTeam | null = codeNamesGame?.lastGuess?.role === "assassin"
    ? codeNamesGame.teams[codeNamesGame.lastGuess.playerId] || (codeNamesWinner === "red" ? "blue" : "red")
    : null;
  const codeNamesDeathMessage = codeNamesDeathTeam
    ? `${codeNamesTeamName(codeNamesDeathTeam)} picked the death card. ${codeNamesTeamName(codeNamesWinner)} wins.`
    : "";
  const sequenceGame = room!.sequence;
  const sequenceWinner = sequenceGame?.winnerTeam || "coral";
  const tokyoWinnerId = room!.kingTokyo?.winnerIds[0];
  const spadesWinner = room!.spades?.winnerTeam;
  const currentHexchainLeaderboardKey = isHexchain ? `${room!.roomCode}:${room!.hexchain?.startedAt || 0}` : "";
  const hexchainDuelResult = isHexchain && room!.hexchain ? hexchainDuelResultHtml(room!.hexchain, myId) : "";
  const title = isConnectFour ? (room!.connectFour?.draw ? "The board is full — it’s a draw!" : room!.connectFour?.winnerId === myId ? "You connected four!" : `${room!.players.find((player) => player.id === room!.connectFour?.winnerId)?.name || "A player"} connected four!`) : isHexchain ? (winners.length > 1 ? "The siege ends in a tie!" : winners[0]?.id === myId ? "You survived the siege!" : `${winners[0]?.name || "Nobody"} survived the siege!`) : isFeud ? (feudWinner ? `${familyFeudTeamName(feudWinner)} wins the feud!` : "The feud ends in a tie!") : isSpades ? `${spadesWinner === "blue" ? "Blue" : "Gold"} team made 500!` : isTokyo ? (tokyoWinnerId ? tokyoWinnerId === myId ? "You are the King of Tokyo!" : `${room!.players.find((player) => player.id === tokyoWinnerId)?.name || "A Monster"} is the King of Tokyo!` : "Tokyo survives!") : isMonopoly ? (room!.monopolyDeal?.winnerId === myId ? "You made the winning deal!" : `${room!.players.find((player) => player.id === room!.monopolyDeal?.winnerId)?.name || "A player"} owns the town!`) : isKart ? (winners[0]?.id === myId ? "You took the checkered flag!" : `${winners[0]?.name || "Nobody"} wins the rally!`) : isPoker ? `${winners[0]?.id === myId ? "You own the table!" : `${winners[0]?.name || "Nobody"} owns the table!`}` : isSequence ? `${sequenceTeamName(sequenceWinner)} wins!` : isCodeNames ? `${codeNamesTeamName(codeNamesWinner)} wins!` : isTaboo ? `${tabooTeamName(tabooWinner)} wins!` : winners.length > 1 ? "It’s a tie!" : winners[0]?.id === myId ? (isHearts ? "You kept the lowest score!" : isMatch ? "You matched best!" : isImpostor ? "Master of disguise!" : isWild ? "You nailed the numbers!" : isDice ? "Your bluff held!" : isBlackjack ? "You ruled the table!" : "You sparked brightest!") : `${winners[0]?.name || "Nobody"} wins!`;
  const finalScoreCard = isConnectFour && room!.connectFour ? connectFourFinalHtml(room!.connectFour, room!.players, IS_LOCAL_CONNECT_FOUR ? { enabled: true } : undefined) : isFeud && feudGame ? `<section class="feud-final-scores">${(["a", "b"] as FamilyFeudTeam[]).map((team) => `<div class="feud-final-team feud-final-team--${team} ${feudWinner === team ? "is-winner" : ""}"><span>${familyFeudTeamName(team)}</span><strong>${feudGame.teamScores[team].toLocaleString()}</strong><small>${room!.players.filter((player) => feudGame.teams[player.id] === team).map((player) => escapeHtml(player.name)).join(" · ") || "No players"}</small></div>`).join("")}</section>` : isTokyo && room!.kingTokyo ? kingTokyoRecapHtml(room!.kingTokyo, room!.players, myId) : isMonopoly && room!.monopolyDeal ? monopolyRecapHtml(room!.monopolyDeal) : isSequence ? `<section class="sequence-final">
    <div class="sequence-final__winner sequence-final__winner--${sequenceWinner}"><span>SEQUENCE COMPLETE</span><strong>${escapeHtml(sequenceTeamName(sequenceWinner))}</strong><p>${room!.players.filter((player) => sequenceGame?.teams[player.id] === sequenceWinner).map((player) => escapeHtml(player.name)).join(" · ")}</p></div>
    <div class="sequence-final-scores">${(["coral", "aqua", "gold"] as SequenceTeam[]).slice(0, sequenceGame?.teamCount || 2).map((team) => `<div class="sequence-team-score sequence-team-score--${team} ${team === sequenceWinner ? "is-winner" : ""}"><span>${escapeHtml(sequenceTeamName(team))}</span><strong>${sequenceGame?.teamScores[team] || 0}</strong><small>completed runs</small></div>`).join("")}</div>
  </section>` : isCodeNames ? `<section class="codenames-final">
    ${codeNamesDeathTeam ? `<div class="codenames-final__death"><span>DEATH CARD SELECTED</span><strong>${escapeHtml(codeNamesTeamName(codeNamesDeathTeam))} picked the death card.</strong><b>${escapeHtml(codeNamesTeamName(codeNamesWinner))} wins.</b></div>` : ""}
    <div class="codenames-final__stamp codenames-final__stamp--${codeNamesWinner}"><span>MISSION COMPLETE</span><strong>${escapeHtml(codeNamesTeamName(codeNamesWinner))}</strong><p>${room!.players.filter((player) => codeNamesGame?.teams[player.id] === codeNamesWinner).map((player) => escapeHtml(player.name)).join(" · ")}</p></div>
    <div class="codenames-board codenames-board--final">${codeNamesGame?.board.map((card) => `<div class="codenames-card codenames-card--${card.role} is-revealed"><span>${escapeHtml(card.word)}</span></div>`).join("") || ""}</div>
  </section>` : isTaboo ? `<section class="taboo-final-teams">
    ${(["a", "b"] as TabooTeam[]).map((team) => `<div class="taboo-final-team taboo-final-team--${team} ${team === tabooWinner ? "winner" : ""}"><span>${escapeHtml(tabooTeamName(team))}</span><strong>${tabooGame?.teamScores[team] || 0}</strong><p>${room!.players.filter((player) => tabooGame?.teams[player.id] === team).map((player) => escapeHtml(player.name)).join(" · ")}</p></div>`).join("")}
  </section>` : `<section class="glass-card final-card"><ol class="final-list">${standings.map((player, index) => `
        <li class="${isHexchain ? hexchainWinnerIds.includes(player.id) ? "winner" : "" : index === 0 ? "winner" : ""}"><span class="place">${index + 1}</span><span class="avatar avatar--${index % 5}">${escapeHtml(initials(player.name))}</span><strong>${escapeHtml(player.name)}</strong><b>${isPoker || isBlackjack ? money(player.score) : player.score}</b></li>
      `).join("")}</ol></section>`;
  app.innerHTML = pageShell(kingGameHtml(`
    ${hexchainDuelResult}
    <section class="finish-hero">
      <div class="trophy ${isMatch ? "trophy--match" : ""} ${isImpostor ? "trophy--impostor" : ""} ${isWild ? "trophy--wild" : ""} ${isDice ? "trophy--dice" : ""} ${isTaboo ? "trophy--taboo" : ""} ${isCodeNames ? "trophy--codenames" : ""} ${isSequence ? "trophy--sequence" : ""} ${isPoker ? "trophy--poker" : ""} ${isBlackjack ? "trophy--blackjack" : ""} ${isMonopoly ? "trophy--monopoly" : ""} ${isTokyo ? "trophy--tokyo" : ""} ${isKart ? "trophy--kart" : ""} ${isFeud ? "trophy--feud" : ""}" aria-hidden="true">${isConnectFour ? "4" : isHexchain ? "❄" : isHearts ? "♥" : isSpades ? "♠" : isMatch ? "✦" : isImpostor ? "◉" : isWild ? "≤" : isDice ? "⚄" : isTaboo ? "⊘" : isCodeNames ? "⌗" : isSequence ? "5" : isPoker ? "♠" : isBlackjack ? "21" : isMonopoly ? "M" : isTokyo ? "✹" : isKart ? "🏁" : isFeud ? "100" : "ϟ"}</div>
      <p class="eyebrow">${isConnectFour ? "CONNECT FOUR COMPLETE" : isHexchain ? "THE FROST HAS SETTLED" : isHearts ? "LOWEST SCORE WINS" : isSpades ? "PARTNERS TO 500" : isTokyo ? "THE CITY HAS A NEW KING" : isKart ? "CHECKERED FLAG" : isPoker ? "LAST STACK STANDING" : isBlackjack ? "TABLE CHAMPION" : isDice ? "LAST DICE STANDING" : isTaboo ? "THE FINAL BUZZER" : isCodeNames ? "IDENTITIES CONFIRMED" : isSequence ? "FIVE IN A ROW" : isFeud ? "SURVEY SAYS: FINAL SCORE" : "FINAL SCORE"}</p>
      <h1>${escapeHtml(title)}</h1>
      <p>${isConnectFour ? `${room!.connectFour?.moveCount || 0} moves settled the board` : isHexchain ? "Every chain, counter, and frost attack has been counted" : isHearts ? "The table crossed 100 points and the cleanest score prevailed" : isSpades ? "The contracts, Nil bids, and bags are all settled" : isTokyo ? "Twenty Victory Points—or the last Monster standing—settled the battle" : isKart ? `${room!.kartRace?.racerIds.length || 0} human racers finished three full laps` : isMonopoly ? "Three complete property sets closed the deal" : isPoker ? `${room!.poker?.history.length || 0} hands are in the history` : isBlackjack ? "The table’s final bankrolls are counted" : isMatch ? "The judge has spoken" : isImpostor ? "The final secret is out" : isWild ? "The boldest underbid won" : isDice ? "The final bluff survived" : isTaboo ? "The forbidden words have been conquered" : isCodeNames ? codeNamesDeathMessage || "The field team found its final agent" : isSequence ? "The winning line is locked on the board" : isFeud ? `${room!.totalQuestions} survey boards settled the showdown` : "A trivia legend was made"} in room ${room!.roomCode}.</p>
    </section>
    ${finalScoreCard}
    ${isHexchain ? hexchainLeaderboardHtml() : ""}
    ${isPoker ? pokerHistoryHtml(room!.poker?.history || []) : ""}
    <div class="sticky-action">
      <button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>
      ${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}
      <button id="leave" class="text-button">Leave room</button>
    </div>
  `, room!.gameType));
  bindCommon();
  if (isHexchain) void loadHexchainLeaderboard(currentHexchainLeaderboardKey);
  if (isTokyo) bindKingTokyo(app, send, showToast);
  if (isConnectFour) bindConnectFour(app, sendConnectFourAction);
  document.querySelector("#rematch")!.addEventListener("click", () => send({ type: isTokyo ? "tokyo_rematch" : "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")!.addEventListener("click", leaveRoom);
  playFanfare();
}

function renderTicketToRideFinished() {
  const current = room!;
  const view = current.ticketToRide!;
  const winners = view.winnerIds.map((id) => current.players.find((player) => player.id === id)?.name).filter(Boolean);
  app.innerHTML = pageShell(`
    <section class="finish-hero"><div class="trophy trophy--ticket" aria-hidden="true">🚂</div><p class="eyebrow">TICKET TO RIDE COMPLETE</p><h1>${view.winnerIds.includes(myId) ? "Your railway rules the continent!" : `${escapeHtml(winners.join(" & ") || "The top conductor")} wins the journey!`}</h1><p>Route points, destination tickets, and the longest continuous railway have all been scored in room ${escapeHtml(current.roomCode)}.</p></section>
    ${ticketToRideFinalHtml(view, current.players)}
    <div class="sticky-action"><button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}<button id="leave" class="text-button">Leave room</button></div>
  `);
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
  playFanfare();
}

function renderAxisAlliesFinished() {
  const current = room!;
  app.innerHTML = pageShell(`${axisAlliesFinalHtml(current.axisAllies!)}<div class="sticky-action"><button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}<button id="leave" class="text-button">Leave room</button></div>`, false, "app-shell--axis-game");
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
  playFanfare();
}

function renderPuzzleFighterFinished() {
  const current = room!;
  app.innerHTML = pageShell(`${puzzleFighterFinalHtml(current.puzzleFighter!, current.players)}<div class="sticky-action"><button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}<button id="leave" class="text-button">Leave room</button></div>`, false, "app-shell--puzzle-fighter");
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
  playFanfare();
}

function renderStrategoFinished() {
  const current = room!;
  app.innerHTML = pageShell(`${strategoFinalHtml(current.stratego!, current.players)}<div class="sticky-action"><button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}<button id="leave" class="text-button">Leave room</button></div>`, false, "app-shell--stratego-game");
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom);
  playFanfare();
}

function renderModernArtFinished() {
  const current = room!;
  app.innerHTML = pageShell(`${modernArtFinalHtml(current.modernArt!, current.players)}<div class="sticky-action"><button id="rematch" class="primary-button primary-button--large"><span>Rematch</span><b>↻</b></button>${(!IS_LOCAL_RUNTIME || LOCAL_GAME_TYPES.length > 1) ? `<button id="change-game" class="secondary-button party-change-button"><span>${IS_LOCAL_RUNTIME ? "Choose another local game" : "Choose another game"}</span><b>→</b></button>` : ""}<button id="leave" class="text-button">Leave room</button></div>`, false, "app-shell--modern-art");
  bindCommon();
  document.querySelector("#rematch")?.addEventListener("click", () => send({ type: "reset" }));
  document.querySelector("#change-game")?.addEventListener("click", () => IS_LOCAL_RUNTIME ? leaveRoom() : send({ type: "party_menu" }));
  document.querySelector("#leave")?.addEventListener("click", leaveRoom); playFanfare();
}

function hexchainLeaderboardHtml() {
  const leaderboardBody = hexchainLeaderboardStatus === "loading" || hexchainLeaderboardStatus === "idle"
    ? `<p class="hexchain-leaderboard__message"><span class="loader"><i></i><i></i><i></i></span>Loading the all-time scores…</p>`
    : hexchainLeaderboardStatus === "error"
      ? `<p class="hexchain-leaderboard__message">The leaderboard could not be loaded right now.</p>`
      : hexchainLeaderboardEntries.length
        ? `<div class="hexchain-leaderboard__grid">${[hexchainLeaderboardEntries.slice(0, 10), hexchainLeaderboardEntries.slice(10, 20)].filter((entries) => entries.length).map((entries, column) => `<ol start="${column * 10 + 1}">${entries.map((entry, index) => `<li><span>${column * 10 + index + 1}</span><strong>${escapeHtml(entry.playerName)}</strong><b>${entry.score.toLocaleString()}</b></li>`).join("")}</ol>`).join("")}</div>`
        : `<p class="hexchain-leaderboard__message">Finish a run to claim the first spot.</p>`;
  return `<section class="hexchain-leaderboard" id="hexchain-leaderboard" aria-live="polite">
    <header><div><p>ALL-TIME</p><h2>Hex Chain Top 20</h2></div><span>Each player’s personal best</span></header>
    ${leaderboardBody}
  </section>`;
}

async function loadHexchainLeaderboard(key: string) {
  if (hexchainLeaderboardKey === key && (hexchainLeaderboardStatus === "loading" || hexchainLeaderboardStatus === "loaded")) return;
  hexchainLeaderboardKey = key;
  hexchainLeaderboardEntries = [];
  hexchainLeaderboardStatus = "loading";
  try {
    const response = await fetch(`${API_BASE}/hexchain/leaderboard`, { cache: "no-store" });
    if (!response.ok) throw new Error("Leaderboard request failed");
    const data = await response.json() as { entries?: unknown };
    if (!Array.isArray(data.entries)) throw new Error("Invalid leaderboard response");
    const entries = data.entries.filter((entry): entry is HexchainLeaderboardEntry => {
      if (!entry || typeof entry !== "object") return false;
      const candidate = entry as Partial<HexchainLeaderboardEntry>;
      return typeof candidate.playerName === "string" && candidate.playerName.length > 0 && Number.isSafeInteger(candidate.score) && candidate.score! >= 0;
    }).slice(0, 20);
    if (hexchainLeaderboardKey !== key) return;
    hexchainLeaderboardEntries = entries;
    hexchainLeaderboardStatus = "loaded";
  } catch {
    if (hexchainLeaderboardKey !== key) return;
    hexchainLeaderboardStatus = "error";
  }
  const leaderboard = document.querySelector<HTMLElement>("#hexchain-leaderboard");
  if (leaderboard && hexchainLeaderboardKey === key) leaderboard.outerHTML = hexchainLeaderboardHtml();
}

function startTimer(totalMs = 15_000, useLocalDuration = false) {
  cleanupTimer();
  const startedAt = performance.now();
  const update = () => {
    const fill = document.querySelector<HTMLElement>("#timer-fill");
    const pokerCountdown = document.querySelector<HTMLElement>("#poker-next-hand-countdown");
    if ((!fill && !pokerCountdown) || !room?.deadline) return;
    const remaining = Math.max(0, useLocalDuration ? totalMs - (performance.now() - startedAt) : room.deadline - Date.now());
    if (fill) {
      fill.style.transform = `scaleX(${remaining / totalMs})`;
      fill.classList.toggle("urgent", remaining < 4000);
    }
    if (pokerCountdown) pokerCountdown.textContent = String(Math.ceil(remaining / 1_000));
    if (remaining > 0) timerFrame = requestAnimationFrame(update);
  };
  update();
}

function cleanupTimer() {
  if (timerFrame) cancelAnimationFrame(timerFrame);
  timerFrame = undefined;
}

function bindCommon() {
  bindRoomExperiences(app, () => room, send, IS_LOCAL_RUNTIME ? LOCAL_GAME_TYPES : []);
  document.querySelector("#open-saved-library")?.addEventListener("click", event => {
    if (room) return;
    event.preventDefault(); history.pushState({}, "", "/?view=library"); renderSavedLibrary();
  });
  document.querySelector("#open-all-time-leaderboard")?.addEventListener("click", (event) => {
    if (room) return;
    event.preventDefault();
    history.pushState({}, "", "/?view=leaderboard");
    renderLeaderboard();
  });
  document.querySelector<HTMLInputElement>("#admin-sit-out")?.addEventListener("change", (event) => {
    const input = event.currentTarget as HTMLInputElement;
    input.disabled = true;
    send({ type: "admin_participation", sittingOut: input.checked });
  });
  roomChat.bindButton();
  document.querySelector("#choose-party-game")?.addEventListener("click", () => send({ type: "party_menu" }));
  document.querySelector<HTMLAnchorElement>(".brand")?.addEventListener("click", (event) => {
    if (displayMode) {
      event.preventDefault();
      leaveSharedDisplay();
      return;
    }
    if (!room) return;
    event.preventDefault();
    if (IS_LOCAL_RUNTIME) {
      leaveRoom();
      return;
    }
    if (canUseRoomControl(room, "party_menu", room.isAdmin)) send({ type: "party_menu" });
    else showToast("Finish this game to choose the next one with your party.");
  });
  document.querySelector("#music-toggle")?.addEventListener("click", () => {
    musicEnabled = !musicEnabled;
    if (musicEnabled && soundVolume === 0) soundVolume = 50;
    persistAudioPreferences();
    syncBackgroundMusic();
    renderRoomOrHome();
  });
  document.querySelector<HTMLInputElement>("#voice-toggle")?.addEventListener("change", (event) => {
    voicesEnabled = (event.currentTarget as HTMLInputElement).checked;
    if (voicesEnabled && soundVolume === 0) soundVolume = 50;
    persistAudioPreferences();
    updateAudioOutputLevel();
    if (voicesEnabled) {
      unlockAudio();
      const pack = room ? voiceoverPackForGame(room.gameType) : null;
      if (pack) preloadVoiceoverPack(pack);
    } else stopVoiceover();
    renderRoomOrHome();
  });
  const fontSizeInput = document.querySelector<HTMLInputElement>("#app-font-size");
  fontSizeInput?.addEventListener("input", () => {
    appFontScale = Math.max(80, Math.min(150, Math.round((Number(fontSizeInput.value) || 100) / 5) * 5));
    document.documentElement.style.fontSize = `${appFontScale}%`;
    localStorage.setItem("party-stack-font-scale", String(appFontScale));
    const output = document.querySelector<HTMLOutputElement>("#app-font-size-output");
    if (output) output.value = `${appFontScale}%`;
    fontSizeInput.setAttribute("aria-label", `App font size, ${appFontScale} percent`);
  });
  document.querySelector("#local-games-home")?.addEventListener("click", leaveRoom);
  document.querySelector("#sound-toggle")?.addEventListener("click", () => {
    soundEffectsEnabled = !soundEffectsEnabled;
    if (soundEffectsEnabled && soundVolume === 0) soundVolume = 50;
    persistAudioPreferences();
    updateAudioOutputLevel();
    if (soundEffectsEnabled) { unlockAudio(); playTone(620, .08); }
    renderRoomOrHome();
  });
  const volumeInput = document.querySelector<HTMLInputElement>("#sound-volume");
  volumeInput?.addEventListener("input", () => {
    soundVolume = Math.max(0, Math.min(100, Number(volumeInput.value) || 0));
    persistAudioPreferences();
    updateAudioOutputLevel();
    syncBackgroundMusic();
    const output = document.querySelector<HTMLOutputElement>("#sound-volume-output");
    if (output) output.value = `${soundVolume}%`;
    volumeInput.setAttribute("aria-label", `Audio volume, ${soundVolume} percent`);
  });
  volumeInput?.addEventListener("change", () => {
    if (soundEffectsEnabled && soundVolume > 0) { unlockAudio(); playTone(620, .08); }
  });
  document.querySelector("#display-fullscreen")?.addEventListener("click", async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } }).wakeLock;
      if (wakeLock) await wakeLock.request("screen");
    } catch {
      showToast("Full screen or screen wake isn’t available in this browser.");
    }
  });
  document.querySelector("#display-exit")?.addEventListener("click", () => leaveSharedDisplay());
  document.querySelector("#join-qr-trigger")?.addEventListener("click", () => {
    tap();
    joinQrOpen = true;
    const overlay = document.querySelector<HTMLElement>("#join-qr-overlay");
    if (overlay) overlay.hidden = false;
    document.querySelector<HTMLButtonElement>("#join-qr-close")?.focus();
  });
  document.querySelector("#join-qr-close")?.addEventListener("click", () => {
    tap();
    joinQrOpen = false;
    const overlay = document.querySelector<HTMLElement>("#join-qr-overlay");
    if (overlay) overlay.hidden = true;
    document.querySelector<HTMLButtonElement>("#join-qr-trigger")?.focus();
  });
  const joinQrCanvas = document.querySelector<HTMLCanvasElement>("#join-qr-canvas");
  if (joinQrCanvas && room) {
    const joinUrl = `${location.origin}/?room=${encodeURIComponent(room.roomCode)}`;
    void QRCode.toCanvas(joinQrCanvas, joinUrl, {
      width: 340,
      margin: 2,
      color: { dark: "#171236", light: "#fff9ec" },
      errorCorrectionLevel: "M",
    });
  }
}

function renderLeaderboard() {
  renderHome();
  document.title = "All-time leaderboard · Party Stack";
  app.innerHTML = pageShell(leaderboardShellHtml(IS_LOCAL_RUNTIME), false, "app-shell--leaderboard");
  bindCommon();
  unmountLeaderboard = mountLeaderboard(document.querySelector<HTMLElement>("#all-time-leaderboard")!, API_BASE);
}

let unmountLibrary: (() => void) | null = null;
function renderSavedLibrary(galleryId?: string) {
  renderHome();
  document.title = galleryId ? "Shared creation · Party Stack" : "Your library · Party Stack";
  app.innerHTML = pageShell(libraryShellHtml(Boolean(galleryId)), false, "app-shell--library");
  bindCommon();
  unmountLibrary = mountLibrary(document.querySelector<HTMLElement>("#saved-library")!, {
    api: API_BASE, playerId: myId, playerName: myName, local: IS_LOCAL_RUNTIME, galleryId,
    onOpen: (code, token, name) => {
      unmountLibrary?.(); unmountLibrary = null;
      if (name) { myName = name; localStorage.setItem("quiz-spark-name", name); }
      if (token) localStorage.setItem(`quiz-spark-admin-${code}`, token);
      joinRoom(code);
    },
  });
}
function renderRoomOrHome() {
  const params = new URLSearchParams(location.search);
  room ? renderRoom() : displayMode ? renderDisplayJoin() : params.get("view") === "library" || params.has("gallery") ? renderSavedLibrary(params.get("gallery") || undefined) : params.get("view") === "leaderboard" ? renderLeaderboard() : renderHome();
}
function send(message: object) { if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message)); }
function tap() { navigator.vibrate?.(18); }

async function refreshStaleClientRelease() {
  if (unmountCoralChorus || unmountStainedGlass || import.meta.env.DEV || clientReleaseCheckPending || document.visibilityState === "hidden" || (room?.gameType === "party_pets" && room.partyPets?.currentMinigame)) return;
  clientReleaseCheckPending = true;
  try {
    const response = await fetch(`/?release-check=${Date.now()}`, { cache: "no-store", headers: { Accept: "text/html" } });
    if (!response.ok) return;
    const html = await response.text();
    if (!clientReleaseIsStale(html, import.meta.url, location.origin)) return;
    const latestModuleUrl = moduleScriptUrl(html, location.origin);
    if (!latestModuleUrl || sessionStorage.getItem(CLIENT_RELEASE_REFRESH_KEY) === latestModuleUrl) return;
    sessionStorage.setItem(CLIENT_RELEASE_REFRESH_KEY, latestModuleUrl);
    location.reload();
  } catch {
    // A version check must never interrupt a party when the network is unavailable.
  } finally {
    clientReleaseCheckPending = false;
  }
}

function visibleBlackjackCardCount(state: RoomState | null) {
  if (!state?.blackjack) return 0;
  const playerCards = Object.values(state.blackjack.hands).reduce((total, hands) => total + hands.reduce((handTotal, hand) => handTotal + hand.length, 0), 0);
  return playerCards + state.blackjack.dealerHand.filter(Boolean).length;
}

function tokyoMovementSnapshot(state: RoomState | null): TokyoMovementSnapshot | null {
  if (!state || !["king_of_tokyo", KING_COURT_GAME_TYPE].includes(state.gameType) || !state.kingTokyo) return null;
  return {
    phase: state.phase,
    turnNumber: state.kingTokyo.turnNumber,
    currentPlayerId: state.kingTokyo.currentPlayerId,
    tokyoCityId: state.kingTokyo.tokyoCityId,
    tokyoBayId: state.kingTokyo.tokyoBayId,
    playerNames: Object.fromEntries(state.players.map((player) => [player.id, player.name])),
    alive: Object.fromEntries(Object.entries(state.kingTokyo.players).map(([id, player]) => [id, player.alive])),
  };
}

function persistAudioPreferences() {
  localStorage.setItem("quiz-spark-music", musicEnabled ? "on" : "off");
  localStorage.setItem("quiz-spark-sound-effects", soundEffectsEnabled ? "on" : "off");
  localStorage.setItem("quiz-spark-sound", soundEffectsEnabled ? "on" : "off");
  localStorage.setItem("quiz-spark-voices", voicesEnabled ? "on" : "off");
  localStorage.setItem("quiz-spark-volume", String(soundVolume));
  localStorage.setItem("quiz-spark-volume-calibration", SOUND_VOLUME_CALIBRATION);
}

function activeGameMusicUrl() {
  if (displayMode || !musicEnabled || soundVolume <= 0 || !room) return null;
  const isMusicPhase = room.gameType === "hexchain"
    ? room.phase === "lobby" || room.phase === "hexchain_battle"
    : room.gameType === "puzzle_fighter"
      ? room.phase === "lobby" || room.phase === "puzzle_fighter_playing"
      : false;
  return isMusicPhase ? GAME_MUSIC_URLS[room.gameType] || null : null;
}

function getBackgroundMusic(url: string) {
  if (!backgroundMusic || backgroundMusicUrl !== url) {
    backgroundMusic?.pause();
    backgroundMusic = new Audio(url);
    backgroundMusicUrl = url;
    backgroundMusic.loop = true;
    backgroundMusic.preload = "metadata";
  }
  return backgroundMusic;
}

function syncBackgroundMusic() {
  const url = activeGameMusicUrl();
  if (!url) {
    backgroundMusic?.pause();
    return;
  }
  const music = getBackgroundMusic(url);
  const voiceDuck = activeVoiceSource ? .24 : 1;
  music.volume = Math.min(1, soundVolume / 100 * (room ? GAME_MUSIC_VOLUME_MULTIPLIERS[room.gameType] || 1 : 1) * voiceDuck);
  if (!music.paused || backgroundMusicPlayPending === music) return;
  backgroundMusicPlayPending = music;
  void music.play().catch(() => undefined).finally(() => {
    if (backgroundMusicPlayPending === music) backgroundMusicPlayPending = null;
  });
}

function audioOutputLevel() {
  if (!soundEffectsEnabled || soundVolume <= 0) return 0;
  return Math.pow(soundVolume / 100, .68) * 1.15;
}

function updateAudioOutputLevel() {
  if (!audioContext) return;
  if (audioMasterGain) audioMasterGain.gain.setTargetAtTime(audioOutputLevel(), audioContext.currentTime, .015);
  if (voiceGain) voiceGain.gain.setTargetAtTime(voiceOutputLevel(), audioContext.currentTime, .015);
}

function getAudioOutput(context: AudioContext) {
  if (!audioMasterGain || !audioCompressor) {
    audioMasterGain = context.createGain();
    audioCompressor = context.createDynamicsCompressor();
    audioCompressor.threshold.value = -8;
    audioCompressor.knee.value = 10;
    audioCompressor.ratio.value = 6;
    audioCompressor.attack.value = .003;
    audioCompressor.release.value = .16;
    audioMasterGain.connect(audioCompressor).connect(context.destination);
  }
  audioMasterGain.gain.value = audioOutputLevel();
  return audioMasterGain;
}

function getAudioContext() {
  if (!soundEffectsEnabled && !voicesEnabled) return null;
  audioContext ||= new AudioContext();
  return audioContext;
}

function voiceOutputLevel() {
  if (!voicesEnabled || soundVolume <= 0) return 0;
  return Math.min(1.15, Math.pow(soundVolume / 100, .72) * 1.05);
}

function getVoiceOutput(context: AudioContext) {
  if (!voiceGain) {
    voiceGain = context.createGain();
    voiceGain.connect(context.destination);
  }
  voiceGain.gain.value = voiceOutputLevel();
  return voiceGain;
}

function isPrimaryVoiceDevice() {
  return !room || IS_LOCAL_RUNTIME || displayMode || room.isDisplay || room.isAdmin;
}

function loadVoiceover(cue: VoiceoverCue) {
  const cached = voiceBufferCache.get(cue);
  if (cached) return cached;
  const loading = (async () => {
    const response = await fetch(VOICEOVER_URLS[cue]);
    if (!response.ok) throw new Error(`Voiceover request failed: ${response.status}`);
    const context = getAudioContext();
    if (!context) throw new Error("Voiceover audio is disabled");
    return context.decodeAudioData(await response.arrayBuffer());
  })();
  voiceBufferCache.set(cue, loading);
  void loading.catch(() => voiceBufferCache.delete(cue));
  return loading;
}

function preloadVoiceoverPack(pack: VoiceoverPack) {
  if (!voicesEnabled || !isPrimaryVoiceDevice()) return;
  VOICEOVER_PACKS[pack].forEach((cue) => { void loadVoiceover(cue).catch(() => undefined); });
}

function stopVoiceover() {
  voicePlaybackId += 1;
  const source = activeVoiceSource;
  activeVoiceSource = null;
  if (source) {
    source.onended = null;
    try { source.stop(); } catch { /* The source may already have ended. */ }
  }
  syncBackgroundMusic();
}

function playVoiceover(cue: VoiceoverCue) {
  if (!voicesEnabled || soundVolume <= 0 || !isPrimaryVoiceDevice()) return;
  const playbackId = ++voicePlaybackId;
  void loadVoiceover(cue).then((buffer) => {
    if (playbackId !== voicePlaybackId || !voicesEnabled || soundVolume <= 0 || !isPrimaryVoiceDevice()) return;
    const context = getAudioContext();
    if (!context) return;
    const start = () => {
      if (playbackId !== voicePlaybackId || context.state !== "running") return;
      if (activeVoiceSource) {
        activeVoiceSource.onended = null;
        try { activeVoiceSource.stop(); } catch { /* The source may already have ended. */ }
      }
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(getVoiceOutput(context));
      activeVoiceSource = source;
      source.onended = () => {
        if (activeVoiceSource !== source) return;
        activeVoiceSource = null;
        syncBackgroundMusic();
      };
      syncBackgroundMusic();
      source.start();
    };
    if (context.state === "running") start();
    else void resumeAudio(context).then(start);
  }).catch(() => undefined);
}

function resumeAudio(context: AudioContext) {
  if (context.state === "running") return Promise.resolve();
  if (!audioResumePromise) {
    audioResumePromise = context.resume().catch(() => undefined).finally(() => { audioResumePromise = null; });
  }
  return audioResumePromise;
}

function unlockAudio() {
  const context = getAudioContext();
  if (context) void resumeAudio(context);
}

function playTone(frequency: number, duration = .1, type: OscillatorType = "triangle", delay = 0, volume = .42) {
  const context = getAudioContext();
  if (!context) return;
  const scheduleTone = () => {
    if (!soundEffectsEnabled || context.state !== "running") return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = context.currentTime + delay;
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gain.gain.setValueAtTime(.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(.001, volume), start + .01);
    gain.gain.exponentialRampToValueAtTime(.0001, start + duration);
    oscillator.connect(gain).connect(getAudioOutput(context));
    oscillator.start(start);
    oscillator.stop(start + duration + .02);
  };
  if (context.state === "running") scheduleTone();
  else void resumeAudio(context).then(scheduleTone);
}

function playSweep(startFrequency: number, endFrequency: number, duration = .3, type: OscillatorType = "sawtooth", delay = 0, volume = .1, filterStart = 400, filterEnd = 3_000) {
  const context = getAudioContext();
  if (!context) return;
  const scheduleSweep = () => {
    if (!soundEffectsEnabled || context.state !== "running") return;
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(1, startFrequency), start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), start + duration);
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(Math.max(1, filterStart), start);
    filter.frequency.exponentialRampToValueAtTime(Math.max(1, filterEnd), start + duration);
    gain.gain.setValueAtTime(Math.max(.001, volume), start);
    gain.gain.exponentialRampToValueAtTime(.0001, start + duration + .05);
    oscillator.connect(filter).connect(gain).connect(getAudioOutput(context));
    oscillator.start(start);
    oscillator.stop(start + duration + .08);
  };
  if (context.state === "running") scheduleSweep();
  else void resumeAudio(context).then(scheduleSweep);
}

function playNoiseSweep(duration = .12, volume = .06, startFrequency = 3_000, peakFrequency = startFrequency, endFrequency = startFrequency, delay = 0) {
  const context = getAudioContext();
  if (!context) return;
  const scheduleNoise = () => {
    if (!soundEffectsEnabled || context.state !== "running") return;
    const start = context.currentTime + delay;
    const frameCount = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) data[index] = Math.random() * 2 - 1;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = "bandpass";
    filter.Q.value = 2;
    filter.frequency.setValueAtTime(Math.max(1, startFrequency), start);
    filter.frequency.exponentialRampToValueAtTime(Math.max(1, peakFrequency), start + duration * .42);
    filter.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), start + duration);
    gain.gain.setValueAtTime(Math.max(.001, volume), start);
    gain.gain.exponentialRampToValueAtTime(.0001, start + duration);
    source.connect(filter).connect(gain).connect(getAudioOutput(context));
    source.start(start);
    source.stop(start + duration + .02);
  };
  if (context.state === "running") scheduleNoise();
  else void resumeAudio(context).then(scheduleNoise);
}

// Hexchain's short, bright cues need substantially more headroom than the longer
// sounds in the rest of Party Stack. At the new 50% default these land at roughly
// twice the previous Hexchain level at 100%, while the compressor still catches
// overlapping chain notes cleanly.
const HEXCHAIN_AUDIO_BOOST = 3.2;

function playHexchainTone(frequency: number, duration = .1, type: OscillatorType = "triangle", delay = 0, volume = .42) {
  playTone(frequency, duration, type, delay, volume * HEXCHAIN_AUDIO_BOOST);
}

function playHexchainSweep(startFrequency: number, endFrequency: number, duration = .3, type: OscillatorType = "sawtooth", delay = 0, volume = .1, filterStart = 400, filterEnd = 3_000) {
  playSweep(startFrequency, endFrequency, duration, type, delay, volume * HEXCHAIN_AUDIO_BOOST, filterStart, filterEnd);
}

function playHexchainNoise(duration = .12, volume = .06, startFrequency = 3_000, peakFrequency = startFrequency, endFrequency = startFrequency, delay = 0) {
  playNoiseSweep(duration, volume * HEXCHAIN_AUDIO_BOOST, startFrequency, peakFrequency, endFrequency, delay);
}

function playCardDealSound(cardCount: number) {
  for (let index = 0; index < Math.min(cardCount, 8); index += 1) {
    playTone(index % 2 ? 350 : 310, .035, "sine", index * .055);
  }
}

function celebrate(correct: boolean) {
  tap();
  if (correct) {
    [523, 659, 784].forEach((frequency, index) => playTone(frequency, .15, "triangle", index * .08));
    document.body.classList.add("celebrate");
    setTimeout(() => document.body.classList.remove("celebrate"), 700);
  } else playTone(170, .22, "sawtooth");
}

function playMiss() {
  tap();
  playTone(185, .12, "sawtooth");
}

function playTurnAlert() {
  if (!soundEffectsEnabled) return;
  navigator.vibrate?.([85, 45, 85]);
  [784, 1047, 1319].forEach((frequency, index) => playTone(frequency, .16, index === 1 ? "sine" : "triangle", index * .11));
}

function playFanfare() {
  [392, 523, 659, 784].forEach((frequency, index) => playTone(frequency, .22, "triangle", index * .11));
}

function setBusy(selector: string, busy: boolean) {
  const button = document.querySelector<HTMLButtonElement>(selector);
  if (!button) return;
  button.disabled = busy;
  button.classList.toggle("is-busy", busy);
}

function showToast(message: string) {
  const toast = document.querySelector<HTMLElement>("#toast");
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add("toast--show");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("toast--show"), 2600);
}

function showTokyoMovementPopup(event: TokyoMovementEvent) {
  document.querySelector(".kt-movement-popup")?.remove();
  clearTimeout(tokyoMovementPopupTimer);
  const popup = document.createElement("aside");
  popup.className = `kt-movement-popup kt-movement-popup--${event.kind}`;
  popup.setAttribute("role", "status");
  popup.setAttribute("aria-live", "assertive");
  popup.innerHTML = kingGameHtml(`<button type="button" aria-label="Close notification">×</button><span aria-hidden="true">${event.kind === "entered" ? "🏀" : "↗"}</span><div><strong>${escapeHtml(event.title)}</strong><p>${escapeHtml(event.detail)}</p></div>`);
  const dismiss = () => {
    popup.classList.remove("is-visible");
    window.setTimeout(() => popup.remove(), 180);
  };
  popup.querySelector("button")?.addEventListener("click", dismiss);
  document.body.append(popup);
  window.requestAnimationFrame(() => popup.classList.add("is-visible"));
  tokyoMovementPopupTimer = window.setTimeout(dismiss, 5200);
}

function showTokyoRollResultPopup(result: KingTokyoRollResult, players: Player[]) {
  document.querySelector(".kt-roll-result-popup")?.remove();
  clearTimeout(tokyoRollResultPopupTimer);
  const popup = document.createElement("aside");
  popup.className = "kt-roll-result-popup";
  popup.setAttribute("role", "status");
  popup.setAttribute("aria-live", "polite");
  popup.innerHTML = kingGameHtml(kingTokyoRollResultPopupHtml(result, players));
  const dismiss = () => {
    popup.classList.remove("is-visible");
    window.setTimeout(() => popup.remove(), 180);
  };
  popup.querySelector(".kt-roll-result-popup__close")?.addEventListener("click", dismiss);
  document.body.append(popup);
  window.requestAnimationFrame(() => popup.classList.add("is-visible"));
  tokyoRollResultPopupTimer = window.setTimeout(dismiss, 8000);
}

function showTokyoCardPurchasePopup(purchase: KingTokyoCardPurchase, players: Player[]) {
  let stack = document.querySelector<HTMLElement>(".kt-card-purchase-stack");
  if (!stack) {
    stack = document.createElement("div");
    stack.className = "kt-card-purchase-stack";
    document.body.append(stack);
  }
  const popup = document.createElement("aside");
  popup.className = "kt-card-purchase-popup";
  popup.setAttribute("role", "status");
  popup.setAttribute("aria-live", "polite");
  popup.innerHTML = kingGameHtml(kingTokyoCardPurchasePopupHtml(purchase, players));
  const dismiss = () => {
    popup.classList.remove("is-visible");
    window.setTimeout(() => {
      popup.remove();
      if (!stack?.children.length) stack?.remove();
    }, 180);
  };
  popup.querySelector(".kt-card-purchase-popup__close")?.addEventListener("click", dismiss);
  stack.append(popup);
  while (stack.children.length > 3) stack.firstElementChild?.remove();
  window.requestAnimationFrame(() => popup.classList.add("is-visible"));
  window.setTimeout(dismiss, 8000);
}

function showTokyoYieldRefusalPopup(event: KingTokyoYieldDecisionEvent, players: Player[]) {
  let stack = document.querySelector<HTMLElement>(".kt-yield-refusal-stack");
  if (!stack) {
    stack = document.createElement("div");
    stack.className = "kt-yield-refusal-stack";
    document.body.append(stack);
  }
  const popup = document.createElement("aside");
  popup.className = "kt-yield-refusal-popup";
  popup.setAttribute("role", "status");
  popup.setAttribute("aria-live", "assertive");
  popup.innerHTML = kingGameHtml(kingTokyoYieldRefusalPopupHtml(event, players));
  const dismiss = () => {
    popup.classList.remove("is-visible");
    window.setTimeout(() => {
      popup.remove();
      if (!stack?.children.length) stack?.remove();
    }, 180);
  };
  popup.querySelector(".kt-yield-refusal-popup__close")?.addEventListener("click", dismiss);
  stack.append(popup);
  while (stack.children.length > 2) stack.firstElementChild?.remove();
  window.requestAnimationFrame(() => popup.classList.add("is-visible"));
  window.setTimeout(dismiss, 6500);
}

function leaveRoom() {
  setMarketUiRoom("");
  cleanupCreativeRoom();
  roomChat.setRoom(null);
  if (displayMode) return leaveSharedDisplay();
  const currentSocket = socket;
  socket = null;
  currentSocket?.close();
  room = null;
  connectFourHint = null;
  connectFourHintLoading = false;
  document.querySelector(".kt-roll-result-popup")?.remove();
  document.querySelector(".kt-card-purchase-stack")?.remove();
  document.querySelector(".kt-yield-refusal-stack")?.remove();
  clearTimeout(tokyoRollResultPopupTimer);
  tokyoMovementDepartures = [];
  joinQrOpen = false;
  pendingRoom = "";
  history.replaceState({}, "", "/");
  renderHome();
}

function leaveSharedDisplay(updateHistory = true) {
  roomChat.setRoom(null);
  const currentSocket = socket;
  socket = null;
  currentSocket?.close();
  room = null;
  document.querySelector(".kt-roll-result-popup")?.remove();
  document.querySelector(".kt-card-purchase-stack")?.remove();
  document.querySelector(".kt-yield-refusal-stack")?.remove();
  clearTimeout(tokyoRollResultPopupTimer);
  tokyoMovementDepartures = [];
  joinQrOpen = false;
  pendingRoom = "";
  displayMode = false;
  if (updateHistory) history.replaceState({}, "", "/");
  renderHome();
}

window.addEventListener("popstate", () => {
  const params = new URLSearchParams(location.search);
  if (params.get("view") === "library" || params.has("gallery")) { renderSavedLibrary(params.get("gallery") || undefined); return; }
  if (unmountLibrary) { renderHome(); return; }
  if (params.get("view") === "leaderboard") { renderLeaderboard(); return; }
  if (unmountLeaderboard) { renderHome(); return; }
  if (params.get("game") === "coral-chorus" && params.get("mode") === "offline") {
    launchCoralChorus(false);
    return;
  }
  if (unmountCoralChorus) {
    cleanupCoralChorus();
    if (!["stained-glass", "ghost-mansion"].includes(params.get("game") || "")) { renderHome(); return; }
  }
  if (params.get("game") === "stained-glass" && params.get("mode") === "offline") {
    launchStainedGlass(false);
    return;
  }
  if (unmountStainedGlass) {
    unmountStainedGlass();
    unmountStainedGlass = null;
    document.body.classList.remove("is-stained-glass");
    if (params.get("game") !== "ghost-mansion") { renderHome(); return; }
  }
  if (params.get("game") === "ghost-mansion") {
    launchGhostMansion(false);
    return;
  }
  if (unmountGhostMansion) {
    unmountGhostMansion();
    unmountGhostMansion = null;
    renderHome();
    return;
  }
  if (displayMode || params.get("view") === "display") {
    roomChat.setRoom(null);
    const currentSocket = socket;
    socket = null;
    currentSocket?.close();
    room = null;
    displayMode = params.get("view") === "display";
    pendingRoom = params.get("room")?.toUpperCase() || "";
    if (displayMode && isRoomCode(pendingRoom)) connect();
    else if (displayMode) renderDisplayJoin();
    else renderHome();
    return;
  }
  if (!room) return;
  if (room.isAdmin) {
    send({ type: "party_menu" });
    history.pushState({ partyRoom: room.roomCode }, "", `/?room=${room.roomCode}`);
  } else {
    leaveRoom();
  }
});

window.setInterval(() => void refreshStaleClientRelease(), CLIENT_RELEASE_CHECK_MS);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void refreshStaleClientRelease();
});
window.setTimeout(() => void refreshStaleClientRelease(), 5_000);
window.addEventListener("online", () => { void flushStandaloneResults(API_BASE); });
void flushStandaloneResults(API_BASE);

if (initialParams.get("view") === "library" || initialParams.has("gallery")) renderSavedLibrary(initialParams.get("gallery") || undefined);
else if (initialParams.get("view") === "leaderboard") renderLeaderboard();
else if (initialParams.get("game") === "coral-chorus" && initialParams.get("mode") === "offline") launchCoralChorus(false);
else if (initialParams.get("game") === "stained-glass" && initialParams.get("mode") === "offline") launchStainedGlass(false);
else if (initialParams.get("game") === "ghost-mansion") launchGhostMansion(false);
else if (displayMode && isRoomCode(pendingRoom)) connect();
else if (displayMode) renderDisplayJoin();
else if (isRoomCode(pendingRoom) && myName && localStorage.getItem("party-stack-market-room") === pendingRoom) connect();
else renderHome();
